import {
  ChangeEvent,
  FormEvent,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  MutableRefObject,
  PointerEvent as ReactPointerEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useVirtualizer } from "@tanstack/react-virtual";

import { GatewayAPIError } from "../../app/api/client";
import { SearchMessageHit } from "../search/searchApi";
import { SearchPanel } from "../search/SearchPanel";
import { authStore } from "../auth/authStore";
import { isNotificationNavigationSearch } from "../notifications/notificationLinks";
import {
  addChannelSubscriber,
  addGroupMember,
  ChatAttachment,
  ChatMessage,
  ConversationSummary,
  createVoiceFinalizeMetadata,
  createChatMessageInput,
  closeChannelConversation,
  closeGroupConversation,
  deleteDirectConversation,
  deleteChatMessage,
  editChatMessage,
  finalizeMediaUpload,
  getMessageAttachmentMetadata,
  getConversationReceiptSummary,
  getMediaAsset,
  InitiateMediaUploadResponse,
  initiateMediaUpload,
  listConversations,
  listMessageContext,
  listMessages,
  markConversationRead,
  removeChannelSubscriber,
  removeChannelConversation,
  removeGroupMember,
  removeGroupConversation,
  readPhotoFinalizeMetadata,
  normalizeFileUploadFilename,
  resolveUserIDByIdentifier,
  resolveMessageAttachmentDownloadURL,
  SAFE_FILE_UPLOAD_ACCEPT,
  updateChannelConversationMetadata,
  updateGroupConversationMetadata,
  readActorUserIDFromAccessToken,
  type MessageAttachmentMetadata,
  type MessageReferencePreview,
  type SendChatMessagePayload,
  SendSecretMessagePayload,
  sendChatMessage,
  sendSecretMessage,
  uploadAvatarMedia,
  uploadMediaBinary,
  type ReceiptParticipantSummary,
} from "./chatApi";
import { RealtimeEvent, connectRealtime } from "./realtimeClient";
import {
  resolveOwnMessageReceiptStatus,
  type MessageReceiptVisualStatus,
} from "./messageReceiptStatus";
import {
  collectDirectPeerUserIDs,
  collectMembershipUserIDs,
  collectMessageSenderUserIDs,
  describeDirectPeerAvatar,
  describeConversationWithDisplayName,
  formatSenderLabel,
  isAvatarReferenceDirectlyRenderable,
  resolveUserDisplayNames,
} from "./userDisplayNames";
import {
  IconBack,
  IconClose,
  IconDownload,
  IconEdit,
  IconFile,
  IconLock,
  IconMic,
  IconPhoto,
  IconPlus,
  IconRefresh,
  IconForward,
  IconReply,
  IconSearch,
  IconSend,
  IconTrash,
  IconUserPlus,
  IconVoiceWave,
} from "./chatIcons";
import { AudioPlayerAdapter } from "./AudioPlayerAdapter";
import {
  buildVirtualTimelineItems,
  estimateTimelineItemHeight,
  resolveTimelineMessageIndex,
  shouldFollowTimelineUpdateWhileComposing,
  type VirtualTimelineItem,
} from "./messageVirtualization";
import {
  deleteCachedMediaPreview,
  readCachedMediaPreview,
  writeCachedMediaPreview,
  type MediaPreviewCacheKeyInput,
  type MediaPreviewKind,
} from "./mediaPreviewCache";

type RealtimeState = "connecting" | "connected" | "closed" | "error";

const SEARCH_CONTEXT_BEFORE = 24;
const SEARCH_CONTEXT_AFTER = 24;
const SECRET_REALTIME_CONTEXT_BEFORE = 12;
const SECRET_REALTIME_CONTEXT_AFTER = 12;
const MESSAGE_ACTION_LONG_PRESS_MS = 520;

interface ContextNavigationTarget {
  aroundSequence: number;
  messageID?: string;
}

export type NotificationTargetFailureCode = "forbidden" | "missing" | "not_found" | "other";

export function shouldShowNotificationTargetModal(
  isNotificationNavigation: boolean,
  failureCode: NotificationTargetFailureCode,
): boolean {
  if (!isNotificationNavigation) {
    return false;
  }
  return failureCode === "missing" || failureCode === "forbidden" || failureCode === "not_found";
}

type PendingPhotoStatus = "failed" | "pending";
type PendingVoiceStatus = "failed" | "pending";
type ComposerFileAttachmentStatus = "selected" | "uploading" | "failed";

export interface PendingPhotoMessage {
  local_id: string;
  chat_id: string;
  file_name: string;
  preview_url: string;
  created_at: string;
  progress_percent: number | null;
  status: PendingPhotoStatus;
  error_message?: string;
}

export interface ComposerFileAttachmentState {
  local_id: string;
  file: File;
  filename: string;
  size_bytes: number;
  mime_type: string;
  status: ComposerFileAttachmentStatus;
  progress_percent: number | null;
  error_message?: string;
}

export interface PendingVoiceMessage {
  local_id: string;
  chat_id: string;
  created_at: string;
  preview_url: string;
  mime_type: string;
  duration_seconds: number;
  capped: boolean;
  progress_percent: number | null;
  status: PendingVoiceStatus;
  error_message?: string;
}

interface PendingVoiceUploadInput {
  blob: Blob;
  mimeType: string;
  durationSeconds: number;
  capped: boolean;
  chatID: string;
  previewURL: string;
}

interface VoicePreviewState {
  blob: Blob;
  previewURL: string;
  mimeType: string;
  durationSeconds: number;
  capped: boolean;
}

interface ActiveVoiceRecordingSession {
  mediaRecorder: MediaRecorder;
  stream: MediaStream;
  startedAt: number;
  stopPromise: Promise<Blob>;
}

export type ManageMembersConversationType = "group" | "channel";

export interface ManageMembersUIState {
  identifier: string;
  submitting: boolean;
  removingUserID: string | null;
  errorMessage: string | null;
  successMessage: string | null;
}

export interface ManageMembersEntry {
  userID: string;
  role: string;
  removable: boolean;
}

export interface MetadataEditorUIState {
  name: string;
  description: string;
  submitting: boolean;
  errorMessage: string | null;
  successMessage: string | null;
}

export type LifecycleConversationType = "group" | "channel";
export type LifecycleAction = "close" | "remove";

export interface DirectDeleteUIState {
  panelOpen: boolean;
  confirmationValue: string;
  loading: boolean;
  errorMessage: string | null;
}

export interface LifecycleActionUIState {
  pendingAction: LifecycleAction | null;
  confirmationValue: string;
  loading: boolean;
  statusMessage: string | null;
  errorMessage: string | null;
}

export type ManageMembersUIEvent =
  | { type: "identifier-changed"; value: string }
  | { type: "submit-requested" }
  | { type: "submit-succeeded"; conversationType: ManageMembersConversationType }
  | {
      type: "submit-failed";
      conversationType: ManageMembersConversationType;
      errorCode?: string;
      fallbackMessage?: string;
    }
  | { type: "remove-requested"; userID: string }
  | { type: "remove-succeeded"; conversationType: ManageMembersConversationType; userID: string }
  | {
      type: "remove-failed";
      conversationType: ManageMembersConversationType;
      userID: string;
      errorCode?: string;
      fallbackMessage?: string;
    }
  | { type: "clear-feedback" }
  | { type: "reset" };

export type MetadataEditorUIEvent =
  | { type: "seed"; name: string; description: string }
  | { type: "name-changed"; value: string }
  | { type: "description-changed"; value: string }
  | { type: "submit-requested" }
  | { type: "submit-succeeded"; conversationType: ManageMembersConversationType; name: string; description: string }
  | {
      type: "submit-failed";
      conversationType: ManageMembersConversationType;
      errorCode?: string;
      fallbackMessage?: string;
    }
  | { type: "clear-feedback" }
  | { type: "reset" };

export type DirectDeleteUIEvent =
  | { type: "open" }
  | { type: "confirmation-changed"; value: string }
  | { type: "submit-requested" }
  | { type: "submit-succeeded" }
  | { type: "submit-failed"; errorCode?: string; fallbackMessage?: string }
  | { type: "cancel" }
  | { type: "clear-feedback" }
  | { type: "reset" };

export type LifecycleActionUIEvent =
  | { type: "open"; action: LifecycleAction }
  | { type: "confirmation-changed"; value: string }
  | { type: "submit-requested" }
  | { type: "submit-succeeded"; action: LifecycleAction; conversationType: LifecycleConversationType }
  | {
      type: "submit-failed";
      action: LifecycleAction;
      conversationType: LifecycleConversationType;
      errorCode?: string;
      fallbackMessage?: string;
    }
  | { type: "cancel" }
  | { type: "clear-feedback" }
  | { type: "reset" };

export interface ExecuteConversationLifecycleActionInput {
  chatID: string;
  action: LifecycleAction;
  conversationType: LifecycleConversationType;
  closeGroupConversation: (groupID: string) => Promise<unknown>;
  closeChannelConversation: (channelID: string) => Promise<unknown>;
  removeGroupConversation: (groupID: string) => Promise<unknown>;
  removeChannelConversation: (channelID: string) => Promise<unknown>;
  refreshThreadAfterClose: () => Promise<unknown>;
  navigateToSafeChatShell: () => void;
}

export interface ExecuteDirectDeleteActionInput {
  chatID: string;
  deleteDirectConversation: (chatID: string) => Promise<unknown>;
  navigateToSafeChatShell: () => void;
}

export interface DirectDeleteConfirmationPanelProps {
  state: DirectDeleteUIState;
  confirmationPhrase: string;
  submitEnabled: boolean;
  errorMessage: string | null;
  translate: (key: string, options?: Record<string, string>) => string;
  onCancel: () => void;
  onConfirmationChange: (value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

export const CHAT_THREAD_SHELL_CLASSES = {
  root: "chat-shell chat-shell-thread",
  window: "messenger-window",
  searchPanel: "messenger-search-panel",
  timelinePane: "messenger-timeline",
  composerLane: "messenger-composer",
  messageList: "message-list",
} as const;

const ZERO_TIME_RFC3339 = "0001-01-01T00:00:00Z";
const MEDIA_ASSET_POLL_INTERVAL_MS = 700;
const MEDIA_ASSET_POLL_ATTEMPTS = 12;
const VOICE_RECORDING_TICK_MS = 1000;
const VOICE_MIME_TYPE_FALLBACK = "audio/webm";
const VOICE_MIME_TYPE_WAV = "audio/wav";
const VOICE_SECURE_CONTEXT_REQUIRED_MESSAGE = "Voice recording requires HTTPS (or localhost).";
const VOICE_MIN_CAPTURE_BYTES = 1024;
const FILE_UPLOAD_MIME_BY_EXTENSION = new Map<string, string>([
  ["pdf", "application/pdf"],
  ["txt", "text/plain"],
  ["md", "text/markdown"],
  ["csv", "text/csv"],
  ["json", "application/json"],
  ["rtf", "application/rtf"],
  ["doc", "application/msword"],
  ["xls", "application/vnd.ms-excel"],
  ["ppt", "application/vnd.ms-powerpoint"],
]);
const MESSAGE_ATTACHMENT_CACHE_KEY = "msg.web.message_attachment_cache.v1";
const METADATA_EDITOR_NAME_MAX_LENGTH = 64;
const METADATA_EDITOR_DESCRIPTION_MAX_LENGTH = 280;
const LIFECYCLE_CONFIRMATION_FALLBACK_PHRASE = "CONFIRM";
const DIRECT_DELETE_CONFIRMATION_FALLBACK_PHRASE = "CONFIRM";

export const VOICE_MAX_DURATION_SECONDS = 120;
const VOICE_MAX_DURATION_MILLIS = VOICE_MAX_DURATION_SECONDS * 1000;

function normalizeAttachmentCacheItem(value: unknown): ChatAttachment | null {
  if (value === null || typeof value !== "object") {
    return null;
  }

  const mediaIDValue = (value as { media_id?: unknown }).media_id;
  const kindValue = (value as { kind?: unknown }).kind;
  if (typeof mediaIDValue !== "string" || typeof kindValue !== "string") {
    return null;
  }

  const mediaID = mediaIDValue.trim();
  const kind = kindValue.trim().toLowerCase();
  if (mediaID === "" || kind === "") {
    return null;
  }

  return {
    media_id: mediaID,
    kind,
  };
}

function normalizeAttachmentCacheList(value: unknown): ChatAttachment[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const normalized: ChatAttachment[] = [];
  for (const item of value) {
    const attachment = normalizeAttachmentCacheItem(item);
    if (attachment !== null) {
      normalized.push(attachment);
    }
  }
  return normalized;
}

function readAttachmentCacheFromStorage(): Record<string, ChatAttachment[]> {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(MESSAGE_ATTACHMENT_CACHE_KEY);
    if (raw === null || raw.trim() === "") {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== "object") {
      return {};
    }

    const normalized: Record<string, ChatAttachment[]> = {};
    for (const [messageID, attachmentsValue] of Object.entries(parsed as Record<string, unknown>)) {
      const normalizedMessageID = messageID.trim();
      if (normalizedMessageID === "") {
        continue;
      }
      const attachments = normalizeAttachmentCacheList(attachmentsValue);
      if (attachments.length > 0) {
        normalized[normalizedMessageID] = attachments;
      }
    }
    return normalized;
  } catch {
    return {};
  }
}

function writeAttachmentCacheToStorage(cache: Record<string, ChatAttachment[]>): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(MESSAGE_ATTACHMENT_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Ignore quota/serialization failures; cache is best-effort only.
  }
}

type MediaBinaryReadPurpose = "message_attachment" | "conversation_avatar";

interface MediaBinaryReadContext {
  conversationID: string;
  purpose: MediaBinaryReadPurpose;
}

function mediaBinaryPath(mediaID: string, context?: MediaBinaryReadContext): string {
  const path = `/v1/media/assets/${encodeURIComponent(mediaID.trim())}/binary`;
  const conversationID = context?.conversationID.trim() ?? "";
  if (conversationID === "" || context === undefined) {
    return path;
  }
  const query = new URLSearchParams();
  query.set("conversation_id", conversationID);
  query.set("purpose", context.purpose);
  return `${path}?${query.toString()}`;
}

function mediaPreviewPath(mediaID: string, context?: MediaBinaryReadContext): string {
  const path = `/v1/media/assets/${encodeURIComponent(mediaID.trim())}/preview`;
  const conversationID = context?.conversationID.trim() ?? "";
  if (conversationID === "" || context === undefined) {
    return path;
  }
  const query = new URLSearchParams();
  query.set("conversation_id", conversationID);
  query.set("purpose", context.purpose);
  return `${path}?${query.toString()}`;
}

function normalizeGatewayOrigin(origin: string): string {
  const trimmed = origin.trim();
  if (trimmed.endsWith("/")) {
    return trimmed.slice(0, -1);
  }
  return trimmed;
}

function resolveGatewayOrigin(override?: string): string {
  if (typeof override === "string" && override.trim() !== "") {
    return normalizeGatewayOrigin(override);
  }
  if (typeof import.meta.env.VITE_GATEWAY_ORIGIN === "string" && import.meta.env.VITE_GATEWAY_ORIGIN !== "") {
    return normalizeGatewayOrigin(import.meta.env.VITE_GATEWAY_ORIGIN);
  }
  return "";
}

export function resolveConversationMediaStateKey(conversationID: string, mediaID: string): string {
  const normalizedConversationID = conversationID.trim();
  const normalizedMediaID = mediaID.trim();
  if (normalizedConversationID === "") {
    return normalizedMediaID;
  }
  return `${normalizedConversationID}::${normalizedMediaID}`;
}

export function resolveMediaBinaryURL(
  mediaID: string,
  gatewayOrigin?: string,
  context?: MediaBinaryReadContext,
): string {
  const path = mediaBinaryPath(mediaID, context);
  const origin = resolveGatewayOrigin(gatewayOrigin);
  if (origin === "") {
    return path;
  }
  return `${origin}${path}`;
}

export function resolveMediaPreviewURL(
  mediaID: string,
  gatewayOrigin?: string,
  context?: MediaBinaryReadContext,
): string {
  const path = mediaPreviewPath(mediaID, context);
  const origin = resolveGatewayOrigin(gatewayOrigin);
  if (origin === "") {
    return path;
  }
  return `${origin}${path}`;
}

export function resolvePhotoAttachmentPreviewReadURL(
  mediaID: string,
  conversationID: string,
  gatewayOrigin?: string,
): string {
  return resolveMediaPreviewURL(mediaID, gatewayOrigin, {
    conversationID,
    purpose: "message_attachment",
  });
}

export function resolvePhotoAttachmentBinaryReadURL(
  mediaID: string,
  conversationID: string,
  gatewayOrigin?: string,
): string {
  return resolveMediaBinaryURL(mediaID, gatewayOrigin, {
    conversationID,
    purpose: "message_attachment",
  });
}

export type PhotoAttachmentFullSizeReadResult =
  | { status: "loaded"; sourceURL: string }
  | { status: "unavailable"; sourceURL: null };

export interface PhotoAttachmentFullSizeReadInput {
  mediaID: string;
  conversationID: string;
  accessToken: string | null;
  gatewayOrigin?: string;
  fetchImpl?: typeof fetch;
  createObjectURL?: (blob: Blob) => string;
}

export async function readPhotoAttachmentFullSizeSource(
  input: PhotoAttachmentFullSizeReadInput,
): Promise<PhotoAttachmentFullSizeReadResult> {
  const normalizedMediaID = input.mediaID.trim();
  const normalizedConversationID = input.conversationID.trim();
  if (normalizedMediaID === "" || normalizedConversationID === "" || input.accessToken === null) {
    return { status: "unavailable", sourceURL: null };
  }

  try {
    const fetchPhoto = input.fetchImpl ?? fetch;
    const createObjectURL = input.createObjectURL ?? URL.createObjectURL;
    const response = await fetchPhoto(
      resolvePhotoAttachmentBinaryReadURL(normalizedMediaID, normalizedConversationID, input.gatewayOrigin),
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${input.accessToken}`,
        },
      },
    );
    if (!response.ok) {
      return { status: "unavailable", sourceURL: null };
    }
    const binary = await response.blob();
    if (binary.size <= 0) {
      return { status: "unavailable", sourceURL: null };
    }
    return {
      status: "loaded",
      sourceURL: createObjectURL(binary),
    };
  } catch {
    return { status: "unavailable", sourceURL: null };
  }
}

export type ExpandedPhotoState =
  | { status: "loading"; sourceURL: null }
  | { status: "loaded"; sourceURL: string }
  | { status: "unavailable"; sourceURL: null };

interface PhotoAttachmentLightboxProps {
  expandedPhoto: ExpandedPhotoState;
  dialogLabel: string;
  closeLabel: string;
  fullAlt: string;
  loadingLabel: string;
  failedLabel: string;
  onClose: () => void;
}

export function PhotoAttachmentLightbox({
  expandedPhoto,
  dialogLabel,
  closeLabel,
  fullAlt,
  loadingLabel,
  failedLabel,
  onClose,
}: PhotoAttachmentLightboxProps) {
  const statusLabel = expandedPhoto.status === "loading" ? loadingLabel : failedLabel;

  return (
    <div className="media-lightbox" role="dialog" aria-modal="true" aria-label={dialogLabel} onClick={onClose}>
      <div className="media-lightbox-content" onClick={(event) => event.stopPropagation()}>
        <button
          className="icon-button icon-button-ghost media-lightbox-close"
          type="button"
          aria-label={closeLabel}
          onClick={onClose}
        >
          <IconClose />
        </button>
        {expandedPhoto.status === "loaded" ? (
          <img className="media-lightbox-image" src={expandedPhoto.sourceURL} alt={fullAlt} data-testid="expanded-photo-loaded" />
        ) : (
          <div
            className={`media-lightbox-status media-lightbox-status-${expandedPhoto.status}`}
            role="status"
            aria-live="polite"
            data-testid={`expanded-photo-${expandedPhoto.status}`}
          >
            {expandedPhoto.status === "loading" ? (
              <span className="media-lightbox-spinner" aria-hidden="true" />
            ) : (
              <IconPhoto />
            )}
            <span>{statusLabel}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export function shouldInvalidateMediaPreviewCacheForStatus(status: number): boolean {
  return status === 401 || status === 403 || status === 404;
}

export function isVoiceAttachmentKind(kind: string): boolean {
  const normalizedKind = kind.trim().toLowerCase();
  return normalizedKind === "voice" || normalizedKind === "audio";
}

export function isPhotoAttachmentKind(kind: string): boolean {
  const normalizedKind = kind.trim().toLowerCase();
  return normalizedKind === "photo" || normalizedKind === "image" || normalizedKind === "picture";
}

export function isFileAttachmentKind(kind: string): boolean {
  return kind.trim().toLowerCase() === "file";
}

function fileExtension(filename: string): string {
  const normalized = filename.trim().toLowerCase();
  const dotIndex = normalized.lastIndexOf(".");
  if (dotIndex < 0 || dotIndex === normalized.length - 1) {
    return "";
  }
  return normalized.slice(dotIndex + 1);
}

function resolveFileAttachmentMimeType(file: File, filename: string): string {
  const explicit = file.type.trim().toLowerCase().split(";")[0]?.trim() ?? "";
  if (explicit !== "" && explicit !== "application/octet-stream") {
    return explicit;
  }
  return FILE_UPLOAD_MIME_BY_EXTENSION.get(fileExtension(filename)) ?? "application/octet-stream";
}

function isSafeFileUploadExtension(filename: string): boolean {
  return FILE_UPLOAD_MIME_BY_EXTENSION.has(fileExtension(filename));
}

export function formatFileAttachmentSize(sizeBytes: number): string {
  const normalized = Number.isFinite(sizeBytes) ? Math.max(0, Math.trunc(sizeBytes)) : 0;
  if (normalized < 1024) {
    return `${normalized} B`;
  }
  if (normalized < 1024 * 1024) {
    const kilobytes = normalized / 1024;
    return `${Number.isInteger(kilobytes) ? kilobytes.toFixed(0) : kilobytes.toFixed(1)} KB`;
  }
  const megabytes = normalized / (1024 * 1024);
  return `${Number.isInteger(megabytes) ? megabytes.toFixed(0) : megabytes.toFixed(1)} MB`;
}

export function mapFileAttachmentGatewayErrorCode(code: string): string {
  switch (code.trim().toLowerCase()) {
    case "invalid_request":
      return "chatThread.files.errors.invalid";
    case "conflict":
      return "chatThread.files.errors.rejected";
    case "unauthorized":
      return "chatThread.files.errors.unauthorized";
    case "forbidden":
      return "chatThread.errors.forbidden";
    default:
      return "chatThread.files.errors.failed";
  }
}

function randomComposerFileAttachmentID(): string {
  return `composer_file_${Date.now()}_${Math.random().toString(16).slice(2, 10)}`;
}

export function createComposerFileAttachmentDraft(file: File): ComposerFileAttachmentState {
  const filename = normalizeFileUploadFilename(file.name);
  return {
    local_id: randomComposerFileAttachmentID(),
    file,
    filename,
    size_bytes: Math.max(1, Math.trunc(file.size)),
    mime_type: resolveFileAttachmentMimeType(file, filename),
    status: "selected",
    progress_percent: null,
  };
}

export interface SecretComposerEnvelopeDraft {
  ciphertext: string;
  ratchetSessionID: string;
  ratchetCounter: string;
  previousChainLength: string;
}

export function buildSecretMessageEnvelope(draft: SecretComposerEnvelopeDraft): SendSecretMessagePayload {
  const ciphertext = draft.ciphertext.trim();
  if (ciphertext === "") {
    throw new Error("chatThread.secretEnvelope.validation.ciphertextRequired");
  }

  const ratchetSessionID = draft.ratchetSessionID.trim();
  if (ratchetSessionID === "") {
    throw new Error("chatThread.secretEnvelope.validation.ratchetSessionIdRequired");
  }
  if (ratchetSessionID.includes("/")) {
    throw new Error("chatThread.secretEnvelope.validation.ratchetSessionIdInvalidPathSeparator");
  }

  const parsedRatchetCounter = Number.parseInt(draft.ratchetCounter.trim(), 10);
  if (!Number.isFinite(parsedRatchetCounter) || parsedRatchetCounter <= 0) {
    throw new Error("chatThread.secretEnvelope.validation.ratchetCounterPositive");
  }

  const parsedPreviousChainLength = Number.parseInt(draft.previousChainLength.trim(), 10);
  if (!Number.isFinite(parsedPreviousChainLength) || parsedPreviousChainLength < 0) {
    throw new Error("chatThread.secretEnvelope.validation.previousChainLengthNonNegative");
  }

  return {
    ciphertext,
    ratchet_session_id: ratchetSessionID,
    ratchet_counter: Math.trunc(parsedRatchetCounter),
    previous_chain_length: Math.trunc(parsedPreviousChainLength),
  };
}

export function resolveMessageBodyForDisplay(message: ChatMessage): string {
  const body = message.body.trim();
  if (body !== "") {
    return body;
  }

  const ciphertext = message.ciphertext?.trim() ?? "";
  if (ciphertext !== "") {
    return ciphertext;
  }

  return "";
}

export type VoiceRecordingMode = "idle" | "arming" | "recording" | "preview";

export interface VoiceRecordingUIState {
  mode: VoiceRecordingMode;
  elapsedSeconds: number;
  previewDurationSeconds: number;
  capped: boolean;
  errorMessage: string | null;
}

export type VoiceRecordingUIEvent =
  | { type: "start-requested" }
  | { type: "start-succeeded" }
  | { type: "tick"; elapsedSeconds: number }
  | { type: "stop-succeeded"; durationSeconds: number; capped: boolean }
  | { type: "cancelled" }
  | { type: "preview-discarded" }
  | { type: "error"; message: string };

function normalizeVoiceDurationSeconds(value: number): number {
  if (!Number.isFinite(value) || value <= 0) {
    return 0;
  }
  return Math.min(VOICE_MAX_DURATION_SECONDS, Math.trunc(value));
}

export function createInitialVoiceRecordingUIState(): VoiceRecordingUIState {
  return {
    mode: "idle",
    elapsedSeconds: 0,
    previewDurationSeconds: 0,
    capped: false,
    errorMessage: null,
  };
}

function readErrorName(error: unknown): string {
  if (error !== null && typeof error === "object" && "name" in error) {
    const name = (error as { name?: unknown }).name;
    if (typeof name === "string") {
      return name.trim();
    }
  }
  return "";
}

export function resolveVoiceRecorderErrorMessage(error: unknown): string {
  const name = readErrorName(error);
  if (name === "SecurityError") {
    return VOICE_SECURE_CONTEXT_REQUIRED_MESSAGE;
  }
  if (name === "NotAllowedError") {
    return "Microphone access was denied. Allow microphone access and try again.";
  }
  if (name === "NotFoundError" || name === "DevicesNotFoundError") {
    return "No microphone is available on this device.";
  }
  if (name === "NotReadableError" || name === "TrackStartError") {
    return "Microphone is currently busy in another app.";
  }
  if (error instanceof Error && error.message.trim() !== "") {
    return error.message.trim();
  }
  return "Voice recording is unavailable in this browser.";
}

function readFirstBlobMimeType(parts: BlobPart[]): string {
  for (const part of parts) {
    if (part instanceof Blob) {
      const mimeType = part.type.trim();
      if (mimeType !== "") {
        return mimeType;
      }
    }
  }
  return "";
}

export function resolveRecordedVoiceMimeType({
  recorderMimeType,
  chunkMimeType,
  requestedMimeType,
  fallbackMimeType = VOICE_MIME_TYPE_FALLBACK,
}: {
  recorderMimeType?: string;
  chunkMimeType?: string;
  requestedMimeType?: string;
  fallbackMimeType?: string;
}): string {
  const candidates = [recorderMimeType, chunkMimeType, requestedMimeType, fallbackMimeType];
  for (const candidate of candidates) {
    if (typeof candidate !== "string") {
      continue;
    }
    const trimmed = candidate.trim();
    if (trimmed !== "") {
      return trimmed;
    }
  }
  return VOICE_MIME_TYPE_FALLBACK;
}

function normalizeVoiceMimeType(value: string): string {
  const trimmed = value.trim().toLowerCase();
  if (trimmed === "") {
    return "";
  }
  const separatorIndex = trimmed.indexOf(";");
  if (separatorIndex === -1) {
    return trimmed;
  }
  return trimmed.slice(0, separatorIndex).trim();
}

export function shouldNormalizeRecordedVoiceToWav(mimeType: string): boolean {
  const normalized = normalizeVoiceMimeType(mimeType);
  switch (normalized) {
    case "audio/webm":
    case "audio/ogg":
    case "audio/opus":
    case "application/ogg":
      return true;
    default:
      return false;
  }
}

function resolveAudioContextConstructor(): (new () => AudioContext) | null {
  if (typeof window === "undefined") {
    return null;
  }
  if (typeof window.AudioContext === "function") {
    return window.AudioContext;
  }
  const legacyAudioContext = (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (typeof legacyAudioContext === "function") {
    return legacyAudioContext;
  }
  return null;
}

function writeWavASCII(view: DataView, offset: number, value: string): void {
  for (let index = 0; index < value.length; index += 1) {
    view.setUint8(offset + index, value.charCodeAt(index));
  }
}

function encodeAudioBufferAsWav(buffer: AudioBuffer): ArrayBuffer {
  const channelCount = Math.max(1, buffer.numberOfChannels);
  const frameCount = Math.max(0, buffer.length);
  const sampleRate = Math.max(1, Math.trunc(buffer.sampleRate));
  const bitsPerSample = 16;
  const bytesPerSample = bitsPerSample / 8;
  const blockAlign = channelCount * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = frameCount * blockAlign;
  const wavBuffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(wavBuffer);

  writeWavASCII(view, 0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeWavASCII(view, 8, "WAVE");
  writeWavASCII(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, channelCount, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitsPerSample, true);
  writeWavASCII(view, 36, "data");
  view.setUint32(40, dataSize, true);

  const channels: Float32Array[] = [];
  for (let channel = 0; channel < channelCount; channel += 1) {
    channels.push(buffer.getChannelData(channel));
  }

  let sampleOffset = 44;
  for (let frame = 0; frame < frameCount; frame += 1) {
    for (let channel = 0; channel < channelCount; channel += 1) {
      const channelData = channels[channel];
      const sample = channelData === undefined ? 0 : (channelData[frame] ?? 0);
      const clamped = Math.max(-1, Math.min(1, sample));
      const int16 = clamped < 0 ? Math.round(clamped * 0x8000) : Math.round(clamped * 0x7fff);
      view.setInt16(sampleOffset, int16, true);
      sampleOffset += bytesPerSample;
    }
  }

  return wavBuffer;
}

async function transcodeRecordedVoiceBlobToWav(source: Blob): Promise<Blob> {
  const AudioContextConstructor = resolveAudioContextConstructor();
  if (AudioContextConstructor === null) {
    throw new Error("AudioContext unavailable.");
  }

  const context = new AudioContextConstructor();
  try {
    const encoded = await source.arrayBuffer();
    const decoded = await context.decodeAudioData(encoded.slice(0));
    const wavPayload = encodeAudioBufferAsWav(decoded);
    return new Blob([wavPayload], {
      type: VOICE_MIME_TYPE_WAV,
    });
  } finally {
    await context.close();
  }
}

async function normalizeRecordedVoiceBlobForPlayback(source: Blob): Promise<Blob> {
  if (source.size <= 0 || !shouldNormalizeRecordedVoiceToWav(source.type)) {
    return source;
  }
  try {
    return await transcodeRecordedVoiceBlobToWav(source);
  } catch {
    return source;
  }
}

async function normalizeRecordedVoiceBlobForUpload(source: Blob): Promise<Blob> {
  if (source.size <= 0) {
    return source;
  }
  if (normalizeVoiceMimeType(source.type) === VOICE_MIME_TYPE_WAV) {
    return source;
  }
  try {
    return await transcodeRecordedVoiceBlobToWav(source);
  } catch {
    throw new Error("This browser could not convert voice format for iPhone playback. Reload and record again.");
  }
}

export function reduceVoiceRecordingUIState(
  current: VoiceRecordingUIState,
  event: VoiceRecordingUIEvent,
): VoiceRecordingUIState {
  switch (event.type) {
    case "start-requested":
      return {
        mode: "arming",
        elapsedSeconds: 0,
        previewDurationSeconds: 0,
        capped: false,
        errorMessage: null,
      };
    case "start-succeeded":
      return {
        mode: "recording",
        elapsedSeconds: 0,
        previewDurationSeconds: 0,
        capped: false,
        errorMessage: null,
      };
    case "tick":
      if (current.mode !== "recording") {
        return current;
      }
      {
        const normalizedElapsedSeconds = normalizeVoiceDurationSeconds(event.elapsedSeconds);
        if (normalizedElapsedSeconds === current.elapsedSeconds) {
          return current;
        }
        return {
          ...current,
          elapsedSeconds: normalizedElapsedSeconds,
        };
      }
    case "stop-succeeded":
      return {
        mode: "preview",
        elapsedSeconds: normalizeVoiceDurationSeconds(current.elapsedSeconds),
        previewDurationSeconds: normalizeVoiceDurationSeconds(event.durationSeconds),
        capped: event.capped,
        errorMessage: null,
      };
    case "cancelled":
    case "preview-discarded":
      return createInitialVoiceRecordingUIState();
    case "error":
      return {
        ...createInitialVoiceRecordingUIState(),
        errorMessage: event.message.trim() === "" ? "Voice recording is unavailable in this browser." : event.message.trim(),
      };
    default:
      return current;
  }
}

function normalizePendingProgress(value: number | null | undefined): number | null {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return null;
  }

  return Math.max(1, Math.min(99, Math.trunc(value)));
}

function comparePendingPhotoOrder(left: PendingPhotoMessage, right: PendingPhotoMessage): number {
  if (left.created_at !== right.created_at) {
    return left.created_at.localeCompare(right.created_at);
  }
  return left.local_id.localeCompare(right.local_id);
}

function comparePendingVoiceOrder(left: PendingVoiceMessage, right: PendingVoiceMessage): number {
  if (left.created_at !== right.created_at) {
    return left.created_at.localeCompare(right.created_at);
  }
  return left.local_id.localeCompare(right.local_id);
}

export function upsertPendingPhoto(messages: PendingPhotoMessage[], incoming: PendingPhotoMessage): PendingPhotoMessage[] {
  const next = [...messages];
  const index = next.findIndex((item) => item.local_id === incoming.local_id);
  if (index === -1) {
    next.push(incoming);
  } else {
    next[index] = incoming;
  }
  return next.sort(comparePendingPhotoOrder);
}

export function patchPendingPhoto(
  messages: PendingPhotoMessage[],
  localID: string,
  patch: Partial<PendingPhotoMessage>,
): PendingPhotoMessage[] {
  const hasProgressPatch = Object.prototype.hasOwnProperty.call(patch, "progress_percent");
  return messages.map((item) => {
    if (item.local_id !== localID) {
      return item;
    }
    return {
      ...item,
      ...patch,
      progress_percent: normalizePendingProgress(hasProgressPatch ? patch.progress_percent : item.progress_percent),
    };
  });
}

export function removePendingPhoto(messages: PendingPhotoMessage[], localID: string): PendingPhotoMessage[] {
  return messages.filter((item) => item.local_id !== localID);
}

export function upsertPendingVoice(messages: PendingVoiceMessage[], incoming: PendingVoiceMessage): PendingVoiceMessage[] {
  const next = [...messages];
  const index = next.findIndex((item) => item.local_id === incoming.local_id);
  if (index === -1) {
    next.push(incoming);
  } else {
    next[index] = incoming;
  }
  return next.sort(comparePendingVoiceOrder);
}

export function patchPendingVoice(
  messages: PendingVoiceMessage[],
  localID: string,
  patch: Partial<PendingVoiceMessage>,
): PendingVoiceMessage[] {
  const hasProgressPatch = Object.prototype.hasOwnProperty.call(patch, "progress_percent");
  return messages.map((item) => {
    if (item.local_id !== localID) {
      return item;
    }
    return {
      ...item,
      ...patch,
      progress_percent: normalizePendingProgress(hasProgressPatch ? patch.progress_percent : item.progress_percent),
    };
  });
}

export function removePendingVoice(messages: PendingVoiceMessage[], localID: string): PendingVoiceMessage[] {
  return messages.filter((item) => item.local_id !== localID);
}

function randomPendingPhotoID(): string {
  return `pending_photo_${Date.now()}_${Math.random().toString(16).slice(2, 10)}`;
}

function randomPendingVoiceID(): string {
  return `pending_voice_${Date.now()}_${Math.random().toString(16).slice(2, 10)}`;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function compareMessageOrder(left: ChatMessage, right: ChatMessage): number {
  if (left.sequence !== right.sequence) {
    return left.sequence - right.sequence;
  }
  if (left.created_at !== right.created_at) {
    return left.created_at.localeCompare(right.created_at);
  }
  return left.message_id.localeCompare(right.message_id);
}

function mergeMessage(current: ChatMessage | null, incoming: ChatMessage): ChatMessage {
  if (current === null) {
    return incoming;
  }

  return {
    ...current,
    ...incoming,
    attachments: incoming.attachments ?? current.attachments,
    body: incoming.body === "" ? current.body : incoming.body,
  };
}

function normalizeOptionalTimestamp(value: string | undefined): string | undefined {
  if (value === undefined) {
    return undefined;
  }

  const trimmed = value.trim();
  if (trimmed === "" || trimmed === ZERO_TIME_RFC3339) {
    return undefined;
  }
  return trimmed;
}

function upsertMessage(messages: ChatMessage[], incoming: ChatMessage): ChatMessage[] {
  const next = [...messages];
  const index = next.findIndex((message) => message.message_id === incoming.message_id);
  if (index === -1) {
    next.push(incoming);
    return sortMessagesAscending(next);
  }

  next[index] = mergeMessage(next[index] ?? null, incoming);
  return sortMessagesAscending(next);
}

function toRealtimeChatMessage(event: RealtimeEvent): ChatMessage | null {
  if (typeof event.sequence !== "number" || !Number.isFinite(event.sequence)) {
    return null;
  }
  const messageID = event.message_id?.trim() ?? "";
  if (event.chat_id.trim() === "" || messageID === "") {
    return null;
  }

  const createdAt = normalizeOptionalTimestamp(event.created_at) ?? new Date().toISOString();
  const updatedAt = normalizeOptionalTimestamp(event.updated_at) ?? createdAt;
  const secretEnvelope = readSecretRealtimeEnvelope(event);

  return {
    message_id: messageID,
    chat_id: event.chat_id,
    sequence: event.sequence,
    sender_user_id: event.sender_user_id ?? "",
    body: event.body ?? "",
    ciphertext: secretEnvelope.ciphertext,
    idempotency_key: event.idempotency_key ?? "",
    ratchet_session_id: secretEnvelope.ratchet_session_id,
    ratchet_counter: secretEnvelope.ratchet_counter,
    previous_chain_length: secretEnvelope.previous_chain_length,
    edit_version: event.edit_version ?? 0,
    root_post_id: event.root_post_id,
    attachments: event.attachments,
    created_at: createdAt,
    updated_at: updatedAt,
    edited_at: normalizeOptionalTimestamp(event.edited_at),
    deleted_at: normalizeOptionalTimestamp(event.deleted_at),
    deleted_by_user_id: event.deleted_by_user_id,
  };
}

function normalizeChatID(raw: string | undefined): string {
  if (raw === undefined) {
    return "";
  }
  try {
    return decodeURIComponent(raw).trim();
  } catch {
    return raw.trim();
  }
}

function formatMessageTimestamp(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleTimeString();
}

function formatVoiceDuration(totalSeconds: number): string {
  const normalized = normalizeVoiceDurationSeconds(totalSeconds);
  const minutes = Math.trunc(normalized / 60);
  const seconds = normalized % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function stopMediaStreamTracks(stream: MediaStream): void {
  for (const track of stream.getTracks()) {
    track.stop();
  }
}

function normalizeAttachmentForMessage(attachment: ChatAttachment): ChatAttachment | null {
  const mediaID = attachment.media_id.trim();
  const kind = attachment.kind.trim().toLowerCase();
  if (mediaID === "" || kind === "") {
    return null;
  }
  return {
    media_id: mediaID,
    kind,
  };
}

function ensureMessageAttachment(message: ChatMessage, attachment: ChatAttachment): ChatMessage {
  const normalizedAttachment = normalizeAttachmentForMessage(attachment);
  if (normalizedAttachment === null) {
    return message;
  }

  const current = normalizeAttachmentCacheList(message.attachments);
  const alreadyPresent = current.some(
    (item) =>
      item.media_id === normalizedAttachment.media_id &&
      item.kind === normalizedAttachment.kind,
  );
  if (alreadyPresent) {
    if (message.attachments === current) {
      return message;
    }
    return {
      ...message,
      attachments: current,
    };
  }

  return {
    ...message,
    attachments: [...current, normalizedAttachment],
  };
}

function isOwnMessage(message: ChatMessage, actorUserID: string | null): boolean {
  if (actorUserID === null) {
    return false;
  }
  return message.sender_user_id === actorUserID;
}

export function buildMessageBubbleClassName({
  own,
  deleted,
  highlighted,
}: {
  own: boolean;
  deleted: boolean;
  highlighted: boolean;
}): string {
  const classNames = ["message-bubble", own ? "message-bubble-own" : "message-bubble-incoming"];
  if (deleted) {
    classNames.push("message-bubble-deleted");
  }
  if (highlighted) {
    classNames.push("message-bubble-highlighted");
  }
  return classNames.join(" ");
}

export function canRevealMessageActions({
  deleted,
  editing,
}: {
  deleted: boolean;
  editing: boolean;
}): boolean {
  return !deleted && !editing;
}

export function canReplyToMessage({
  deleted,
  secretChat,
}: {
  deleted: boolean;
  secretChat: boolean;
}): boolean {
  return !deleted && !secretChat;
}

export function canForwardMessage({
  deleted,
  sourceSecretChat,
}: {
  deleted: boolean;
  sourceSecretChat: boolean;
}): boolean {
  return !deleted && !sourceSecretChat;
}

export function canEditMessage({
  own,
  deleted,
  editing,
}: {
  own: boolean;
  deleted: boolean;
  editing: boolean;
}): boolean {
  return own && !deleted && !editing;
}

export function canDeleteMessage({
  own,
  deleted,
  editing,
}: {
  own: boolean;
  deleted: boolean;
  editing: boolean;
}): boolean {
  return own && !deleted && !editing;
}

export type SelectedMessageActionID = "reply" | "forward" | "edit" | "delete";

export interface SelectedMessageActionDescriptor {
  id: SelectedMessageActionID;
  labelKey: string;
  buttonClassName: string;
}

const SELECTED_MESSAGE_ACTION_DESCRIPTORS: Record<SelectedMessageActionID, SelectedMessageActionDescriptor> = {
  reply: {
    id: "reply",
    labelKey: "chatThread.actions.replyMessage",
    buttonClassName: "icon-button icon-button-ghost",
  },
  forward: {
    id: "forward",
    labelKey: "chatThread.actions.forwardMessage",
    buttonClassName: "icon-button icon-button-ghost",
  },
  edit: {
    id: "edit",
    labelKey: "chatThread.actions.editMessage",
    buttonClassName: "icon-button icon-button-ghost",
  },
  delete: {
    id: "delete",
    labelKey: "chatThread.actions.deleteMessage",
    buttonClassName: "icon-button icon-button-danger",
  },
};

export function buildSelectedMessageActionDescriptors({
  own,
  deleted,
  editing,
  sourceSecretChat,
}: {
  own: boolean;
  deleted: boolean;
  editing: boolean;
  sourceSecretChat: boolean;
}): SelectedMessageActionDescriptor[] {
  if (!canRevealMessageActions({ deleted, editing })) {
    return [];
  }

  const actions: SelectedMessageActionDescriptor[] = [];
  if (canReplyToMessage({ deleted, secretChat: sourceSecretChat })) {
    actions.push(SELECTED_MESSAGE_ACTION_DESCRIPTORS.reply);
  }
  if (canForwardMessage({ deleted, sourceSecretChat })) {
    actions.push(SELECTED_MESSAGE_ACTION_DESCRIPTORS.forward);
  }
  if (canEditMessage({ own, deleted, editing })) {
    actions.push(SELECTED_MESSAGE_ACTION_DESCRIPTORS.edit);
  }
  if (canDeleteMessage({ own, deleted, editing })) {
    actions.push(SELECTED_MESSAGE_ACTION_DESCRIPTORS.delete);
  }
  return actions;
}

type MessageActionAvailabilityInput = {
  activeMessageActionsID: string | null;
  messageID: string;
  canReply: boolean;
  canForward: boolean;
  canEdit: boolean;
  canDelete: boolean;
};

type MessageActionDescriptorInput = {
  activeMessageActionsID: string | null;
  messageID: string;
  actions: readonly SelectedMessageActionDescriptor[];
};

export function shouldRenderMessageActions(input: MessageActionAvailabilityInput | MessageActionDescriptorInput): boolean {
  const hasAvailableActions =
    "actions" in input
      ? input.actions.length > 0
      : input.canReply || input.canForward || input.canEdit || input.canDelete;
  return input.activeMessageActionsID === input.messageID && hasAvailableActions;
}

export interface MessageSelectionState {
  selectionActive: boolean;
  selected: boolean;
  dimmed: boolean;
  menuVisible: boolean;
}

export function resolveMessageSelectionState({
  activeMessageActionsID,
  messageID,
  actions,
}: {
  activeMessageActionsID: string | null;
  messageID: string;
  actions: readonly SelectedMessageActionDescriptor[];
}): MessageSelectionState {
  const selectionActive = activeMessageActionsID !== null;
  const selected = activeMessageActionsID === messageID;
  return {
    selectionActive,
    selected,
    dimmed: selectionActive && !selected,
    menuVisible: selected && actions.length > 0,
  };
}

export interface MessageSelectionPointerTarget {
  closest: (selector: string) => unknown;
}

export function shouldDismissMessageSelectionFromPointerTarget(
  target: MessageSelectionPointerTarget | null,
): boolean {
  if (target === null) {
    return false;
  }
  return (
    target.closest(".message-selection-menu") === null &&
    target.closest('[data-message-selection-row="selected"]') === null
  );
}

export function isMessageSelectionDismissKey(key: string): boolean {
  return key === "Escape" || key === "Esc";
}

function renderSelectedMessageActionIcon(actionID: SelectedMessageActionID) {
  switch (actionID) {
    case "reply":
      return <IconReply />;
    case "forward":
      return <IconForward />;
    case "edit":
      return <IconEdit />;
    case "delete":
      return <IconTrash />;
    default:
      return null;
  }
}

export type ComposerReferenceSendResult = "success" | "failure" | "cancel";

export type ComposerMessageReferenceState =
  | {
      kind: "reply";
      message: MessageReferencePreview;
    }
  | {
      kind: "forward";
      message: MessageReferencePreview;
      sourceChatID: string;
      attachments?: ChatAttachment[];
    };

type ForwardComposerReferenceState = Extract<ComposerMessageReferenceState, { kind: "forward" }>;

interface ForwardTargetPickerState {
  source: ForwardComposerReferenceState;
  targets: ConversationSummary[];
  loading: boolean;
  errorMessage: string | null;
}

function buildComposerMessageReferencePreview(message: ChatMessage): MessageReferencePreview {
  const bodyPreview = resolveMessageBodyForDisplay(message).trim();
  const hasAttachments = (message.attachments?.length ?? 0) > 0;
  return {
    chat_id: message.chat_id,
    message_id: message.message_id,
    sequence: message.sequence,
    sender_user_id: message.sender_user_id,
    ...(bodyPreview === "" ? {} : { body_preview: bodyPreview }),
    ...(hasAttachments ? { has_attachments: true } : {}),
    created_at: message.created_at,
  };
}

export function buildReplyComposerReference(
  message: ChatMessage,
  sourceSecretChat: boolean,
): ComposerMessageReferenceState | null {
  const deleted = typeof message.deleted_at === "string" && message.deleted_at !== "";
  if (!canReplyToMessage({ deleted, secretChat: sourceSecretChat })) {
    return null;
  }
  return {
    kind: "reply",
    message: buildComposerMessageReferencePreview(message),
  };
}

export function buildForwardComposerReference(
  message: ChatMessage,
  sourceSecretChat: boolean,
): ComposerMessageReferenceState | null {
  const deleted = typeof message.deleted_at === "string" && message.deleted_at !== "";
  const sourceChatID = message.chat_id.trim();
  if (sourceChatID === "" || !canForwardMessage({ deleted, sourceSecretChat })) {
    return null;
  }
  const attachments = normalizeAttachmentCacheList(message.attachments);
  return {
    kind: "forward",
    sourceChatID,
    message: buildComposerMessageReferencePreview(message),
    ...(attachments.length === 0 ? {} : { attachments }),
  };
}

export function applyComposerReferenceToSendPayload(
  payload: SendChatMessagePayload,
  reference: ComposerMessageReferenceState | null,
): SendChatMessagePayload {
  if (reference === null) {
    return payload;
  }
  if (reference.kind === "reply") {
    return {
      ...payload,
      reply_to_message_id: reference.message.message_id,
    };
  }
  return {
    ...payload,
    forward_from_chat_id: reference.sourceChatID,
    forward_from_message_id: reference.message.message_id,
    ...(reference.attachments === undefined || reference.attachments.length === 0
      ? {}
      : { attachments: reference.attachments }),
  };
}

export function reduceComposerReferenceAfterSend<T extends ComposerMessageReferenceState | null>(
  reference: T,
  result: ComposerReferenceSendResult,
): T | null {
  return result === "failure" ? reference : null;
}

export function filterForwardTargetConversations(conversations: ConversationSummary[]): ConversationSummary[] {
  return conversations.filter((conversation) => {
    const chatID = conversation.chat_id.trim();
    const type = conversation.type.trim().toLowerCase();
    if (chatID === "" || (type !== "direct" && type !== "group" && type !== "channel")) {
      return false;
    }
    return conversation.lifecycle_state !== "closed" && conversation.lifecycle_state !== "removed";
  });
}

export function mapForwardSendGatewayErrorCode(code: string): string {
  switch (code) {
    case "forbidden":
    case "not_found":
    case "conflict":
      return "chatThread.forward.errors.unavailable";
    case "invalid_request":
      return "chatThread.forward.errors.invalid";
    case "unauthorized":
      return "chatThread.forward.errors.unauthorized";
    default:
      return "chatThread.forward.errors.failed";
  }
}

function readForwardComposerReferenceFromLocationState(state: unknown): ForwardComposerReferenceState | null {
  if (state === null || typeof state !== "object" || Array.isArray(state)) {
    return null;
  }
  const record = state as { forwardComposerReference?: unknown };
  const rawReference = record.forwardComposerReference;
  if (rawReference === null || typeof rawReference !== "object" || Array.isArray(rawReference)) {
    return null;
  }
  const reference = rawReference as {
    kind?: unknown;
    sourceChatID?: unknown;
    message?: unknown;
    attachments?: unknown;
  };
  if (reference.kind !== "forward" || typeof reference.sourceChatID !== "string") {
    return null;
  }
  const sourceChatID = reference.sourceChatID.trim();
  if (sourceChatID === "" || reference.message === null || typeof reference.message !== "object" || Array.isArray(reference.message)) {
    return null;
  }
  const message = reference.message as Partial<MessageReferencePreview>;
  const messageID = typeof message.message_id === "string" ? message.message_id.trim() : "";
  const sequence = typeof message.sequence === "number" && Number.isFinite(message.sequence) ? Math.trunc(message.sequence) : 0;
  if (messageID === "" || sequence <= 0) {
    return null;
  }
  const chatID = typeof message.chat_id === "string" ? message.chat_id.trim() : "";
  const senderUserID = typeof message.sender_user_id === "string" ? message.sender_user_id.trim() : "";
  const bodyPreview = typeof message.body_preview === "string" ? message.body_preview.trim() : "";
  const createdAt = typeof message.created_at === "string" ? message.created_at.trim() : "";
  const hasAttachments = typeof message.has_attachments === "boolean" ? message.has_attachments : undefined;
  const attachments = normalizeAttachmentCacheList(reference.attachments);
  return {
    kind: "forward",
    sourceChatID,
    message: {
      ...(chatID === "" ? {} : { chat_id: chatID }),
      message_id: messageID,
      sequence,
      ...(senderUserID === "" ? {} : { sender_user_id: senderUserID }),
      ...(bodyPreview === "" ? {} : { body_preview: bodyPreview }),
      ...(hasAttachments === undefined ? {} : { has_attachments: hasAttachments }),
      ...(createdAt === "" ? {} : { created_at: createdAt }),
    },
    ...(attachments.length === 0 ? {} : { attachments }),
  };
}

export function resolveManageMembersConversationType(
  conversation: ConversationSummary | null,
): ManageMembersConversationType | null {
  if (conversation === null) {
    return null;
  }
  if (conversation.type === "group" || conversation.type === "channel") {
    return conversation.type;
  }
  return null;
}

export function shouldShowManageMembersAction(
  hasChatAccess: boolean,
  conversation: ConversationSummary | null,
): boolean {
  if (!hasChatAccess) {
    return false;
  }
  return resolveManageMembersConversationType(conversation) !== null;
}

export function canManageMembersAddParticipants(
  conversation: ConversationSummary | null,
  actorUserID: string | null,
): boolean {
  const conversationType = resolveManageMembersConversationType(conversation);
  if (conversationType === null) {
    return false;
  }
  const actorRole = resolveActorMembershipRole(conversation, actorUserID);
  if (actorRole === null) {
    return false;
  }
  if (conversationType === "group") {
    return actorRole === "admin";
  }
  return actorRole === "admin" || actorRole === "publisher";
}

export function resolveManageMembersSubmitLabel(conversationType: ManageMembersConversationType): string {
  return conversationType === "group"
    ? "chatThread.manageMembers.actions.addMember"
    : "chatThread.manageMembers.actions.addSubscriber";
}

function resolveManageMembersAddSuccessMessage(conversationType: ManageMembersConversationType): string {
  return conversationType === "group"
    ? "chatThread.manageMembers.success.memberAdded"
    : "chatThread.manageMembers.success.subscriberAdded";
}

function resolveManageMembersRemoveSuccessMessage(conversationType: ManageMembersConversationType): string {
  return conversationType === "group"
    ? "chatThread.manageMembers.success.memberRemoved"
    : "chatThread.manageMembers.success.subscriberRemoved";
}

function resolveManageMembersAddDefaultError(conversationType: ManageMembersConversationType): string {
  return conversationType === "group"
    ? "chatThread.manageMembers.errors.addFailedGroup"
    : "chatThread.manageMembers.errors.addFailedChannel";
}

function resolveManageMembersRemoveDefaultError(conversationType: ManageMembersConversationType): string {
  return conversationType === "group"
    ? "chatThread.manageMembers.errors.removeFailedGroup"
    : "chatThread.manageMembers.errors.removeFailedChannel";
}

export function mapManageMembersGatewayErrorCode(
  errorCode: string,
  conversationType: ManageMembersConversationType,
): string {
  const normalizedCode = errorCode.trim().toLowerCase();
  switch (normalizedCode) {
    case "invalid_request":
      return "chatThread.manageMembers.errors.invalidIdentifier";
    case "not_found":
      return "chatThread.manageMembers.errors.userNotFound";
    case "forbidden":
      return conversationType === "group"
        ? "chatThread.manageMembers.errors.addForbiddenGroup"
        : "chatThread.manageMembers.errors.addForbiddenChannel";
    case "conflict":
      return conversationType === "group"
        ? "chatThread.manageMembers.errors.alreadyMember"
        : "chatThread.manageMembers.errors.alreadySubscriber";
    default:
      return resolveManageMembersAddDefaultError(conversationType);
  }
}

export function mapManageMembersRemoveGatewayErrorCode(
  errorCode: string,
  conversationType: ManageMembersConversationType,
): string {
  const normalizedCode = errorCode.trim().toLowerCase();
  switch (normalizedCode) {
    case "invalid_request":
      return "chatThread.manageMembers.errors.removeInvalidSelection";
    case "not_found":
      return conversationType === "group"
        ? "chatThread.manageMembers.errors.removeNotFoundGroup"
        : "chatThread.manageMembers.errors.removeNotFoundChannel";
    case "forbidden":
      return conversationType === "group"
        ? "chatThread.manageMembers.errors.removeForbiddenGroup"
        : "chatThread.manageMembers.errors.removeForbiddenChannel";
    case "conflict":
      return conversationType === "group"
        ? "chatThread.manageMembers.errors.removeConflictGroup"
        : "chatThread.manageMembers.errors.removeConflictChannel";
    default:
      return resolveManageMembersRemoveDefaultError(conversationType);
  }
}

function normalizeManageMembersRole(role: string): string {
  const normalized = role.trim().toLowerCase();
  return normalized === "" ? "member" : normalized;
}

function normalizeConversationLifecycleState(conversation: ConversationSummary | null): string {
  return conversation?.lifecycle_state?.trim().toLowerCase() ?? "active";
}

export function resolveLifecycleConversationType(
  conversation: ConversationSummary | null,
): LifecycleConversationType | null {
  if (conversation === null) {
    return null;
  }
  if (conversation.type === "group" || conversation.type === "channel") {
    return conversation.type;
  }
  return null;
}

export function canManageConversationLifecycle(
  conversation: ConversationSummary | null,
  actorUserID: string | null,
): boolean {
  if (resolveLifecycleConversationType(conversation) === null || conversation === null) {
    return false;
  }
  if (normalizeConversationLifecycleState(conversation) !== "active") {
    return false;
  }

  const actor = actorUserID?.trim() ?? "";
  if (actor === "") {
    return false;
  }

  const ownerUserID = conversation.owner_user_id?.trim() ?? "";
  if (ownerUserID !== "") {
    return actor === ownerUserID;
  }

  const adminMemberships = conversation.memberships
    .map((membership) => ({
      userID: membership.user_id.trim(),
      role: normalizeManageMembersRole(membership.role),
    }))
    .filter((membership) => membership.userID !== "" && membership.role === "admin");

  return adminMemberships.length === 1 && adminMemberships[0]?.userID === actor;
}

export function shouldShowLifecycleControls(
  hasChatAccess: boolean,
  conversation: ConversationSummary | null,
  actorUserID: string | null,
): boolean {
  return hasChatAccess && canManageConversationLifecycle(conversation, actorUserID);
}

export function canDeleteDirectConversation(
  hasChatAccess: boolean,
  conversation: ConversationSummary | null,
): boolean {
  if (!hasChatAccess || conversation === null || conversation.type !== "direct") {
    return false;
  }
  return normalizeConversationLifecycleState(conversation) === "active";
}

export function isConversationLifecycleReadOnly(conversation: ConversationSummary | null): boolean {
  if (resolveLifecycleConversationType(conversation) === null) {
    return false;
  }
  return normalizeConversationLifecycleState(conversation) !== "active";
}

export function resolveLifecycleConfirmationPhrase(conversation: ConversationSummary | null): string {
  const name = conversation?.name?.trim() ?? "";
  return name === "" ? LIFECYCLE_CONFIRMATION_FALLBACK_PHRASE : name;
}

function containsRawIdentifier(value: string): boolean {
  return /(^|[^a-z0-9])(usr|dir|dch|sec|grp|chn)_[a-z0-9_-]+/i.test(value);
}

export function resolveDirectDeleteConfirmationPhrase(
  conversation: ConversationSummary | null,
  displayLabel = "",
): string {
  if (conversation === null || conversation.type !== "direct") {
    return DIRECT_DELETE_CONFIRMATION_FALLBACK_PHRASE;
  }

  const normalizedDisplayLabel = displayLabel.trim();
  if (normalizedDisplayLabel !== "" && !containsRawIdentifier(normalizedDisplayLabel)) {
    return normalizedDisplayLabel;
  }

  const name = conversation.name?.trim() ?? "";
  if (name !== "" && !containsRawIdentifier(name)) {
    return name;
  }

  return DIRECT_DELETE_CONFIRMATION_FALLBACK_PHRASE;
}

function isManageMembersRoleProtected(role: string): boolean {
  return role === "publisher" || role === "owner";
}

export function resolveManageMembersEntries(
  conversation: ConversationSummary | null,
  actorUserID: string | null,
): ManageMembersEntry[] {
  const conversationType = resolveManageMembersConversationType(conversation);
  if (conversationType === null || conversation === null) {
    return [];
  }

  const actor = actorUserID?.trim() ?? "";
  const actorRole = resolveActorMembershipRole(conversation, actorUserID);
  const canRemoveOthers =
    conversationType === "group" ? actorRole === "admin" : actorRole === "admin" || actorRole === "publisher";
  const normalizedMemberships = conversation.memberships
    .map((membership) => ({
      userID: membership.user_id.trim(),
      role: normalizeManageMembersRole(membership.role),
    }))
    .filter((membership) => membership.userID !== "");

  const adminCount = normalizedMemberships.reduce(
    (count, membership) => (membership.role === "admin" ? count + 1 : count),
    0,
  );

  return normalizedMemberships
    .sort((left, right) => left.userID.localeCompare(right.userID))
    .map((membership) => {
      const isSelf = actor !== "" && membership.userID === actor;
      const isAdmin = membership.role === "admin";
      const isProtectedByRole =
        isManageMembersRoleProtected(membership.role) ||
        (conversationType === "channel" && (membership.role === "admin" || membership.role === "publisher"));
      const removableByLeave = isSelf;
      const removableByModeration =
        !isSelf && canRemoveOthers && !isProtectedByRole && !(isAdmin && adminCount <= 1);
      const removable = removableByLeave || removableByModeration;
      return {
        userID: membership.userID,
        role: membership.role,
        removable,
      };
    });
}

function resolveActorMembershipRole(
  conversation: ConversationSummary | null,
  actorUserID: string | null,
): string | null {
  if (conversation === null) {
    return null;
  }
  const actor = actorUserID?.trim() ?? "";
  if (actor === "") {
    return null;
  }
  const actorMembership = conversation.memberships.find((membership) => membership.user_id.trim() === actor);
  if (actorMembership === undefined) {
    return null;
  }
  return normalizeManageMembersRole(actorMembership.role);
}

export function resolveMetadataEditorConversationType(
  conversation: ConversationSummary | null,
  actorUserID: string | null,
): ManageMembersConversationType | null {
  if (conversation === null) {
    return null;
  }
  const actorRole = resolveActorMembershipRole(conversation, actorUserID);
  if (actorRole === null) {
    return null;
  }
  if (conversation.type === "group") {
    return actorRole === "admin" ? "group" : null;
  }
  if (conversation.type === "channel") {
    return actorRole === "admin" || actorRole === "publisher" ? "channel" : null;
  }
  return null;
}

export function validateMetadataEditorName(name: string): string | null {
  const normalized = name.trim();
  if (normalized === "") {
    return "chatThread.metadataEditor.validation.nameRequired";
  }
  if (/\s/.test(normalized)) {
    return "chatThread.metadataEditor.validation.nameOneWord";
  }
  if (normalized.length > METADATA_EDITOR_NAME_MAX_LENGTH) {
    return "chatThread.metadataEditor.validation.nameMax";
  }
  return null;
}

export function validateMetadataEditorDescription(description: string): string | null {
  const normalized = description.trim();
  if (normalized.includes("\n") || normalized.includes("\r")) {
    return "chatThread.metadataEditor.validation.descriptionSingleLine";
  }
  if (normalized.length > METADATA_EDITOR_DESCRIPTION_MAX_LENGTH) {
    return "chatThread.metadataEditor.validation.descriptionMax";
  }
  return null;
}

function resolveMetadataEditorDefaultError(conversationType: ManageMembersConversationType): string {
  return conversationType === "group"
    ? "chatThread.metadataEditor.errors.updateFailedGroup"
    : "chatThread.metadataEditor.errors.updateFailedChannel";
}

function resolveMetadataEditorSuccessMessage(conversationType: ManageMembersConversationType): string {
  return conversationType === "group"
    ? "chatThread.metadataEditor.success.groupUpdated"
    : "chatThread.metadataEditor.success.channelUpdated";
}

export function mapMetadataEditorGatewayErrorCode(
  errorCode: string,
  conversationType: ManageMembersConversationType,
): string {
  switch (errorCode.trim().toLowerCase()) {
    case "invalid_request":
      return "chatThread.metadataEditor.errors.invalidRequest";
    case "forbidden":
      return conversationType === "group"
        ? "chatThread.metadataEditor.errors.forbiddenGroup"
        : "chatThread.metadataEditor.errors.forbiddenChannel";
    case "not_found":
      return conversationType === "group"
        ? "chatThread.metadataEditor.errors.notFoundGroup"
        : "chatThread.metadataEditor.errors.notFoundChannel";
    case "conflict":
      return conversationType === "group"
        ? "chatThread.metadataEditor.errors.conflictGroup"
        : "chatThread.metadataEditor.errors.conflictChannel";
    default:
      return resolveMetadataEditorDefaultError(conversationType);
  }
}

function resolveLifecycleDefaultError(action: LifecycleAction, conversationType: LifecycleConversationType): string {
  if (action === "close") {
    return conversationType === "group"
      ? "chatThread.lifecycle.errors.closeFailedGroup"
      : "chatThread.lifecycle.errors.closeFailedChannel";
  }
  return conversationType === "group"
    ? "chatThread.lifecycle.errors.removeFailedGroup"
    : "chatThread.lifecycle.errors.removeFailedChannel";
}

function resolveLifecycleSuccessMessage(action: LifecycleAction, conversationType: LifecycleConversationType): string {
  if (action === "close") {
    return conversationType === "group"
      ? "chatThread.lifecycle.success.groupClosed"
      : "chatThread.lifecycle.success.channelClosed";
  }
  return conversationType === "group"
    ? "chatThread.lifecycle.success.groupRemoved"
    : "chatThread.lifecycle.success.channelRemoved";
}

export function mapLifecycleGatewayErrorCode(
  errorCode: string | undefined,
  action: LifecycleAction,
  conversationType: LifecycleConversationType,
): string {
  switch (errorCode?.trim().toLowerCase()) {
    case "invalid_request":
      if (action === "close") {
        return conversationType === "group"
          ? "chatThread.lifecycle.errors.closeInvalidGroup"
          : "chatThread.lifecycle.errors.closeInvalidChannel";
      }
      return conversationType === "group"
        ? "chatThread.lifecycle.errors.removeInvalidGroup"
        : "chatThread.lifecycle.errors.removeInvalidChannel";
    case "forbidden":
      if (action === "close") {
        return conversationType === "group"
          ? "chatThread.lifecycle.errors.closeForbiddenGroup"
          : "chatThread.lifecycle.errors.closeForbiddenChannel";
      }
      return conversationType === "group"
        ? "chatThread.lifecycle.errors.removeForbiddenGroup"
        : "chatThread.lifecycle.errors.removeForbiddenChannel";
    case "not_found":
      if (action === "close") {
        return conversationType === "group"
          ? "chatThread.lifecycle.errors.closeNotFoundGroup"
          : "chatThread.lifecycle.errors.closeNotFoundChannel";
      }
      return conversationType === "group"
        ? "chatThread.lifecycle.errors.removeNotFoundGroup"
        : "chatThread.lifecycle.errors.removeNotFoundChannel";
    case "conflict":
      if (action === "close") {
        return conversationType === "group"
          ? "chatThread.lifecycle.errors.closeConflictGroup"
          : "chatThread.lifecycle.errors.closeConflictChannel";
      }
      return conversationType === "group"
        ? "chatThread.lifecycle.errors.removeConflictGroup"
        : "chatThread.lifecycle.errors.removeConflictChannel";
    default:
      return resolveLifecycleDefaultError(action, conversationType);
  }
}

export function mapDirectDeleteGatewayErrorCode(errorCode: string | undefined): string {
  switch (errorCode?.trim().toLowerCase()) {
    case "invalid_request":
      return "chatThread.directDelete.errors.invalidRequest";
    case "unauthorized":
      return "chatThread.directDelete.errors.unauthorized";
    case "not_found":
      return "chatThread.directDelete.errors.notFound";
    case "conflict":
      return "chatThread.directDelete.errors.conflict";
    default:
      return "chatThread.directDelete.errors.failed";
  }
}

export type ThreadLifecycleRealtimeAction = "refresh" | "navigate-away";

export function resolveThreadLifecycleRealtimeAction(
  event: RealtimeEvent,
  activeChatID: string,
): ThreadLifecycleRealtimeAction | null {
  const chatID = activeChatID.trim();
  if (chatID === "" || event.chat_id.trim() !== chatID) {
    return null;
  }
  if (event.conversation_type !== "group" && event.conversation_type !== "channel") {
    return null;
  }
  const actorUserID = event.actor_user_id?.trim() ?? "";
  const occurredAt = event.occurred_at?.trim() ?? "";
  if (actorUserID === "" || occurredAt === "") {
    return null;
  }
  if (event.type === "conversation.closed" && event.lifecycle_state === "closed") {
    return "refresh";
  }
  if (event.type === "conversation.removed" && event.lifecycle_state === "removed") {
    return "navigate-away";
  }
  return null;
}

export function createInitialMetadataEditorUIState(): MetadataEditorUIState {
  return {
    name: "",
    description: "",
    submitting: false,
    errorMessage: null,
    successMessage: null,
  };
}

export function reduceMetadataEditorUIState(
  current: MetadataEditorUIState,
  event: MetadataEditorUIEvent,
): MetadataEditorUIState {
  switch (event.type) {
    case "seed":
      return {
        name: event.name.trim(),
        description: event.description.trim(),
        submitting: false,
        errorMessage: null,
        successMessage: null,
      };
    case "name-changed":
      return {
        ...current,
        name: event.value,
        errorMessage: null,
        successMessage: null,
      };
    case "description-changed":
      return {
        ...current,
        description: event.value,
        errorMessage: null,
        successMessage: null,
      };
    case "submit-requested":
      return {
        ...current,
        submitting: true,
        errorMessage: null,
        successMessage: null,
      };
    case "submit-succeeded":
      return {
        name: event.name.trim(),
        description: event.description.trim(),
        submitting: false,
        errorMessage: null,
        successMessage: resolveMetadataEditorSuccessMessage(event.conversationType),
      };
    case "submit-failed": {
      const fallbackMessage = event.fallbackMessage?.trim() ?? "";
      const resolvedMessage =
        event.errorCode?.trim() !== ""
          ? mapMetadataEditorGatewayErrorCode(event.errorCode ?? "", event.conversationType)
          : fallbackMessage === ""
            ? resolveMetadataEditorDefaultError(event.conversationType)
            : fallbackMessage;
      return {
        ...current,
        submitting: false,
        errorMessage: resolvedMessage,
        successMessage: null,
      };
    }
    case "clear-feedback":
      if (current.errorMessage === null && current.successMessage === null) {
        return current;
      }
      return {
        ...current,
        errorMessage: null,
        successMessage: null,
      };
    case "reset":
      return createInitialMetadataEditorUIState();
    default:
      return current;
  }
}

export function createInitialDirectDeleteUIState(): DirectDeleteUIState {
  return {
    panelOpen: false,
    confirmationValue: "",
    loading: false,
    errorMessage: null,
  };
}

export function isDirectDeleteConfirmationSubmitEnabled(
  state: DirectDeleteUIState,
  confirmationPhrase: string,
): boolean {
  const normalizedPhrase = confirmationPhrase.trim();
  return (
    state.panelOpen &&
    !state.loading &&
    normalizedPhrase !== "" &&
    state.confirmationValue.trim() === normalizedPhrase
  );
}

export function reduceDirectDeleteUIState(
  current: DirectDeleteUIState,
  event: DirectDeleteUIEvent,
): DirectDeleteUIState {
  switch (event.type) {
    case "open":
      return {
        panelOpen: true,
        confirmationValue: "",
        loading: false,
        errorMessage: null,
      };
    case "confirmation-changed":
      return {
        ...current,
        confirmationValue: event.value,
        errorMessage: null,
      };
    case "submit-requested":
      if (!current.panelOpen) {
        return current;
      }
      return {
        ...current,
        loading: true,
        errorMessage: null,
      };
    case "submit-succeeded":
      return createInitialDirectDeleteUIState();
    case "submit-failed": {
      const fallbackMessage = event.fallbackMessage?.trim() ?? "";
      const resolvedMessage =
        event.errorCode?.trim() !== ""
          ? mapDirectDeleteGatewayErrorCode(event.errorCode)
          : fallbackMessage === ""
            ? mapDirectDeleteGatewayErrorCode(undefined)
            : fallbackMessage;
      return {
        ...current,
        panelOpen: true,
        loading: false,
        errorMessage: resolvedMessage,
      };
    }
    case "cancel":
      return createInitialDirectDeleteUIState();
    case "clear-feedback":
      if (current.errorMessage === null) {
        return current;
      }
      return {
        ...current,
        errorMessage: null,
      };
    case "reset":
      return createInitialDirectDeleteUIState();
    default:
      return current;
  }
}

export function createInitialLifecycleActionUIState(): LifecycleActionUIState {
  return {
    pendingAction: null,
    confirmationValue: "",
    loading: false,
    statusMessage: null,
    errorMessage: null,
  };
}

export function isLifecycleConfirmationSubmitEnabled(
  state: LifecycleActionUIState,
  confirmationPhrase: string,
): boolean {
  const normalizedPhrase = confirmationPhrase.trim();
  return (
    state.pendingAction !== null &&
    !state.loading &&
    normalizedPhrase !== "" &&
    state.confirmationValue.trim() === normalizedPhrase
  );
}

export function reduceLifecycleActionUIState(
  current: LifecycleActionUIState,
  event: LifecycleActionUIEvent,
): LifecycleActionUIState {
  switch (event.type) {
    case "open":
      return {
        pendingAction: event.action,
        confirmationValue: "",
        loading: false,
        statusMessage: null,
        errorMessage: null,
      };
    case "confirmation-changed":
      return {
        ...current,
        confirmationValue: event.value,
        statusMessage: null,
        errorMessage: null,
      };
    case "submit-requested":
      if (current.pendingAction === null) {
        return current;
      }
      return {
        ...current,
        loading: true,
        statusMessage: null,
        errorMessage: null,
      };
    case "submit-succeeded":
      return {
        ...createInitialLifecycleActionUIState(),
        statusMessage: resolveLifecycleSuccessMessage(event.action, event.conversationType),
      };
    case "submit-failed": {
      const fallbackMessage = event.fallbackMessage?.trim() ?? "";
      const resolvedMessage =
        event.errorCode?.trim() !== ""
          ? mapLifecycleGatewayErrorCode(event.errorCode, event.action, event.conversationType)
          : fallbackMessage === ""
            ? resolveLifecycleDefaultError(event.action, event.conversationType)
            : fallbackMessage;
      return {
        ...current,
        pendingAction: event.action,
        loading: false,
        errorMessage: resolvedMessage,
        statusMessage: null,
      };
    }
    case "cancel":
      return createInitialLifecycleActionUIState();
    case "clear-feedback":
      if (current.errorMessage === null && current.statusMessage === null) {
        return current;
      }
      return {
        ...current,
        errorMessage: null,
        statusMessage: null,
      };
    case "reset":
      return createInitialLifecycleActionUIState();
    default:
      return current;
  }
}

export async function executeConversationLifecycleAction({
  chatID,
  action,
  conversationType,
  closeGroupConversation: closeGroup,
  closeChannelConversation: closeChannel,
  removeGroupConversation: removeGroup,
  removeChannelConversation: removeChannel,
  refreshThreadAfterClose,
  navigateToSafeChatShell,
}: ExecuteConversationLifecycleActionInput): Promise<void> {
  if (action === "close") {
    if (conversationType === "group") {
      await closeGroup(chatID);
    } else {
      await closeChannel(chatID);
    }
    await refreshThreadAfterClose();
    return;
  }

  if (conversationType === "group") {
    await removeGroup(chatID);
  } else {
    await removeChannel(chatID);
  }
  navigateToSafeChatShell();
}

export async function executeDirectDeleteAction({
  chatID,
  deleteDirectConversation: deleteDirect,
  navigateToSafeChatShell,
}: ExecuteDirectDeleteActionInput): Promise<void> {
  await deleteDirect(chatID);
  navigateToSafeChatShell();
}

export function DirectDeleteConfirmationPanel({
  state,
  confirmationPhrase,
  submitEnabled,
  errorMessage,
  translate,
  onCancel,
  onConfirmationChange,
  onSubmit,
}: DirectDeleteConfirmationPanelProps): JSX.Element {
  return (
    <section className="direct-delete-panel" data-testid="direct-delete-confirmation-panel">
      <div className="direct-delete-panel-head">
        <p className="message-state">{translate("chatThread.directDelete.title")}</p>
        <p className="muted-line">{translate("chatThread.directDelete.helper")}</p>
      </div>
      <form className="direct-delete-form" onSubmit={onSubmit}>
        <label className="chat-field">
          <span>{translate("chatThread.directDelete.confirmationLabel", { phrase: confirmationPhrase })}</span>
          <input
            data-testid="direct-delete-confirmation-input"
            value={state.confirmationValue}
            onChange={(changeEvent) => onConfirmationChange(changeEvent.target.value)}
            placeholder={translate("chatThread.directDelete.confirmationPlaceholder")}
            disabled={state.loading}
          />
        </label>
        <p className="direct-delete-confirmation-hint">
          {translate("chatThread.directDelete.confirmationRequirement")}
        </p>
        <div className="direct-delete-actions">
          <button className="secondary-button" type="button" onClick={onCancel} disabled={state.loading}>
            {translate("chatThread.directDelete.actions.cancel")}
          </button>
          <button
            className="danger-button"
            type="submit"
            data-testid="direct-delete-submit"
            disabled={!submitEnabled}
          >
            {state.loading
              ? translate("chatThread.directDelete.actions.deleting")
              : translate("chatThread.directDelete.actions.delete")}
          </button>
        </div>
      </form>
      {errorMessage !== null ? (
        <p className="inline-error" data-testid="direct-delete-error">
          {errorMessage}
        </p>
      ) : null}
    </section>
  );
}

export function createInitialManageMembersUIState(): ManageMembersUIState {
  return {
    identifier: "",
    submitting: false,
    removingUserID: null,
    errorMessage: null,
    successMessage: null,
  };
}

export function reduceManageMembersUIState(
  current: ManageMembersUIState,
  event: ManageMembersUIEvent,
): ManageMembersUIState {
  switch (event.type) {
    case "identifier-changed":
      return {
        ...current,
        identifier: event.value,
        errorMessage: null,
        successMessage: null,
      };
    case "submit-requested":
      return {
        ...current,
        submitting: true,
        removingUserID: null,
        errorMessage: null,
        successMessage: null,
      };
    case "submit-succeeded":
      return {
        identifier: "",
        submitting: false,
        removingUserID: null,
        errorMessage: null,
        successMessage: resolveManageMembersAddSuccessMessage(event.conversationType),
      };
    case "submit-failed": {
      const fallbackMessage = event.fallbackMessage?.trim() ?? "";
      const resolvedMessage =
        event.errorCode?.trim() !== ""
          ? mapManageMembersGatewayErrorCode(event.errorCode ?? "", event.conversationType)
          : fallbackMessage === ""
            ? resolveManageMembersAddDefaultError(event.conversationType)
            : fallbackMessage;
      return {
        ...current,
        submitting: false,
        removingUserID: null,
        errorMessage: resolvedMessage,
        successMessage: null,
      };
    }
    case "remove-requested":
      return {
        ...current,
        removingUserID: event.userID.trim() === "" ? null : event.userID.trim(),
        errorMessage: null,
        successMessage: null,
      };
    case "remove-succeeded":
      return {
        ...current,
        removingUserID: null,
        errorMessage: null,
        successMessage: resolveManageMembersRemoveSuccessMessage(event.conversationType),
      };
    case "remove-failed": {
      const fallbackMessage = event.fallbackMessage?.trim() ?? "";
      const resolvedMessage =
        event.errorCode?.trim() !== ""
          ? mapManageMembersRemoveGatewayErrorCode(event.errorCode ?? "", event.conversationType)
          : fallbackMessage === ""
            ? resolveManageMembersRemoveDefaultError(event.conversationType)
            : fallbackMessage;
      return {
        ...current,
        removingUserID: null,
        errorMessage: resolvedMessage,
        successMessage: null,
      };
    }
    case "clear-feedback":
      if (current.errorMessage === null && current.successMessage === null) {
        return current;
      }
      return {
        ...current,
        errorMessage: null,
        successMessage: null,
      };
    case "reset":
      return createInitialManageMembersUIState();
    default:
      return current;
  }
}

export function getRealtimeTone(state: RealtimeState): "online" | "busy" | "offline" {
  if (state === "connected") {
    return "online";
  }
  if (state === "connecting") {
    return "busy";
  }
  return "offline";
}

export function resolveThreadTitle(
  chatID: string,
  conversation: ConversationSummary | null,
  actorUserID: string | null,
): string {
  if (chatID === "") {
    return "Unknown chat";
  }

  if (conversation === null) {
    return chatID;
  }

  const title = describeConversationWithDisplayName(conversation, actorUserID);
  if (conversation.type === "secret") {
    return `Secret • ${title}`;
  }
  return title;
}

function deriveConversationInitial(value: string): string {
  const source = value.trim();
  if (source === "") {
    return "#";
  }
  return source[0]?.toUpperCase() ?? "#";
}

export interface ThreadHeaderAvatarDescriptor {
  initial: string;
  sourceURL: string;
  profileAvatarBinaryPath: string | null;
  mediaAssetBinaryPath: string | null;
}

export type MetadataEditorAvatarPatchMode = "keep" | "remove" | "replace";

function normalizeConversationAvatarAssetID(value: string | null | undefined): string {
  if (typeof value !== "string") {
    return "";
  }
  return value.trim();
}

export function resolveMetadataEditorAvatarAssetPatch(
  mode: MetadataEditorAvatarPatchMode,
  replacementAssetID?: string,
): string | undefined {
  if (mode === "keep") {
    return undefined;
  }
  if (mode === "remove") {
    return "";
  }
  return normalizeConversationAvatarAssetID(replacementAssetID);
}

function mapMetadataEditorAvatarUploadError(error: unknown): string {
  if (error instanceof GatewayAPIError) {
    switch (error.code) {
      case "not_found":
        return "chatThread.metadataEditor.errors.avatarNotFound";
      case "conflict":
        return "chatThread.metadataEditor.errors.avatarProcessing";
      case "invalid_request":
        return "chatThread.metadataEditor.errors.avatarInvalid";
      default:
        return "chatThread.metadataEditor.errors.avatarUploadFailed";
    }
  }
  return "chatThread.metadataEditor.errors.avatarUploadFailed";
}

export function resolveThreadHeaderAvatarDescriptor(
  conversation: ConversationSummary | null,
  actorUserID: string | null,
): ThreadHeaderAvatarDescriptor {
  if (conversation === null) {
    return {
      initial: "#",
      sourceURL: "",
      profileAvatarBinaryPath: null,
      mediaAssetBinaryPath: null,
    };
  }

  const title = describeConversationWithDisplayName(conversation, actorUserID);
  if (conversation.type !== "direct") {
    const avatarAssetID = normalizeConversationAvatarAssetID(conversation.avatar_asset_id);
    return {
      initial: deriveConversationInitial(title),
      sourceURL: "",
      profileAvatarBinaryPath: null,
      mediaAssetBinaryPath:
        avatarAssetID === ""
          ? null
          : resolveMediaBinaryURL(avatarAssetID, undefined, {
              conversationID: conversation.chat_id,
              purpose: "conversation_avatar",
            }),
    };
  }

  const descriptor = describeDirectPeerAvatar(conversation, actorUserID);
  const initial = deriveConversationInitial(descriptor.label);
  if (descriptor.avatarReference !== "" && isAvatarReferenceDirectlyRenderable(descriptor.avatarReference)) {
    return {
      initial,
      sourceURL: descriptor.avatarReference,
      profileAvatarBinaryPath: null,
      mediaAssetBinaryPath: null,
    };
  }
  return {
    initial,
    sourceURL: "",
    profileAvatarBinaryPath: descriptor.avatarBinaryPath,
    mediaAssetBinaryPath: null,
  };
}

export function mapSecretSendGatewayErrorCode(errorCode: string): string {
  switch (errorCode.trim().toLowerCase()) {
    case "invalid_request":
      return "chatThread.secretEnvelope.errors.invalidEnvelope";
    case "conflict":
      return "chatThread.secretEnvelope.errors.ratchetOutOfSync";
    case "forbidden":
      return "chatThread.errors.forbidden";
    default:
      return "chatThread.secretEnvelope.errors.sendFailed";
  }
}

export function resolveMessageSenderLabel(senderUserID: string): string {
  return formatSenderLabel(senderUserID);
}

export type ReplyReferenceContextTarget =
  | { kind: "loaded"; messageID: string }
  | { kind: "context"; aroundSequence: number; messageID: string };

export function resolveMessageReferencePreviewText(
  reference: MessageReferencePreview,
  translate: (key: string) => string,
): string {
  const bodyPreview = reference.body_preview?.trim() ?? "";
  if (bodyPreview !== "") {
    return bodyPreview;
  }
  if (reference.has_attachments === true) {
    return translate("chatThread.references.attachment");
  }
  return translate("chatThread.references.unavailable");
}

export function resolveReplyReferenceContextTarget(
  reference: MessageReferencePreview,
  activeChatID: string,
  messages: ChatMessage[],
): ReplyReferenceContextTarget | null {
  const referenceChatID = reference.chat_id?.trim() ?? "";
  const chatID = activeChatID.trim();
  const messageID = reference.message_id.trim();
  const sequence = Math.trunc(reference.sequence);
  if (chatID === "" || referenceChatID === "" || referenceChatID !== chatID || messageID === "") {
    return null;
  }
  if (!Number.isFinite(sequence) || sequence <= 0) {
    return null;
  }

  const loadedMessage =
    messages.find((message) => message.message_id === messageID) ??
    messages.find((message) => message.sequence === sequence);
  if (loadedMessage !== undefined) {
    return {
      kind: "loaded",
      messageID: loadedMessage.message_id,
    };
  }

  return {
    kind: "context",
    aroundSequence: sequence,
    messageID,
  };
}

function findClosestMessageID(messages: ChatMessage[], aroundSequence: number): string | null {
  if (messages.length === 0) {
    return null;
  }

  let candidate = messages[0] ?? null;
  let delta = candidate === null ? Number.POSITIVE_INFINITY : Math.abs(candidate.sequence - aroundSequence);
  for (const message of messages) {
    const currentDelta = Math.abs(message.sequence - aroundSequence);
    if (candidate === null || currentDelta < delta) {
      candidate = message;
      delta = currentDelta;
    }
  }
  return candidate?.message_id ?? null;
}

export function parseContextNavigationTarget(search: string): ContextNavigationTarget | null {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const rawAround = params.get("around_sequence");
  if (rawAround === null || rawAround.trim() === "") {
    return null;
  }

  const aroundSequence = Number.parseInt(rawAround, 10);
  if (!Number.isFinite(aroundSequence) || aroundSequence <= 0) {
    return null;
  }

  const rawMessageID = (params.get("message_id") ?? "").trim();
  return {
    aroundSequence,
    messageID: rawMessageID === "" ? undefined : rawMessageID,
  };
}

export function buildContextNavigationPath(chatID: string, aroundSequence: number, messageID: string): string {
  const query = new URLSearchParams();
  query.set("around_sequence", String(Math.max(1, Math.trunc(aroundSequence))));

  const normalizedMessageID = messageID.trim();
  if (normalizedMessageID !== "") {
    query.set("message_id", normalizedMessageID);
  }

  return `/app/chats/${encodeURIComponent(chatID)}?${query.toString()}`;
}

export function resolveContextHighlightMessageID(
  messages: ChatMessage[],
  messageID: string | undefined,
  aroundSequence: number,
): string | null {
  if (messages.length === 0) {
    return null;
  }

  if (messageID !== undefined && messageID.trim() !== "") {
    const byID = messages.find((message) => message.message_id === messageID.trim());
    if (byID !== undefined) {
      return byID.message_id;
    }
  }

  const bySequence = messages.find((message) => message.sequence === aroundSequence);
  if (bySequence !== undefined) {
    return bySequence.message_id;
  }

  return findClosestMessageID(messages, aroundSequence);
}

export function sortMessagesAscending(messages: ChatMessage[]): ChatMessage[] {
  return [...messages].sort(compareMessageOrder);
}

export function resolveLatestMessageSequence(messages: ChatMessage[]): number | null {
  let latest: number | null = null;
  for (const message of messages) {
    if (!Number.isFinite(message.sequence)) {
      continue;
    }
    const sequence = Math.trunc(message.sequence);
    if (sequence <= 0) {
      continue;
    }
    if (latest === null || sequence > latest) {
      latest = sequence;
    }
  }
  return latest;
}

export type ReceiptParticipantMap = Record<string, ReceiptParticipantSummary>;

function normalizeReceiptSequence(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.max(0, Math.trunc(value));
}

function normalizeReceiptUpdatedAt(value: string | undefined): string | undefined {
  const normalized = value?.trim() ?? "";
  return normalized === "" ? undefined : normalized;
}

function normalizeReceiptParticipantSummary(
  participant: ReceiptParticipantSummary,
): ReceiptParticipantSummary | null {
  const userID = participant.user_id.trim();
  if (userID === "") {
    return null;
  }
  return {
    user_id: userID,
    delivered_seq: normalizeReceiptSequence(participant.delivered_seq),
    read_seq: normalizeReceiptSequence(participant.read_seq),
    updated_at: normalizeReceiptUpdatedAt(participant.updated_at),
  };
}

function mergeReceiptParticipant(
  current: ReceiptParticipantMap,
  participant: ReceiptParticipantSummary,
): ReceiptParticipantMap {
  const normalized = normalizeReceiptParticipantSummary(participant);
  if (normalized === null) {
    return current;
  }

  const existing = current[normalized.user_id];
  const merged: ReceiptParticipantSummary = {
    user_id: normalized.user_id,
    delivered_seq: Math.max(existing?.delivered_seq ?? 0, normalized.delivered_seq),
    read_seq: Math.max(existing?.read_seq ?? 0, normalized.read_seq),
    updated_at: normalized.updated_at ?? existing?.updated_at,
  };

  if (
    existing !== undefined &&
    existing.delivered_seq === merged.delivered_seq &&
    existing.read_seq === merged.read_seq &&
    existing.updated_at === merged.updated_at
  ) {
    return current;
  }

  return {
    ...current,
    [merged.user_id]: merged,
  };
}

export function mergeReceiptParticipantSummaries(
  current: ReceiptParticipantMap,
  participants: ReceiptParticipantSummary[],
): ReceiptParticipantMap {
  let next = current;
  for (const participant of participants) {
    next = mergeReceiptParticipant(next, participant);
  }
  return next;
}

export function applyReceiptUpdatedEventToParticipantMap(
  current: ReceiptParticipantMap,
  event: RealtimeEvent,
  actorUserID: string | null,
): ReceiptParticipantMap {
  if (event.type !== "receipt.updated") {
    return current;
  }

  const receiptUserID = event.receipt_user_id?.trim() ?? "";
  if (receiptUserID === "") {
    return current;
  }
  const actor = actorUserID?.trim() ?? "";
  if (actor !== "" && receiptUserID === actor) {
    return current;
  }

  return mergeReceiptParticipant(current, {
    user_id: receiptUserID,
    delivered_seq: event.delivered_seq ?? 0,
    read_seq: event.read_seq ?? 0,
    updated_at: event.updated_at,
  });
}

export function applyRealtimeEvent(messages: ChatMessage[], event: RealtimeEvent): ChatMessage[] {
  if (event.type !== "message.created" && event.type !== "message.updated" && event.type !== "message.deleted") {
    return messages;
  }

  const incoming = toRealtimeChatMessage(event);
  if (incoming === null) {
    return messages;
  }

  return upsertMessage(messages, incoming);
}

interface SecretRealtimeEnvelopeFields {
  ciphertext?: string;
  ratchet_session_id?: string;
  ratchet_counter?: number;
  previous_chain_length?: number;
}

function readSecretRealtimeEnvelope(event: RealtimeEvent): SecretRealtimeEnvelopeFields {
  return event as RealtimeEvent & SecretRealtimeEnvelopeFields;
}

export function mergeTimelineMessages(base: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] {
  let merged = base;
  for (const item of incoming) {
    merged = upsertMessage(merged, item);
  }
  return merged;
}

export function shouldHydrateSecretRealtimeEvent(
  event: RealtimeEvent,
  conversation: ConversationSummary | null,
): boolean {
  if (conversation?.type !== "secret") {
    return false;
  }
  if (event.type !== "message.created") {
    return false;
  }

  const body = event.body?.trim() ?? "";
  const ciphertext = readSecretRealtimeEnvelope(event).ciphertext?.trim() ?? "";
  const hasAttachments = (event.attachments?.length ?? 0) > 0;
  return body === "" && ciphertext === "" && !hasAttachments;
}

export interface ThreadTimelineProps {
  loading: boolean;
  chatID: string;
  messages: ChatMessage[];
  actorUserID: string | null;
  sourceSecretChat: boolean;
  participantReceipts: ReceiptParticipantSummary[];
  editingMessageID: string | null;
  editingBody: string;
  highlightedMessageID: string | null;
  defaultLatestScrollRevision: number;
  defaultLatestMessageID: string | null;
  voicePlaybackSources: Record<string, string>;
  photoAttachmentSources: Record<string, string>;
  fileAttachmentMetadata: Record<string, MessageAttachmentMetadata>;
  voicePlayerNativeFallback: Record<string, true>;
  pendingTimelinePhotos: PendingPhotoMessage[];
  pendingTimelineVoices: PendingVoiceMessage[];
  threadPaneRef: MutableRefObject<HTMLElement | null>;
  messageBodyRefs: MutableRefObject<Record<string, HTMLElement | null>>;
  initialActiveMessageActionsID?: string | null;
  onSetEditingBody: (value: string) => void;
  onSaveEdit: (messageID: string) => Promise<void>;
  onCancelEdit: () => void;
  onStartEdit: (message: ChatMessage) => void;
  onReplyToMessage: (message: ChatMessage) => void;
  onForwardMessage: (message: ChatMessage) => void;
  onDeleteMessage: (messageID: string) => Promise<void>;
  onOpenReplyReference: (reference: MessageReferencePreview) => void;
  onHydrateVoicePlaybackSource: (mediaID: string) => Promise<void>;
  onHydratePhotoAttachmentSource: (mediaID: string) => Promise<void>;
  onHydrateFileAttachmentMetadata: (mediaID: string) => Promise<void>;
  onDownloadFileAttachment: (mediaID: string, filename: string) => Promise<void>;
  onSetExpandedPhotoSource: (mediaID: string) => void | Promise<void>;
  onMarkVoicePlayerNativeFallback: (mediaID: string) => void;
  onRetryPendingPhoto: (localID: string) => void;
  onDeletePendingPhoto: (localID: string) => void;
  onRetryPendingVoice: (localID: string) => void;
  onDeletePendingVoice: (localID: string) => void;
}

interface MessageReceiptStatusIndicatorProps {
  messageID: string;
  status: MessageReceiptVisualStatus | null;
  label: string;
}

interface MessageReferencePreviewBlockProps {
  kind: "reply" | "forward";
  messageID: string;
  reference: MessageReferencePreview | undefined;
  clickable?: boolean;
  onOpen?: () => void;
}

export function MessageReceiptStatusIndicator({
  messageID,
  status,
  label,
}: MessageReceiptStatusIndicatorProps) {
  if (status === null) {
    return null;
  }

  return (
    <span
      className={`message-receipt-status message-receipt-status-${status}`}
      data-testid={`message-receipt-status-${messageID}`}
      data-status={status}
      role="img"
      aria-label={label}
      title={label}
    >
      <span className="message-receipt-status-ticks" aria-hidden="true">
        <span className="message-receipt-status-tick message-receipt-status-tick-primary" />
        {status === "sent" ? null : (
          <span className="message-receipt-status-tick message-receipt-status-tick-secondary" />
        )}
      </span>
    </span>
  );
}

export function MessageReferencePreviewBlock({
  kind,
  messageID,
  reference,
  clickable = false,
  onOpen,
}: MessageReferencePreviewBlockProps) {
  const { t } = useTranslation();
  if (reference === undefined) {
    return null;
  }

  const label = kind === "reply" ? t("chatThread.references.reply") : t("chatThread.references.forwarded");
  const senderUserID = reference.sender_user_id?.trim() ?? "";
  const senderLabel = senderUserID === "" ? "" : resolveMessageSenderLabel(senderUserID);
  const meta = senderLabel === "" ? label : `${label} · ${senderLabel}`;
  const previewText = resolveMessageReferencePreviewText(reference, t);
  const className = `message-reference-preview message-reference-preview-${kind}${
    clickable ? " message-reference-preview-clickable" : ""
  }`;
  const testID = `message-reference-preview-${messageID}-${kind}`;
  const content = (
    <>
      <span className="message-reference-preview-meta">{meta}</span>
      <span className="message-reference-preview-body">{previewText}</span>
    </>
  );

  if (clickable) {
    return (
      <button
        className={className}
        type="button"
        onClick={onOpen}
        aria-label={t("chatThread.references.openReply", { sequence: String(reference.sequence) })}
        data-testid={testID}
      >
        {content}
      </button>
    );
  }

  return (
    <div className={className} data-testid={testID}>
      {content}
    </div>
  );
}

interface ComposerReferencePreviewProps {
  reference: ComposerMessageReferenceState | null;
  onCancel: () => void;
}

function ComposerReferencePreview({ reference, onCancel }: ComposerReferencePreviewProps) {
  const { t } = useTranslation();
  if (reference === null) {
    return null;
  }

  const cancelLabel =
    reference.kind === "reply" ? t("chatThread.actions.cancelReply") : t("chatThread.actions.cancelForward");

  return (
    <div className="composer-reference-preview" data-testid="composer-reference-preview">
      <MessageReferencePreviewBlock
        kind={reference.kind}
        messageID="composer"
        reference={reference.message}
      />
      <button
        className="icon-button icon-button-ghost"
        type="button"
        onClick={onCancel}
        aria-label={cancelLabel}
        title={cancelLabel}
      >
        <IconClose />
      </button>
    </div>
  );
}

interface ComposerFileAttachmentPreviewProps {
  attachment: ComposerFileAttachmentState;
  onRemove: () => void;
}

export function ComposerFileAttachmentPreview({ attachment, onRemove }: ComposerFileAttachmentPreviewProps) {
  const { t } = useTranslation();
  const statusText =
    attachment.status === "uploading"
      ? attachment.progress_percent === null
        ? t("chatThread.files.uploading")
        : t("chatThread.files.uploadingProgress", { progress: String(attachment.progress_percent) })
      : attachment.status === "failed"
        ? t("chatThread.files.failed")
        : t("chatThread.files.ready");

  return (
    <div className="composer-file-attachment" data-testid="composer-file-attachment">
      <IconFile />
      <div className="composer-file-attachment-main">
        <span className="composer-file-attachment-name" title={attachment.filename}>
          {attachment.filename}
        </span>
        <span className="composer-file-attachment-meta">
          {formatFileAttachmentSize(attachment.size_bytes)} · {attachment.mime_type}
        </span>
        <span className="composer-file-attachment-status">{statusText}</span>
        {attachment.error_message !== undefined && attachment.error_message !== "" ? (
          <span className="composer-file-attachment-error">{attachment.error_message}</span>
        ) : null}
      </div>
      <button
        className="icon-button icon-button-ghost"
        type="button"
        onClick={onRemove}
        disabled={attachment.status === "uploading"}
        aria-label={t("chatThread.actions.removeFile")}
        title={t("chatThread.actions.removeFile")}
      >
        <IconClose />
      </button>
    </div>
  );
}

interface MessageFileAttachmentProps {
  mediaID: string;
  chatID: string;
  metadata?: MessageAttachmentMetadata;
  onHydrate: (mediaID: string) => Promise<void>;
  onDownload: (mediaID: string, filename: string) => Promise<void>;
}

function MessageFileAttachment({
  mediaID,
  chatID,
  metadata,
  onHydrate,
  onDownload,
}: MessageFileAttachmentProps) {
  const { t } = useTranslation();
  const filename = metadata?.filename.trim() || t("chatThread.files.attachment");
  const metaText =
    metadata === undefined
      ? t("chatThread.files.loading")
      : `${formatFileAttachmentSize(metadata.size_bytes)} · ${metadata.mime_type}`;
  const downloadURL = resolveMessageAttachmentDownloadURL(mediaID, chatID, resolveGatewayOrigin());

  return (
    <div className="message-file-attachment" data-testid={`file-attachment-${mediaID}`}>
      <IconFile />
      <div className="message-file-attachment-main">
        <span className="message-file-attachment-name" title={filename}>
          {filename}
        </span>
        <span className="message-file-attachment-meta">{metaText}</span>
      </div>
      {metadata === undefined ? (
        <button
          className="icon-button icon-button-ghost"
          type="button"
          onClick={() => void onHydrate(mediaID)}
          aria-label={t("chatThread.actions.loadFile")}
          title={t("chatThread.actions.loadFile")}
        >
          <IconRefresh />
        </button>
      ) : (
        <a
          className="icon-button icon-button-ghost message-file-download"
          href={downloadURL}
          download={filename}
          onClick={(event) => {
            event.preventDefault();
            void onDownload(mediaID, filename);
          }}
          aria-label={t("chatThread.actions.downloadFile", { filename })}
          title={t("chatThread.actions.downloadFile", { filename })}
        >
          <IconDownload />
        </a>
      )}
    </div>
  );
}

interface ForwardTargetPickerProps {
  state: ForwardTargetPickerState;
  actorUserID: string | null;
  onSelectTarget: (conversation: ConversationSummary) => void;
  onCancel: () => void;
}

function ForwardTargetPicker({ state, actorUserID, onSelectTarget, onCancel }: ForwardTargetPickerProps) {
  const { t } = useTranslation();
  return (
    <div className="notification-target-modal-scrim" role="presentation">
      <section
        className="notification-target-modal forward-target-picker"
        role="dialog"
        aria-modal="true"
        aria-labelledby="forward-target-picker-title"
        data-testid="forward-target-picker"
      >
        <h2 className="notification-target-modal-title" id="forward-target-picker-title">
          {t("chatThread.forward.title")}
        </h2>
        {state.loading ? (
          <p className="notification-target-modal-body">{t("chatThread.forward.loading")}</p>
        ) : state.errorMessage !== null ? (
          <p className="inline-error">{state.errorMessage}</p>
        ) : state.targets.length === 0 ? (
          <p className="notification-target-modal-body">{t("chatThread.forward.empty")}</p>
        ) : (
          <div className="forward-target-list">
            {state.targets.map((conversation) => {
              const targetLabel = resolveThreadTitle(conversation.chat_id, conversation, actorUserID);
              return (
                <button
                  key={conversation.chat_id}
                  className="forward-target-button"
                  type="button"
                  data-testid={`forward-target-${conversation.chat_id}`}
                  onClick={() => onSelectTarget(conversation)}
                >
                  <span className="forward-target-name">{targetLabel}</span>
                  <span className="forward-target-kind">{conversation.type}</span>
                </button>
              );
            })}
          </div>
        )}
        <div className="notification-target-modal-actions">
          <button className="secondary-button" type="button" onClick={onCancel}>
            {t("chatThread.actions.cancelForward")}
          </button>
        </div>
      </section>
    </div>
  );
}

interface VoiceRecordingElapsedTimerProps {
  mode: VoiceRecordingMode;
  startedAtMillis: number | null;
}

const VoiceRecordingElapsedTimer = memo(function VoiceRecordingElapsedTimer({
  mode,
  startedAtMillis,
}: VoiceRecordingElapsedTimerProps) {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    if (mode !== "recording" || startedAtMillis === null) {
      setElapsedSeconds(0);
      return;
    }

    const updateElapsed = () => {
      const nextElapsedSeconds = normalizeVoiceDurationSeconds(Math.ceil((Date.now() - startedAtMillis) / 1000));
      setElapsedSeconds((current) => (current === nextElapsedSeconds ? current : nextElapsedSeconds));
    };

    updateElapsed();
    const intervalID = window.setInterval(updateElapsed, VOICE_RECORDING_TICK_MS);
    return () => {
      window.clearInterval(intervalID);
    };
  }, [mode, startedAtMillis]);

  return <>{formatVoiceDuration(elapsedSeconds)}</>;
});

function ThreadTimelineComponent({
  loading,
  chatID,
  messages,
  actorUserID,
  sourceSecretChat,
  participantReceipts,
  editingMessageID,
  editingBody,
  highlightedMessageID,
  defaultLatestScrollRevision,
  defaultLatestMessageID,
  voicePlaybackSources,
  photoAttachmentSources,
  fileAttachmentMetadata,
  voicePlayerNativeFallback,
  pendingTimelinePhotos,
  pendingTimelineVoices,
  threadPaneRef,
  messageBodyRefs,
  initialActiveMessageActionsID = null,
  onSetEditingBody,
  onSaveEdit,
  onCancelEdit,
  onStartEdit,
  onReplyToMessage,
  onForwardMessage,
  onDeleteMessage,
  onOpenReplyReference,
  onHydrateVoicePlaybackSource,
  onHydratePhotoAttachmentSource,
  onHydrateFileAttachmentMetadata,
  onDownloadFileAttachment,
  onSetExpandedPhotoSource,
  onMarkVoicePlayerNativeFallback,
  onRetryPendingPhoto,
  onDeletePendingPhoto,
  onRetryPendingVoice,
  onDeletePendingVoice,
}: ThreadTimelineProps) {
  const { t } = useTranslation();
  const [activeMessageActionsID, setActiveMessageActionsID] = useState<string | null>(initialActiveMessageActionsID);
  const longPressTimerRef = useRef<number | null>(null);
  const timelineRowElementRefs = useRef<Record<string, HTMLLIElement | null>>({});
  const timelineItems = useMemo(
    () =>
      buildVirtualTimelineItems({
        messages,
        pendingPhotos: pendingTimelinePhotos,
        pendingVoices: pendingTimelineVoices,
      }),
    [messages, pendingTimelinePhotos, pendingTimelineVoices],
  );
  const receiptMeasureKey = useMemo(
    () =>
      participantReceipts
        .map((receipt) => `${receipt.user_id}:${receipt.delivered_seq}:${receipt.read_seq}`)
        .join("|"),
    [participantReceipts],
  );
  const referenceMeasureKey = useMemo(
    () =>
      messages
        .map((message) =>
          [
            message.message_id,
            message.reply_to?.message_id ?? "",
            message.reply_to?.body_preview ?? "",
            message.forwarded_from?.message_id ?? "",
            message.forwarded_from?.body_preview ?? "",
          ].join(":"),
        )
        .join("|"),
    [messages],
  );
  const appliedDefaultLatestScrollRevisionRef = useRef(0);

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current !== null) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const revealMessageActions = useCallback(
    (messageID: string) => {
      clearLongPressTimer();
      setActiveMessageActionsID(messageID);
    },
    [clearLongPressTimer],
  );

  const shouldIgnoreActionRevealTarget = useCallback((target: EventTarget | null): boolean => {
    if (!(target instanceof Element)) {
      return true;
    }
    return (
      target.closest(
        'button, a, input, textarea, select, summary, audio, video, [role="button"], [contenteditable="true"]',
      ) !== null
    );
  }, []);

  const cancelActiveMessageActions = useCallback(() => {
    clearLongPressTimer();
    setActiveMessageActionsID(null);
  }, [clearLongPressTimer]);

  useEffect(() => {
    cancelActiveMessageActions();
  }, [cancelActiveMessageActions, chatID, editingMessageID]);

  useEffect(() => {
    return () => {
      clearLongPressTimer();
    };
  }, [clearLongPressTimer]);

  useEffect(() => {
    if (activeMessageActionsID === null || typeof window === "undefined") {
      return;
    }
    const onSelectionKeyDown = (event: KeyboardEvent) => {
      if (!isMessageSelectionDismissKey(event.key)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      cancelActiveMessageActions();
    };
    window.addEventListener("keydown", onSelectionKeyDown, true);
    return () => {
      window.removeEventListener("keydown", onSelectionKeyDown, true);
    };
  }, [activeMessageActionsID, cancelActiveMessageActions]);

  useEffect(() => {
    if (activeMessageActionsID === null) {
      return;
    }
    const activeMessage = messages.find((message) => message.message_id === activeMessageActionsID);
    if (activeMessage === undefined) {
      setActiveMessageActionsID(null);
      return;
    }
    const own = isOwnMessage(activeMessage, actorUserID);
    const deleted = typeof activeMessage.deleted_at === "string" && activeMessage.deleted_at !== "";
    const editing = editingMessageID === activeMessage.message_id;
    const actions = buildSelectedMessageActionDescriptors({ own, deleted, editing, sourceSecretChat });
    if (!shouldRenderMessageActions({ activeMessageActionsID, messageID: activeMessage.message_id, actions })) {
      setActiveMessageActionsID(null);
    }
  }, [activeMessageActionsID, actorUserID, editingMessageID, messages, sourceSecretChat]);

  const virtualizer = useVirtualizer({
    count: timelineItems.length,
    getScrollElement: () => threadPaneRef.current,
    getItemKey: (index) => timelineItems[index]?.key ?? `missing:${index}`,
    estimateSize: (index) => estimateTimelineItemHeight(timelineItems[index], editingMessageID, activeMessageActionsID),
    initialRect: { width: 0, height: 720 },
    overscan: 8,
  });

  useEffect(() => {
    virtualizer.measure();
  }, [receiptMeasureKey, referenceMeasureKey, virtualizer]);

  const measureActiveMessageRow = useCallback(() => {
    const activeRow = activeMessageActionsID === null ? null : timelineRowElementRefs.current[activeMessageActionsID];
    if (activeRow !== null && activeRow !== undefined) {
      virtualizer.measureElement(activeRow);
      return;
    }
    virtualizer.measure();
  }, [activeMessageActionsID, virtualizer]);

  useEffect(() => {
    measureActiveMessageRow();
    const frameID = window.requestAnimationFrame(() => {
      measureActiveMessageRow();
    });
    const settleTimerID = window.setTimeout(() => {
      measureActiveMessageRow();
    }, 80);
    return () => {
      window.cancelAnimationFrame(frameID);
      window.clearTimeout(settleTimerID);
    };
  }, [measureActiveMessageRow]);

  useEffect(() => {
    if (highlightedMessageID === null) {
      return;
    }
    const highlightedIndex = resolveTimelineMessageIndex(timelineItems, highlightedMessageID);
    if (highlightedIndex === null) {
      return;
    }
    virtualizer.scrollToIndex(highlightedIndex, { align: "center" });
  }, [highlightedMessageID, timelineItems, virtualizer]);

  useEffect(() => {
    if (
      defaultLatestScrollRevision <= 0 ||
      appliedDefaultLatestScrollRevisionRef.current === defaultLatestScrollRevision ||
      defaultLatestMessageID === null ||
      highlightedMessageID !== null ||
      timelineItems.length === 0
    ) {
      return;
    }
    const latestMessageIndex = resolveTimelineMessageIndex(timelineItems, defaultLatestMessageID);
    if (latestMessageIndex === null) {
      return;
    }
    appliedDefaultLatestScrollRevisionRef.current = defaultLatestScrollRevision;
    let settleFrameID: number | null = null;
    const frameID = window.requestAnimationFrame(() => {
      virtualizer.scrollToIndex(latestMessageIndex, { align: "end" });
      settleFrameID = window.requestAnimationFrame(() => {
        messageBodyRefs.current[defaultLatestMessageID]?.scrollIntoView({
          block: "end",
          inline: "nearest",
          behavior: "auto",
        });
      });
    });
    return () => {
      window.cancelAnimationFrame(frameID);
      if (settleFrameID !== null) {
        window.cancelAnimationFrame(settleFrameID);
      }
    };
  }, [defaultLatestMessageID, defaultLatestScrollRevision, highlightedMessageID, messageBodyRefs, timelineItems, virtualizer]);

  const onTimelinePointerDownCapture = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (activeMessageActionsID === null || !(event.target instanceof Element)) {
        return;
      }
      if (shouldDismissMessageSelectionFromPointerTarget(event.target)) {
        event.preventDefault();
        event.stopPropagation();
        cancelActiveMessageActions();
      }
    },
    [activeMessageActionsID, cancelActiveMessageActions],
  );

  const renderMessageArticle = (message: ChatMessage) => {
    const own = isOwnMessage(message, actorUserID);
    const deleted = typeof message.deleted_at === "string" && message.deleted_at !== "";
    const editing = editingMessageID === message.message_id;
    const highlighted = highlightedMessageID === message.message_id;
    const messageActionDescriptors = buildSelectedMessageActionDescriptors({ own, deleted, editing, sourceSecretChat });
    const selectionState = resolveMessageSelectionState({
      activeMessageActionsID,
      messageID: message.message_id,
      actions: messageActionDescriptors,
    });
    const canRevealActions = messageActionDescriptors.length > 0;
    const actionsVisible = shouldRenderMessageActions({
      activeMessageActionsID,
      messageID: message.message_id,
      actions: messageActionDescriptors,
    });
    const hasAttachments = (message.attachments?.length ?? 0) > 0;
    const displayBody = resolveMessageBodyForDisplay(message);
    const hasBody = displayBody.trim() !== "";
    const replyReferenceTarget =
      message.reply_to === undefined
        ? null
        : resolveReplyReferenceContextTarget(message.reply_to, chatID, messages);
    const receiptStatus = resolveOwnMessageReceiptStatus({
      message,
      actorUserID,
      participantReceipts,
    });
    const receiptStatusLabel = receiptStatus === null ? "" : t(`chatThread.receipts.${receiptStatus}`);
    const messageBubbleClassName = [
      buildMessageBubbleClassName({ own, deleted, highlighted }),
      canRevealActions ? "message-bubble-action-revealable" : "",
      actionsVisible ? "message-bubble-actions-active" : "",
      selectionState.selected ? "message-bubble-selected" : "",
      selectionState.dimmed ? "message-bubble-selection-dimmed" : "",
    ]
      .filter(Boolean)
      .join(" ");

    const onMessagePointerDown = (event: ReactPointerEvent<HTMLElement>) => {
      if (!canRevealActions || shouldIgnoreActionRevealTarget(event.target)) {
        return;
      }
      if (event.pointerType === "mouse" && event.button !== 0) {
        return;
      }
      clearLongPressTimer();
      longPressTimerRef.current = window.setTimeout(() => {
        longPressTimerRef.current = null;
        setActiveMessageActionsID(message.message_id);
      }, MESSAGE_ACTION_LONG_PRESS_MS);
    };

    const onMessageContextMenu = (event: ReactMouseEvent<HTMLElement>) => {
      if (!canRevealActions || shouldIgnoreActionRevealTarget(event.target)) {
        return;
      }
      event.preventDefault();
      revealMessageActions(message.message_id);
    };

    const onMessageKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
      if (!canRevealActions) {
        return;
      }
      const shouldReveal =
        event.key === "Enter" ||
        event.key === " " ||
        event.key === "ContextMenu" ||
        (event.shiftKey && event.key === "F10");
      if (!shouldReveal) {
        return;
      }
      event.preventDefault();
      revealMessageActions(message.message_id);
    };

    const onStartMessageEdit = () => {
      cancelActiveMessageActions();
      onStartEdit(message);
    };

    const onStartMessageReply = () => {
      cancelActiveMessageActions();
      onReplyToMessage(message);
    };

    const onStartMessageForward = () => {
      cancelActiveMessageActions();
      onForwardMessage(message);
    };

    const onDeleteRevealedMessage = async () => {
      await onDeleteMessage(message.message_id);
      setActiveMessageActionsID((current) => (current === message.message_id ? null : current));
    };

    const onRunSelectedMessageAction = (actionID: SelectedMessageActionID) => {
      switch (actionID) {
        case "reply":
          onStartMessageReply();
          return;
        case "forward":
          onStartMessageForward();
          return;
        case "edit":
          onStartMessageEdit();
          return;
        case "delete":
          void onDeleteRevealedMessage();
          return;
        default:
          return;
      }
    };

    return (
      <>
        <article
          className={messageBubbleClassName}
          data-testid={`message-bubble-${message.message_id}`}
          data-highlighted={highlighted ? "true" : "false"}
          data-selection-active={selectionState.selectionActive ? "true" : "false"}
          data-selected-message={selectionState.selected ? "true" : "false"}
          tabIndex={canRevealActions ? 0 : undefined}
          aria-label={canRevealActions ? t("chatThread.actions.revealMessageActions") : undefined}
          aria-keyshortcuts={canRevealActions ? "Enter Space Shift+F10" : undefined}
          aria-expanded={canRevealActions ? actionsVisible : undefined}
          onPointerDown={canRevealActions ? onMessagePointerDown : undefined}
          onPointerUp={canRevealActions ? clearLongPressTimer : undefined}
          onPointerCancel={canRevealActions ? clearLongPressTimer : undefined}
          onPointerLeave={canRevealActions ? clearLongPressTimer : undefined}
          onContextMenu={canRevealActions ? onMessageContextMenu : undefined}
          onKeyDown={canRevealActions ? onMessageKeyDown : undefined}
        >
        <header className="message-meta">
          <span className="message-meta-author">{resolveMessageSenderLabel(message.sender_user_id)}</span>
          <span className="message-meta-time">{formatMessageTimestamp(message.updated_at || message.created_at)}</span>
          <MessageReceiptStatusIndicator
            messageID={message.message_id}
            status={receiptStatus}
            label={receiptStatusLabel}
          />
        </header>

        {!deleted ? (
          <>
            <MessageReferencePreviewBlock
              kind="reply"
              messageID={message.message_id}
              reference={message.reply_to}
              clickable={replyReferenceTarget !== null}
              onOpen={() => {
                if (message.reply_to !== undefined) {
                  onOpenReplyReference(message.reply_to);
                }
              }}
            />
            <MessageReferencePreviewBlock
              kind="forward"
              messageID={message.message_id}
              reference={message.forwarded_from}
            />
          </>
        ) : null}

        {editing ? (
          <form
            className="stack-form"
            onSubmit={(event) => {
              event.preventDefault();
              void onSaveEdit(message.message_id);
            }}
          >
            <textarea value={editingBody} onChange={(event) => onSetEditingBody(event.target.value)} rows={3} />
            <div className="message-actions">
              <button className="icon-button icon-button-primary" type="submit" aria-label={t("chatThread.actions.saveEdit")} title={t("chatThread.actions.saveEdit")}>
                <IconSend />
              </button>
              <button className="icon-button icon-button-ghost" type="button" onClick={onCancelEdit} aria-label={t("chatThread.actions.cancelEdit")} title={t("chatThread.actions.cancelEdit")}>
                <IconBack />
              </button>
            </div>
          </form>
        ) : (
          <>
            {deleted ? <p className="message-state">{t("chatThread.timeline.deleted")}</p> : null}
            {deleted ? (
              <p
                ref={(node) => {
                  messageBodyRefs.current[message.message_id] = node;
                }}
                className="message-body"
                data-testid={`message-${message.message_id}`}
              >
                {t("chatThread.timeline.messageDeleted")}
              </p>
            ) : (
              <>
                {hasAttachments ? (
                  <ul
                    ref={(node) => {
                      messageBodyRefs.current[message.message_id] = node;
                    }}
                    className="message-attachment-list"
                    data-testid={`attachments-${message.message_id}`}
                  >
                    {message.attachments?.map((attachment) => {
                      const mediaID = attachment.media_id.trim();
                      const mediaStateKey = resolveConversationMediaStateKey(chatID, mediaID);
                      const voiceSource = voicePlaybackSources[mediaStateKey];
                      const photoSource = photoAttachmentSources[mediaStateKey];
                      const fileMetadata = fileAttachmentMetadata[mediaStateKey];
                      const useNativeVoicePlayer = voicePlayerNativeFallback[mediaStateKey] === true;
                      return (
                        <li
                          key={`${message.message_id}-${mediaID}`}
                          className={
                            isVoiceAttachmentKind(attachment.kind) || isFileAttachmentKind(attachment.kind)
                              ? "message-attachment-item-wide"
                              : undefined
                          }
                        >
                          {isVoiceAttachmentKind(attachment.kind) ? (
                            voiceSource !== undefined ? (
                              <div className="voice-attachment-audio" data-testid={`voice-attachment-${mediaID}`}>
                                {useNativeVoicePlayer ? (
                                  <audio className="voice-attachment-audio-native" src={voiceSource} controls preload="metadata" />
                                ) : (
                                  <AudioPlayerAdapter
                                    src={voiceSource}
                                    controls
                                    width="100%"
                                    showDownloadButton={false}
                                    className="voice-attachment-audio-player"
                                    onError={() => {
                                      onMarkVoicePlayerNativeFallback(mediaStateKey);
                                    }}
                                  />
                                )}
                              </div>
                            ) : (
                              <button
                                className="voice-attachment-play"
                                type="button"
                                onClick={() => void onHydrateVoicePlaybackSource(mediaID)}
                                data-testid={`voice-attachment-pending-${mediaID}`}
                              >
                                <IconVoiceWave />
                                <span>{t("chatThread.timeline.loadVoice")}</span>
                              </button>
                            )
                          ) : isPhotoAttachmentKind(attachment.kind) ? (
                            photoSource !== undefined ? (
                              <button
                                className="message-photo-attachment-button"
                                type="button"
                                onClick={() => void onSetExpandedPhotoSource(mediaID)}
                                aria-label={t("chatThread.timeline.openFullSizePhoto")}
                                data-testid={`photo-attachment-open-${mediaID}`}
                              >
                                <span className="message-photo-attachment">
                                  <img src={photoSource} alt={t("chatThread.timeline.photoAttachmentAlt")} loading="lazy" data-testid={`photo-attachment-${mediaID}`} />
                                </span>
                              </button>
                            ) : (
                              <button
                                className="voice-attachment-play"
                                type="button"
                                onClick={() => void onHydratePhotoAttachmentSource(mediaID)}
                                data-testid={`photo-attachment-pending-${mediaID}`}
                              >
                                <IconPhoto />
                                <span>{t("chatThread.timeline.loadPhoto")}</span>
                              </button>
                            )
                          ) : isFileAttachmentKind(attachment.kind) ? (
                            <MessageFileAttachment
                              mediaID={mediaID}
                              chatID={chatID}
                              metadata={fileMetadata}
                              onHydrate={onHydrateFileAttachmentMetadata}
                              onDownload={onDownloadFileAttachment}
                            />
                          ) : (
                            <span className="message-attachment-pill">
                              {attachment.kind} · {mediaID}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
                {hasBody ? (
                  <p
                    ref={(node) => {
                      messageBodyRefs.current[message.message_id] = node;
                    }}
                    className="message-body"
                    data-testid={`message-${message.message_id}`}
                  >
                    {displayBody}
                  </p>
                ) : null}
              </>
            )}
          </>
        )}

        </article>

        {actionsVisible ? (
          <div
            className="message-actions message-selection-menu"
            role="toolbar"
            aria-label={t("chatThread.selection.actionMenu")}
            data-testid={`message-actions-${message.message_id}`}
          >
            {messageActionDescriptors.map((action) => (
              <button
                key={action.id}
                className={action.buttonClassName}
                type="button"
                onClick={() => onRunSelectedMessageAction(action.id)}
                aria-label={t(action.labelKey)}
                title={t(action.labelKey)}
              >
                {renderSelectedMessageActionIcon(action.id)}
              </button>
            ))}
          </div>
        ) : null}
      </>
    );
  };

  const renderPendingPhotoArticle = (pendingPhoto: PendingPhotoMessage) => {
    const failed = pendingPhoto.status === "failed";
    return (
      <article
        className={`message-bubble message-bubble-own message-bubble-pending${failed ? " message-bubble-failed" : ""}`}
        data-testid={`pending-photo-${pendingPhoto.local_id}`}
      >
        <header className="message-meta">
          <span className="message-meta-author">{resolveMessageSenderLabel(actorUserID ?? "you")}</span>
          <span className="message-meta-time">{formatMessageTimestamp(pendingPhoto.created_at)}</span>
        </header>

        <div className="pending-photo-preview">
          <img src={pendingPhoto.preview_url} alt={pendingPhoto.file_name} loading="lazy" />
        </div>

        <div className="pending-photo-status" role="status" aria-live="polite">
          <p className="message-state">
            {failed ? t("chatThread.timeline.photoFailed") : t("chatThread.timeline.photoSending")}
          </p>
          {pendingPhoto.progress_percent !== null ? (
            <span className="pending-photo-progress-label">{pendingPhoto.progress_percent}%</span>
          ) : (
            <span className="pending-photo-spinner" aria-hidden="true" />
          )}
        </div>

        {pendingPhoto.error_message !== undefined && pendingPhoto.error_message !== "" ? (
          <p className="pending-photo-error">{pendingPhoto.error_message}</p>
        ) : null}

        {failed ? (
          <div className="message-actions">
            <button
              className="icon-button icon-button-ghost"
              type="button"
              onClick={() => onRetryPendingPhoto(pendingPhoto.local_id)}
              aria-label={t("chatThread.actions.retryPhoto")}
              title={t("chatThread.actions.retryPhoto")}
            >
              <IconRefresh />
            </button>
            <button
              className="icon-button icon-button-danger"
              type="button"
              onClick={() => onDeletePendingPhoto(pendingPhoto.local_id)}
              aria-label={t("chatThread.actions.deletePendingPhoto")}
              title={t("chatThread.actions.deletePendingPhoto")}
            >
              <IconTrash />
            </button>
          </div>
        ) : null}
      </article>
    );
  };

  const renderPendingVoiceArticle = (pendingVoice: PendingVoiceMessage) => {
    const failed = pendingVoice.status === "failed";
    return (
      <article
        className={`message-bubble message-bubble-own message-bubble-pending${failed ? " message-bubble-failed" : ""}`}
        data-testid={`pending-voice-${pendingVoice.local_id}`}
      >
        <header className="message-meta">
          <span className="message-meta-author">{resolveMessageSenderLabel(actorUserID ?? "you")}</span>
          <span className="message-meta-time">{formatMessageTimestamp(pendingVoice.created_at)}</span>
        </header>

        <div className="pending-voice-preview">
          <audio src={pendingVoice.preview_url} controls preload="metadata" />
        </div>

        <div className="pending-photo-status" role="status" aria-live="polite">
          <p className="message-state">
            {failed ? t("chatThread.timeline.voiceFailed") : t("chatThread.timeline.voiceSending")}
          </p>
          {pendingVoice.progress_percent !== null ? (
            <span className="pending-photo-progress-label">{pendingVoice.progress_percent}%</span>
          ) : (
            <span className="pending-photo-spinner" aria-hidden="true" />
          )}
        </div>

        <p className="message-state">
          {pendingVoice.capped ? t("chatThread.timeline.voiceMaxReached") : t("chatThread.timeline.voiceDuration")} {" "}
          {formatVoiceDuration(pendingVoice.duration_seconds)}
        </p>

        {pendingVoice.error_message !== undefined && pendingVoice.error_message !== "" ? (
          <p className="pending-photo-error">{pendingVoice.error_message}</p>
        ) : null}

        {failed ? (
          <div className="message-actions">
            <button
              className="icon-button icon-button-ghost"
              type="button"
              onClick={() => onRetryPendingVoice(pendingVoice.local_id)}
              aria-label={t("chatThread.actions.retryVoice")}
              title={t("chatThread.actions.retryVoice")}
            >
              <IconRefresh />
            </button>
            <button
              className="icon-button icon-button-danger"
              type="button"
              onClick={() => onDeletePendingVoice(pendingVoice.local_id)}
              aria-label={t("chatThread.actions.deletePendingVoice")}
              title={t("chatThread.actions.deletePendingVoice")}
            >
              <IconTrash />
            </button>
          </div>
        ) : null}
      </article>
    );
  };

  const renderTimelineArticle = (item: VirtualTimelineItem) => {
    switch (item.type) {
      case "message":
        return renderMessageArticle(item.message);
      case "pending-photo":
        return renderPendingPhotoArticle(item.pendingPhoto);
      case "pending-voice":
        return renderPendingVoiceArticle(item.pendingVoice);
      default:
        return null;
    }
  };

  const isOwnTimelineItem = (item: VirtualTimelineItem): boolean => {
    if (item.type !== "message") {
      return true;
    }
    return isOwnMessage(item.message, actorUserID);
  };
  const selectionActive = activeMessageActionsID !== null;

  return (
    <section
      ref={threadPaneRef}
      className={`${CHAT_THREAD_SHELL_CLASSES.timelinePane}${selectionActive ? " message-selection-active" : ""}`}
      data-testid="thread-pane"
      data-selection-active={selectionActive ? "true" : "false"}
      onPointerDownCapture={onTimelinePointerDownCapture}
    >
      {selectionActive ? (
        <div className="message-selection-backdrop" data-testid="message-selection-backdrop" aria-hidden="true" />
      ) : null}
      {loading ? <p className="muted-line">{t("chatThread.timeline.loading")}</p> : null}
      {!loading && messages.length === 0 ? <p className="muted-line">{t("chatThread.timeline.empty")}</p> : null}

      <ul
        className={`${CHAT_THREAD_SHELL_CLASSES.messageList} message-list-virtualized`}
        data-testid="virtualized-message-list"
        data-virtualized="true"
        style={{ height: `${virtualizer.getTotalSize()}px` }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const item = timelineItems[virtualRow.index];
          if (item === undefined) {
            return null;
          }
          const own = isOwnTimelineItem(item);
          const rowSelectionKind =
            selectionActive && item.type === "message" && item.message.message_id === activeMessageActionsID
              ? "selected"
              : selectionActive
                ? "dimmed"
                : null;
          const rowClassName = [
            "message-row",
            "message-row-virtualized",
            own ? "message-row-own" : "",
            rowSelectionKind === "selected" ? "message-row-selected" : "",
            rowSelectionKind === "dimmed" ? "message-row-selection-dimmed" : "",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <li
              key={virtualRow.key}
              ref={(node) => {
                if (node !== null) {
                  virtualizer.measureElement(node);
                }
                if (item.type === "message") {
                  timelineRowElementRefs.current[item.message.message_id] = node;
                }
              }}
              data-index={virtualRow.index}
              data-testid={item.type === "message" ? `message-row-${item.message.message_id}` : undefined}
              data-message-selection-row={rowSelectionKind ?? undefined}
              className={rowClassName}
              style={{ transform: `translateY(${virtualRow.start}px)` }}
            >
              {renderTimelineArticle(item)}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function areThreadTimelinePropsEqual(prev: ThreadTimelineProps, next: ThreadTimelineProps): boolean {
  return (
    prev.loading === next.loading &&
    prev.chatID === next.chatID &&
    prev.messages === next.messages &&
    prev.actorUserID === next.actorUserID &&
    prev.sourceSecretChat === next.sourceSecretChat &&
    prev.participantReceipts === next.participantReceipts &&
    prev.editingMessageID === next.editingMessageID &&
    prev.editingBody === next.editingBody &&
    prev.highlightedMessageID === next.highlightedMessageID &&
    prev.defaultLatestScrollRevision === next.defaultLatestScrollRevision &&
    prev.defaultLatestMessageID === next.defaultLatestMessageID &&
    prev.voicePlaybackSources === next.voicePlaybackSources &&
    prev.photoAttachmentSources === next.photoAttachmentSources &&
    prev.fileAttachmentMetadata === next.fileAttachmentMetadata &&
    prev.voicePlayerNativeFallback === next.voicePlayerNativeFallback &&
    prev.pendingTimelinePhotos === next.pendingTimelinePhotos &&
    prev.pendingTimelineVoices === next.pendingTimelineVoices &&
    prev.threadPaneRef === next.threadPaneRef &&
    prev.messageBodyRefs === next.messageBodyRefs
  );
}

export const ThreadTimeline = memo(ThreadTimelineComponent, areThreadTimelinePropsEqual);

export function ChatThreadPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const chatID = normalizeChatID(params.chatID);
  const actorUserID = useMemo(() => readActorUserIDFromAccessToken(authStore.getAccessToken()), []);
  const contextTarget = useMemo(() => parseContextNavigationTarget(location.search), [location.search]);
  const notificationNavigation = useMemo(() => isNotificationNavigationSearch(location.search), [location.search]);
  const pendingForwardComposerReference = useMemo(
    () => readForwardComposerReferenceFromLocationState(location.state),
    [location.state],
  );

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [timelineError, setTimelineError] = useState<string | null>(null);
  const [realtimeState, setRealtimeState] = useState<RealtimeState>("closed");
  const [realtimeReadyChatID, setRealtimeReadyChatID] = useState<string | null>(null);
  const [hasChatAccess, setHasChatAccess] = useState(false);
  const [activeConversation, setActiveConversation] = useState<ConversationSummary | null>(null);
  const [receiptParticipantsByUserID, setReceiptParticipantsByUserID] = useState<ReceiptParticipantMap>({});
  const [composerBody, setComposerBody] = useState("");
  const [composerReference, setComposerReference] = useState<ComposerMessageReferenceState | null>(null);
  const [forwardTargetPicker, setForwardTargetPicker] = useState<ForwardTargetPickerState | null>(null);
  const [secretRatchetSessionID, setSecretRatchetSessionID] = useState("");
  const [secretRatchetCounter, setSecretRatchetCounter] = useState("1");
  const [secretPreviousChainLength, setSecretPreviousChainLength] = useState("0");
  const [secretComposerError, setSecretComposerError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [pendingPhotos, setPendingPhotos] = useState<PendingPhotoMessage[]>([]);
  const [pendingVoices, setPendingVoices] = useState<PendingVoiceMessage[]>([]);
  const [photoAttachmentSources, setPhotoAttachmentSources] = useState<Record<string, string>>({});
  const [voicePlaybackSources, setVoicePlaybackSources] = useState<Record<string, string>>({});
  const [fileAttachmentMetadata, setFileAttachmentMetadata] = useState<Record<string, MessageAttachmentMetadata>>({});
  const [composerFileAttachment, setComposerFileAttachment] = useState<ComposerFileAttachmentState | null>(null);
  const [voicePlayerNativeFallback, setVoicePlayerNativeFallback] = useState<Record<string, true>>({});
  const [mediaMenuOpen, setMediaMenuOpen] = useState(false);
  const [voiceComposerOpen, setVoiceComposerOpen] = useState(false);
  const [voiceRecordingUI, setVoiceRecordingUI] = useState<VoiceRecordingUIState>(createInitialVoiceRecordingUIState);
  const [voiceRecordingStartedAt, setVoiceRecordingStartedAt] = useState<number | null>(null);
  const [voicePreview, setVoicePreview] = useState<VoicePreviewState | null>(null);
  const [pendingActionError, setPendingActionError] = useState<string | null>(null);
  const [editingMessageID, setEditingMessageID] = useState<string | null>(null);
  const [editingBody, setEditingBody] = useState("");
  const [highlightedMessageID, setHighlightedMessageID] = useState<string | null>(null);
  const [defaultLatestScrollRevision, setDefaultLatestScrollRevision] = useState(0);
  const [defaultLatestMessageID, setDefaultLatestMessageID] = useState<string | null>(null);
  const [expandedPhoto, setExpandedPhoto] = useState<ExpandedPhotoState | null>(null);
  const [notificationTargetModalVisible, setNotificationTargetModalVisible] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [manageMembersOpen, setManageMembersOpen] = useState(false);
  const [manageMembersUI, setManageMembersUI] = useState<ManageMembersUIState>(createInitialManageMembersUIState);
  const [directDeleteUI, setDirectDeleteUI] = useState<DirectDeleteUIState>(createInitialDirectDeleteUIState);
  const [lifecycleActionUI, setLifecycleActionUI] = useState<LifecycleActionUIState>(createInitialLifecycleActionUIState);
  const [metadataEditorOpen, setMetadataEditorOpen] = useState(false);
  const [metadataEditorUI, setMetadataEditorUI] = useState<MetadataEditorUIState>(createInitialMetadataEditorUIState);
  const [metadataEditorAvatarPatchMode, setMetadataEditorAvatarPatchMode] = useState<MetadataEditorAvatarPatchMode>("keep");
  const [metadataEditorAvatarFile, setMetadataEditorAvatarFile] = useState<File | null>(null);
  const [displayNameRevision, setDisplayNameRevision] = useState(0);
  const [threadHeaderAvatarSource, setThreadHeaderAvatarSourceState] = useState("");
  const threadPaneRef = useRef<HTMLElement | null>(null);
  const followThreadBottomRef = useRef(false);
  const messageBodyRefs = useRef<Record<string, HTMLElement | null>>({});
  const composerInputRef = useRef<HTMLTextAreaElement | null>(null);
  const composerBodyRef = useRef("");
  const filePickerRef = useRef<HTMLInputElement | null>(null);
  const fileAttachmentPickerRef = useRef<HTMLInputElement | null>(null);
  const pendingPhotoFilesRef = useRef<Record<string, File>>({});
  const pendingPhotosRef = useRef<PendingPhotoMessage[]>([]);
  const photoAttachmentSourcesRef = useRef<Record<string, string>>({});
  const photoSourceHydrationRef = useRef<Set<string>>(new Set());
  const expandedPhotoSourceRef = useRef<string | null>(null);
  const expandedPhotoRequestRef = useRef(0);
  const pendingVoiceUploadsRef = useRef<Record<string, PendingVoiceUploadInput>>({});
  const pendingVoicesRef = useRef<PendingVoiceMessage[]>([]);
  const voicePlaybackSourcesRef = useRef<Record<string, string>>({});
  const voiceSourceHydrationRef = useRef<Set<string>>(new Set());
  const fileAttachmentMetadataRef = useRef<Record<string, MessageAttachmentMetadata>>({});
  const fileMetadataHydrationRef = useRef<Set<string>>(new Set());
  const attachmentCacheRef = useRef<Record<string, ChatAttachment[]>>({});
  const attachmentCacheLoadedRef = useRef(false);
  const attachmentCacheDirtyRef = useRef(false);
  const voiceCancelButtonRef = useRef<HTMLButtonElement | null>(null);
  const voiceRecordingSessionRef = useRef<ActiveVoiceRecordingSession | null>(null);
  const voiceDurationTimeoutRef = useRef<number | null>(null);
  const voiceHoldActiveRef = useRef(false);
  const voiceQueuedStopRef = useRef<"none" | "release" | "cancel">("none");
  const secretRealtimeHydrationRef = useRef<Set<number>>(new Set());
  const lastMarkedReadSequenceRef = useRef(0);
  const threadHeaderAvatarSourceRef = useRef("");
  const participantReceipts = useMemo(
    () => Object.values(receiptParticipantsByUserID),
    [receiptParticipantsByUserID],
  );
  const manageMembersConversationType = useMemo(
    () => resolveManageMembersConversationType(activeConversation),
    [activeConversation],
  );
  const manageMembersCanAdd = useMemo(
    () => canManageMembersAddParticipants(activeConversation, actorUserID),
    [activeConversation, actorUserID],
  );
  const manageMembersEntries = useMemo(
    () => resolveManageMembersEntries(activeConversation, actorUserID),
    [activeConversation, actorUserID],
  );
  const manageMembersBusy = manageMembersUI.submitting || manageMembersUI.removingUserID !== null;
  const threadLifecycleReadOnly = useMemo(
    () => isConversationLifecycleReadOnly(activeConversation),
    [activeConversation],
  );
  const threadWriteEnabled = hasChatAccess && !threadLifecycleReadOnly;
  const lifecycleConversationType = useMemo(
    () => resolveLifecycleConversationType(activeConversation),
    [activeConversation],
  );
  const showLifecycleAction = useMemo(
    () => shouldShowLifecycleControls(hasChatAccess, activeConversation, actorUserID),
    [activeConversation, actorUserID, hasChatAccess],
  );
  const showDirectDeleteAction = useMemo(
    () => canDeleteDirectConversation(hasChatAccess, activeConversation),
    [activeConversation, hasChatAccess],
  );
  const lifecycleConfirmationPhrase = useMemo(
    () => resolveLifecycleConfirmationPhrase(activeConversation),
    [activeConversation],
  );
  const lifecycleSubmitEnabled = isLifecycleConfirmationSubmitEnabled(
    lifecycleActionUI,
    lifecycleConfirmationPhrase,
  );
  const showManageMembersAction = useMemo(
    () => threadWriteEnabled && shouldShowManageMembersAction(hasChatAccess, activeConversation),
    [activeConversation, hasChatAccess, threadWriteEnabled],
  );
  const metadataEditorConversationType = useMemo(
    () => resolveMetadataEditorConversationType(activeConversation, actorUserID),
    [activeConversation, actorUserID],
  );
  const showMetadataEditorAction = threadWriteEnabled && metadataEditorConversationType !== null;
  const metadataEditorBusy = metadataEditorUI.submitting;
  const metadataNameValidationError = validateMetadataEditorName(metadataEditorUI.name);
  const metadataDescriptionValidationError = validateMetadataEditorDescription(metadataEditorUI.description);
  const metadataValidationMessage = metadataNameValidationError ?? metadataDescriptionValidationError;
  const resolveThreadCopy = useCallback(
    (copy: string): string => {
      const normalized = copy.trim();
      if (!normalized.startsWith("chatThread.")) {
        return copy;
      }
      if (normalized === "chatThread.metadataEditor.validation.nameMax") {
        return t(normalized, { max: METADATA_EDITOR_NAME_MAX_LENGTH });
      }
      if (normalized === "chatThread.metadataEditor.validation.descriptionMax") {
        return t(normalized, { max: METADATA_EDITOR_DESCRIPTION_MAX_LENGTH });
      }
      return t(normalized);
    },
    [t],
  );
  const metadataValidationCopy =
    metadataValidationMessage === null
      ? t("chatThread.metadataEditor.validation.ready")
      : resolveThreadCopy(metadataValidationMessage);
  const metadataEditorErrorCopy =
    metadataEditorUI.errorMessage === null ? null : resolveThreadCopy(metadataEditorUI.errorMessage);
  const metadataEditorSuccessCopy =
    metadataEditorUI.successMessage === null ? null : resolveThreadCopy(metadataEditorUI.successMessage);
  const manageMembersErrorCopy =
    manageMembersUI.errorMessage === null ? null : resolveThreadCopy(manageMembersUI.errorMessage);
  const manageMembersSuccessCopy =
    manageMembersUI.successMessage === null ? null : resolveThreadCopy(manageMembersUI.successMessage);
  const lifecycleErrorCopy =
    lifecycleActionUI.errorMessage === null ? null : resolveThreadCopy(lifecycleActionUI.errorMessage);
  const lifecycleStatusCopy =
    lifecycleActionUI.statusMessage === null ? null : resolveThreadCopy(lifecycleActionUI.statusMessage);
  const directDeleteErrorCopy =
    directDeleteUI.errorMessage === null ? null : resolveThreadCopy(directDeleteUI.errorMessage);
  const secretComposerErrorCopy =
    secretComposerError === null ? null : resolveThreadCopy(secretComposerError);
  const isSecretConversation = activeConversation?.type === "secret";
  const forbiddenMessage = t("chatThread.errors.forbidden");
  const threadWriteUnavailableMessage = threadLifecycleReadOnly ? t("chatThread.lifecycle.closedState") : forbiddenMessage;
  const threadTitle = resolveThreadTitle(chatID, activeConversation, actorUserID);
  const directDeleteConfirmationPhrase = useMemo(
    () => resolveDirectDeleteConfirmationPhrase(activeConversation, threadTitle),
    [activeConversation, threadTitle],
  );
  const directDeleteSubmitEnabled = isDirectDeleteConfirmationSubmitEnabled(
    directDeleteUI,
    directDeleteConfirmationPhrase,
  );
  const threadHeaderAvatarDescriptor = useMemo(
    () => resolveThreadHeaderAvatarDescriptor(activeConversation, actorUserID),
    [activeConversation, actorUserID, displayNameRevision],
  );
  const notificationTargetModalTitle = t("chatThread.notifications.unavailableTitle");
  const notificationTargetModalBody = t("chatThread.notifications.unavailableBody");

  useEffect(() => {
    setComposerReference(null);
    setForwardTargetPicker(null);
    setComposerFileAttachment(null);
  }, [chatID]);

  useEffect(() => {
    composerBodyRef.current = composerBody;
  }, [composerBody]);

  useEffect(() => {
    if (pendingForwardComposerReference === null) {
      return;
    }
    setComposerReference(pendingForwardComposerReference);
    setPendingActionError(null);
    setMediaMenuOpen(false);
    setVoiceComposerOpen(false);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    window.requestAnimationFrame(() => {
      composerInputRef.current?.focus();
    });
  }, [location.pathname, location.search, navigate, pendingForwardComposerReference]);

  useEffect(() => {
    if (isSecretConversation && composerReference !== null) {
      setComposerReference(null);
    }
    if (isSecretConversation && composerFileAttachment !== null) {
      setComposerFileAttachment(null);
    }
  }, [composerFileAttachment, composerReference, isSecretConversation]);

  const handleUnauthorized = (error: unknown): boolean => {
    if (!(error instanceof GatewayAPIError) || error.code !== "unauthorized") {
      return false;
    }
    authStore.clear();
    navigate("/login", { replace: true });
    return true;
  };
  const handleForbidden = (error: unknown): boolean => {
    if (!(error instanceof GatewayAPIError) || error.code !== "forbidden") {
      return false;
    }
    setPendingActionError(null);
    setTimelineError(forbiddenMessage);
    setRealtimeReadyChatID(null);
    setHasChatAccess(false);
    setActiveConversation(null);
    setReceiptParticipantsByUserID({});
    return true;
  };
  const markConversationReadBestEffort = (sequence: number | null) => {
    if (chatID === "" || sequence === null || !Number.isFinite(sequence)) {
      return;
    }
    const readSequence = Math.trunc(sequence);
    if (readSequence <= 0 || readSequence <= lastMarkedReadSequenceRef.current) {
      return;
    }

    lastMarkedReadSequenceRef.current = readSequence;
    void markConversationRead(chatID, readSequence).catch((error) => {
      if (handleUnauthorized(error)) {
        return;
      }
      // Read receipts are best-effort; message rendering must not fail if the receipt update races auth/access.
    });
  };

  const applyVoiceUIEvent = (event: VoiceRecordingUIEvent) => {
    setVoiceRecordingUI((current) => reduceVoiceRecordingUIState(current, event));
  };

  const applyManageMembersUIEvent = (event: ManageMembersUIEvent) => {
    setManageMembersUI((current) => reduceManageMembersUIState(current, event));
  };

  const applyDirectDeleteUIEvent = (event: DirectDeleteUIEvent) => {
    setDirectDeleteUI((current) => reduceDirectDeleteUIState(current, event));
  };

  const applyLifecycleActionUIEvent = (event: LifecycleActionUIEvent) => {
    setLifecycleActionUI((current) => reduceLifecycleActionUIState(current, event));
  };

  const applyMetadataEditorUIEvent = (event: MetadataEditorUIEvent) => {
    setMetadataEditorUI((current) => reduceMetadataEditorUIState(current, event));
  };

  const setThreadHeaderAvatarSource = useCallback((nextSource: string) => {
    setThreadHeaderAvatarSourceState((current) => {
      if (current === nextSource) {
        return current;
      }
      if (current.startsWith("blob:")) {
        URL.revokeObjectURL(current);
      }
      return nextSource;
    });
  }, []);

  const onDismissNotificationTargetModal = () => {
    setNotificationTargetModalVisible(false);
  };

  const onReturnToChatList = () => {
    setNotificationTargetModalVisible(false);
    navigate("/app");
  };

  const clearVoiceTimers = () => {
    if (voiceDurationTimeoutRef.current !== null) {
      window.clearTimeout(voiceDurationTimeoutRef.current);
      voiceDurationTimeoutRef.current = null;
    }
  };

  const releaseVoicePreview = () => {
    setVoicePreview((current) => {
      if (current !== null) {
        URL.revokeObjectURL(current.previewURL);
      }
      return null;
    });
  };

  const releaseVoiceRecordingSession = () => {
    const active = voiceRecordingSessionRef.current;
    if (active !== null) {
      stopMediaStreamTracks(active.stream);
    }
    voiceRecordingSessionRef.current = null;
  };

  const setVoicePlaybackSource = (mediaID: string, previewURL: string) => {
    setVoicePlaybackSources((current) => {
      const normalizedMediaID = mediaID.trim();
      if (normalizedMediaID === "") {
        return current;
      }
      const existing = current[normalizedMediaID];
      if (existing === previewURL) {
        return current;
      }
      if (existing !== undefined) {
        URL.revokeObjectURL(existing);
      }
      return {
        ...current,
        [normalizedMediaID]: previewURL,
      };
    });
  };

  const markVoicePlayerNativeFallback = (mediaID: string) => {
    const normalizedMediaID = mediaID.trim();
    if (normalizedMediaID === "") {
      return;
    }
    setVoicePlayerNativeFallback((current) => {
      if (current[normalizedMediaID] === true) {
        return current;
      }
      return {
        ...current,
        [normalizedMediaID]: true,
      };
    });
  };

  const ensureAttachmentCacheLoaded = () => {
    if (attachmentCacheLoadedRef.current) {
      return;
    }
    attachmentCacheRef.current = readAttachmentCacheFromStorage();
    attachmentCacheLoadedRef.current = true;
  };

  const restoreAndRememberMessageAttachments = (input: ChatMessage[]): ChatMessage[] => {
    ensureAttachmentCacheLoaded();
    const cache = attachmentCacheRef.current;
    let changed = false;
    let cacheChanged = false;

    const next = input.map((message) => {
      const messageID = message.message_id.trim();
      if (messageID === "") {
        return message;
      }

      const incoming = normalizeAttachmentCacheList(message.attachments);
      if (incoming.length > 0) {
        const currentCached = cache[messageID];
        const cachedSerialized = currentCached === undefined ? "" : JSON.stringify(currentCached);
        const incomingSerialized = JSON.stringify(incoming);
        if (cachedSerialized !== incomingSerialized) {
          cache[messageID] = incoming;
          cacheChanged = true;
        }
        if (message.attachments !== incoming) {
          changed = true;
          return {
            ...message,
            attachments: incoming,
          };
        }
        return message;
      }

      const fallback = cache[messageID];
      if (fallback === undefined || fallback.length === 0) {
        return message;
      }
      changed = true;
      return {
        ...message,
        attachments: fallback,
      };
    });

    if (cacheChanged) {
      attachmentCacheDirtyRef.current = true;
      writeAttachmentCacheToStorage(cache);
      attachmentCacheDirtyRef.current = false;
    }

    return changed ? next : input;
  };

  const setTimelineMessages = (
    next: ChatMessage[] | ((current: ChatMessage[]) => ChatMessage[]),
  ) => {
    if (typeof next === "function") {
      setMessages((current) => restoreAndRememberMessageAttachments(next(current)));
      return;
    }
    setMessages(restoreAndRememberMessageAttachments(next));
  };

  const isComposerFocused = (): boolean => {
    if (typeof document === "undefined") {
      return false;
    }
    const composerInput = composerInputRef.current;
    return composerInput !== null && document.activeElement === composerInput;
  };

  const captureThreadBottomFollowIntent = (): boolean => {
    const shouldFollow = shouldFollowTimelineUpdateWhileComposing({
      element: threadPaneRef.current,
      composerFocused: isComposerFocused(),
      composerDraftActive: composerBodyRef.current.trim() !== "",
    });
    followThreadBottomRef.current = shouldFollow;
    return shouldFollow;
  };

  const requestThreadBottomFollow = () => {
    followThreadBottomRef.current = true;
  };

  const setPhotoAttachmentSource = (mediaID: string, previewURL: string) => {
    setPhotoAttachmentSources((current) => {
      const normalizedMediaID = mediaID.trim();
      if (normalizedMediaID === "") {
        return current;
      }
      const existing = current[normalizedMediaID];
      if (existing === previewURL) {
        return current;
      }
      if (existing !== undefined) {
        URL.revokeObjectURL(existing);
      }
      return {
        ...current,
        [normalizedMediaID]: previewURL,
      };
    });
  };

  const resolveMediaPreviewCacheInput = (
    mediaID: string,
    kind: MediaPreviewKind,
    accessToken: string | null,
  ): MediaPreviewCacheKeyInput | null => {
    const normalizedActorUserID = actorUserID?.trim() ?? "";
    const normalizedMediaID = mediaID.trim();
    if (accessToken === null || normalizedActorUserID === "" || normalizedMediaID === "") {
      return null;
    }
    return {
      actorUserID: normalizedActorUserID,
      mediaID: resolveConversationMediaStateKey(chatID, normalizedMediaID),
      kind,
      representation: kind === "voice" ? "playback" : "inline_preview",
      gatewayOrigin: resolveGatewayOrigin(),
    };
  };

  const setExpandedPhotoState = useCallback((next: ExpandedPhotoState | null) => {
    setExpandedPhoto((current) => {
      const currentSource = current?.sourceURL ?? null;
      const nextSource = next?.sourceURL ?? null;
      if (currentSource !== null && currentSource !== nextSource) {
        URL.revokeObjectURL(currentSource);
      }
      expandedPhotoSourceRef.current = nextSource;
      return next;
    });
  }, []);

  const closeExpandedPhoto = useCallback(() => {
    expandedPhotoRequestRef.current += 1;
    setExpandedPhotoState(null);
  }, [setExpandedPhotoState]);

  const openFullSizePhotoAttachment = useCallback(
    async (mediaID: string): Promise<void> => {
      const normalizedMediaID = mediaID.trim();
      if (normalizedMediaID === "") {
        return;
      }
      const requestID = expandedPhotoRequestRef.current + 1;
      expandedPhotoRequestRef.current = requestID;
      setExpandedPhotoState({ status: "loading", sourceURL: null });

      const result = await readPhotoAttachmentFullSizeSource({
        mediaID: normalizedMediaID,
        conversationID: chatID,
        accessToken: authStore.getAccessToken(),
      });
      if (expandedPhotoRequestRef.current !== requestID) {
        if (result.sourceURL !== null) {
          URL.revokeObjectURL(result.sourceURL);
        }
        return;
      }

      if (result.status === "loaded") {
        setExpandedPhotoState({ status: "loaded", sourceURL: result.sourceURL });
        return;
      }
      setExpandedPhotoState({ status: "unavailable", sourceURL: null });
    },
    [chatID, setExpandedPhotoState],
  );

  const hydratePhotoAttachmentSource = async (mediaID: string): Promise<void> => {
    const normalizedMediaID = mediaID.trim();
    if (normalizedMediaID === "") {
      return;
    }
    const mediaStateKey = resolveConversationMediaStateKey(chatID, normalizedMediaID);
    if (photoAttachmentSourcesRef.current[mediaStateKey] !== undefined) {
      return;
    }
    if (photoSourceHydrationRef.current.has(mediaStateKey)) {
      return;
    }

    const accessToken = authStore.getAccessToken();
    if (accessToken === null) {
      return;
    }

    photoSourceHydrationRef.current.add(mediaStateKey);
    try {
      try {
        const cacheInput = resolveMediaPreviewCacheInput(normalizedMediaID, "photo", accessToken);
        if (cacheInput !== null) {
          const cached = await readCachedMediaPreview(cacheInput);
          if (cached !== null) {
            const previewURL = URL.createObjectURL(cached);
            setPhotoAttachmentSource(mediaStateKey, previewURL);
            return;
          }
        }

        const response = await fetch(
          resolvePhotoAttachmentPreviewReadURL(normalizedMediaID, chatID),
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );
        if (!response.ok) {
          if (cacheInput !== null && shouldInvalidateMediaPreviewCacheForStatus(response.status)) {
            await deleteCachedMediaPreview(cacheInput);
          }
          return;
        }
        const binary = await response.blob();
        if (binary.size <= 0) {
          if (cacheInput !== null) {
            await deleteCachedMediaPreview(cacheInput);
          }
          return;
        }
        if (cacheInput !== null) {
          await writeCachedMediaPreview(cacheInput, binary);
        }
        const previewURL = URL.createObjectURL(binary);
        setPhotoAttachmentSource(mediaStateKey, previewURL);
      } catch {
        return;
      }
    } finally {
      photoSourceHydrationRef.current.delete(mediaStateKey);
    }
  };

  const hydrateVoicePlaybackSource = async (mediaID: string): Promise<void> => {
    const normalizedMediaID = mediaID.trim();
    if (normalizedMediaID === "") {
      return;
    }
    const mediaStateKey = resolveConversationMediaStateKey(chatID, normalizedMediaID);
    if (voicePlaybackSourcesRef.current[mediaStateKey] !== undefined) {
      return;
    }
    if (voiceSourceHydrationRef.current.has(mediaStateKey)) {
      return;
    }

    const accessToken = authStore.getAccessToken();
    if (accessToken === null) {
      return;
    }

    voiceSourceHydrationRef.current.add(mediaStateKey);
    try {
      try {
        const cacheInput = resolveMediaPreviewCacheInput(normalizedMediaID, "voice", accessToken);
        if (cacheInput !== null) {
          const cached = await readCachedMediaPreview(cacheInput);
          if (cached !== null) {
            const previewURL = URL.createObjectURL(cached);
            setVoicePlaybackSource(mediaStateKey, previewURL);
            return;
          }
        }

        const response = await fetch(
          resolveMediaBinaryURL(normalizedMediaID, undefined, {
            conversationID: chatID,
            purpose: "message_attachment",
          }),
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );
        if (!response.ok) {
          if (cacheInput !== null && shouldInvalidateMediaPreviewCacheForStatus(response.status)) {
            await deleteCachedMediaPreview(cacheInput);
          }
          return;
        }
        const binary = await response.blob();
        const playableVoice = await normalizeRecordedVoiceBlobForPlayback(binary);
        if (playableVoice.size <= 0) {
          if (cacheInput !== null) {
            await deleteCachedMediaPreview(cacheInput);
          }
          return;
        }
        if (cacheInput !== null) {
          await writeCachedMediaPreview(cacheInput, playableVoice);
        }
        const previewURL = URL.createObjectURL(playableVoice);
        setVoicePlaybackSource(mediaStateKey, previewURL);
      } catch {
        return;
      }
    } finally {
      voiceSourceHydrationRef.current.delete(mediaStateKey);
    }
  };

  const setFileMetadataForConversation = (
    conversationID: string,
    metadata: MessageAttachmentMetadata,
  ) => {
    const normalizedMediaID = metadata.media_id.trim();
    if (normalizedMediaID === "") {
      return;
    }
    const mediaStateKey = resolveConversationMediaStateKey(conversationID, normalizedMediaID);
    setFileAttachmentMetadata((current) => {
      const existing = current[mediaStateKey];
      if (
        existing !== undefined &&
        existing.filename === metadata.filename &&
        existing.status === metadata.status &&
        existing.size_bytes === metadata.size_bytes &&
        existing.mime_type === metadata.mime_type
      ) {
        return current;
      }
      return {
        ...current,
        [mediaStateKey]: metadata,
      };
    });
  };

  const hydrateFileAttachmentMetadata = async (mediaID: string): Promise<void> => {
    const normalizedMediaID = mediaID.trim();
    if (normalizedMediaID === "" || chatID === "") {
      return;
    }
    const mediaStateKey = resolveConversationMediaStateKey(chatID, normalizedMediaID);
    if (fileAttachmentMetadataRef.current[mediaStateKey] !== undefined) {
      return;
    }
    if (fileMetadataHydrationRef.current.has(mediaStateKey)) {
      return;
    }

    fileMetadataHydrationRef.current.add(mediaStateKey);
    try {
      const metadata = await getMessageAttachmentMetadata(normalizedMediaID, chatID);
      setFileMetadataForConversation(chatID, metadata);
    } catch (error) {
      if (error instanceof GatewayAPIError && error.code === "unauthorized") {
        handleUnauthorized(error);
      }
    } finally {
      fileMetadataHydrationRef.current.delete(mediaStateKey);
    }
  };

  const downloadFileAttachment = async (mediaID: string, filename: string): Promise<void> => {
    const normalizedMediaID = mediaID.trim();
    if (normalizedMediaID === "" || chatID === "") {
      return;
    }
    const accessToken = authStore.getAccessToken();
    if (accessToken === null) {
      authStore.clear();
      navigate("/login", { replace: true });
      return;
    }
    try {
      const response = await fetch(
        resolveMessageAttachmentDownloadURL(normalizedMediaID, chatID, resolveGatewayOrigin()),
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      if (!response.ok) {
        setPendingActionError(t("chatThread.files.errors.downloadFailed"));
        return;
      }
      const blob = await response.blob();
      if (blob.size <= 0) {
        setPendingActionError(t("chatThread.files.errors.downloadFailed"));
        return;
      }
      const blobURL = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = blobURL;
      anchor.download = filename.trim() || "download";
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(blobURL), 1_000);
      setPendingActionError(null);
    } catch {
      setPendingActionError(t("chatThread.files.errors.downloadFailed"));
    }
  };

  useEffect(() => {
    pendingPhotosRef.current = pendingPhotos;
  }, [pendingPhotos]);

  useEffect(() => {
    pendingVoicesRef.current = pendingVoices;
  }, [pendingVoices]);

  useEffect(() => {
    photoAttachmentSourcesRef.current = photoAttachmentSources;
  }, [photoAttachmentSources]);

  useEffect(() => {
    voicePlaybackSourcesRef.current = voicePlaybackSources;
  }, [voicePlaybackSources]);

  useEffect(() => {
    fileAttachmentMetadataRef.current = fileAttachmentMetadata;
  }, [fileAttachmentMetadata]);

  useEffect(() => {
    const photoMediaIDs = new Set<string>();
    for (const message of messages) {
      for (const attachment of message.attachments ?? []) {
        if (!isPhotoAttachmentKind(attachment.kind)) {
          continue;
        }
        const mediaID = attachment.media_id.trim();
        if (mediaID !== "") {
          photoMediaIDs.add(mediaID);
        }
      }
    }

    for (const mediaID of photoMediaIDs) {
      void hydratePhotoAttachmentSource(mediaID);
    }
  }, [messages]);

  useEffect(() => {
    const voiceMediaIDs = new Set<string>();
    for (const message of messages) {
      for (const attachment of message.attachments ?? []) {
        if (!isVoiceAttachmentKind(attachment.kind)) {
          continue;
        }
        const mediaID = attachment.media_id.trim();
        if (mediaID !== "") {
          voiceMediaIDs.add(mediaID);
        }
      }
    }

    for (const mediaID of voiceMediaIDs) {
      void hydrateVoicePlaybackSource(mediaID);
    }
  }, [messages]);

  useEffect(() => {
    const fileMediaIDs = new Set<string>();
    for (const message of messages) {
      for (const attachment of message.attachments ?? []) {
        if (!isFileAttachmentKind(attachment.kind)) {
          continue;
        }
        const mediaID = attachment.media_id.trim();
        if (mediaID !== "") {
          fileMediaIDs.add(mediaID);
        }
      }
    }

    for (const mediaID of fileMediaIDs) {
      void hydrateFileAttachmentMetadata(mediaID);
    }
  }, [messages]);

  useEffect(() => {
    closeExpandedPhoto();
    lastMarkedReadSequenceRef.current = 0;
    setReceiptParticipantsByUserID({});
  }, [chatID, closeExpandedPhoto]);

  useEffect(() => {
    if (chatID === "" || !hasChatAccess) {
      return;
    }

    let cancelled = false;

    const loadReceiptSummary = async () => {
      try {
        const summary = await getConversationReceiptSummary(chatID);
        if (cancelled || summary.chat_id !== chatID) {
          return;
        }
        setReceiptParticipantsByUserID((current) =>
          mergeReceiptParticipantSummaries(current, summary.participants),
        );
      } catch (error) {
        if (cancelled) {
          return;
        }
        if (error instanceof GatewayAPIError && error.code === "unauthorized") {
          authStore.clear();
          navigate("/login", { replace: true });
        }
      }
    };

    void loadReceiptSummary();

    return () => {
      cancelled = true;
    };
  }, [chatID, hasChatAccess, navigate]);

  useEffect(() => {
    return () => {
      for (const pending of pendingPhotosRef.current) {
        URL.revokeObjectURL(pending.preview_url);
      }
      for (const pending of pendingVoicesRef.current) {
        URL.revokeObjectURL(pending.preview_url);
      }
      for (const source of Object.values(photoAttachmentSourcesRef.current)) {
        URL.revokeObjectURL(source);
      }
      for (const source of Object.values(voicePlaybackSourcesRef.current)) {
        URL.revokeObjectURL(source);
      }
      if (expandedPhotoSourceRef.current !== null) {
        URL.revokeObjectURL(expandedPhotoSourceRef.current);
      }
      clearVoiceTimers();
      releaseVoiceRecordingSession();
      releaseVoicePreview();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (chatID === "") {
        setTimelineError("Missing chat identifier.");
        setLoading(false);
        setHighlightedMessageID(null);
        setRealtimeReadyChatID(null);
        setHasChatAccess(false);
        setActiveConversation(null);
        return;
      }

      setLoading(true);
      setTimelineError(null);
      setNotificationTargetModalVisible(false);
      setRealtimeReadyChatID(null);
      setHasChatAccess(false);
      setActiveConversation(null);

      try {
        const conversationList = await listConversations({ limit: 200 });
        const conversation = conversationList.conversations.find((item) => item.chat_id === chatID) ?? null;
        setActiveConversation(conversation);

        if (contextTarget !== null) {
          const response = await listMessageContext(chatID, {
            aroundSequence: contextTarget.aroundSequence,
            before: SEARCH_CONTEXT_BEFORE,
            after: SEARCH_CONTEXT_AFTER,
          });
          if (!cancelled) {
            const sorted = sortMessagesAscending(response.messages);
            setTimelineMessages(sorted);
            markConversationReadBestEffort(resolveLatestMessageSequence(sorted));
            setHighlightedMessageID(
              resolveContextHighlightMessageID(sorted, contextTarget.messageID, response.around_sequence),
            );
            setRealtimeReadyChatID(chatID);
            setHasChatAccess(true);
            setActiveConversation(conversation);
          }
          return;
        }

        const response = await listMessages(chatID, { limit: 80 });
        if (!cancelled) {
          const sorted = sortMessagesAscending(response.messages);
          const latestMessage = sorted[sorted.length - 1] ?? null;
          setTimelineMessages(sorted);
          markConversationReadBestEffort(resolveLatestMessageSequence(sorted));
          setHighlightedMessageID(null);
          setDefaultLatestMessageID(latestMessage?.message_id ?? null);
          setDefaultLatestScrollRevision((revision) => revision + 1);
          setRealtimeReadyChatID(chatID);
          setHasChatAccess(true);
          setActiveConversation(conversation);
        }
      } catch (error) {
        if (!cancelled) {
          if (handleUnauthorized(error)) {
            return;
          }
          if (error instanceof GatewayAPIError && (error.code === "forbidden" || error.code === "not_found")) {
            if (shouldShowNotificationTargetModal(notificationNavigation, error.code)) {
              setTimelineMessages([]);
              setTimelineError(null);
              setNotificationTargetModalVisible(true);
              setPendingActionError(null);
              setHighlightedMessageID(null);
              setRealtimeReadyChatID(null);
              setHasChatAccess(false);
              setActiveConversation(null);
              return;
            }
          }
          if (handleForbidden(error)) {
            return;
          }
          setTimelineError(error instanceof Error ? error.message : "Failed to load message timeline.");
          setHighlightedMessageID(null);
          setRealtimeReadyChatID(null);
          setHasChatAccess(false);
          setActiveConversation(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [chatID, contextTarget, notificationNavigation]);

  useEffect(() => {
    if (activeConversation === null && messages.length === 0) {
      return;
    }

    let cancelled = false;

    const hydrateDisplayNames = async () => {
      const userIDs = new Set<string>();
      if (activeConversation !== null) {
        for (const userID of collectDirectPeerUserIDs([activeConversation], actorUserID)) {
          userIDs.add(userID);
        }
        for (const userID of collectMembershipUserIDs(activeConversation)) {
          userIDs.add(userID);
        }
      }
      for (const userID of collectMessageSenderUserIDs(messages)) {
        userIDs.add(userID);
      }

      if (userIDs.size === 0) {
        return;
      }

      const changed = await resolveUserDisplayNames(userIDs);
      if (!cancelled && changed) {
        setDisplayNameRevision((value) => value + 1);
      }
    };

    void hydrateDisplayNames();

    return () => {
      cancelled = true;
    };
  }, [activeConversation, actorUserID, messages]);

  useEffect(() => {
    threadHeaderAvatarSourceRef.current = threadHeaderAvatarSource;
  }, [threadHeaderAvatarSource]);

  useEffect(() => {
    return () => {
      const source = threadHeaderAvatarSourceRef.current;
      if (source.startsWith("blob:")) {
        URL.revokeObjectURL(source);
      }
      threadHeaderAvatarSourceRef.current = "";
    };
  }, []);

  useEffect(() => {
    if (threadHeaderAvatarDescriptor.sourceURL !== "") {
      setThreadHeaderAvatarSource(threadHeaderAvatarDescriptor.sourceURL);
      return;
    }
    const binaryPath =
      threadHeaderAvatarDescriptor.profileAvatarBinaryPath ?? threadHeaderAvatarDescriptor.mediaAssetBinaryPath;
    if (binaryPath === null) {
      setThreadHeaderAvatarSource("");
      return;
    }

    const accessToken = authStore.getAccessToken();
    if (accessToken === null) {
      setThreadHeaderAvatarSource("");
      return;
    }

    let cancelled = false;

    const hydrateProfileAvatar = async () => {
      try {
        const response = await fetch(binaryPath, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        if (!response.ok) {
          if (!cancelled) {
            setThreadHeaderAvatarSource("");
          }
          return;
        }
        const binary = await response.blob();
        if (binary.size <= 0) {
          if (!cancelled) {
            setThreadHeaderAvatarSource("");
          }
          return;
        }
        if (!cancelled) {
          setThreadHeaderAvatarSource(URL.createObjectURL(binary));
        }
      } catch {
        if (!cancelled) {
          setThreadHeaderAvatarSource("");
        }
      }
    };

    void hydrateProfileAvatar();

    return () => {
      cancelled = true;
    };
  }, [
    setThreadHeaderAvatarSource,
    threadHeaderAvatarDescriptor.mediaAssetBinaryPath,
    threadHeaderAvatarDescriptor.profileAvatarBinaryPath,
    threadHeaderAvatarDescriptor.sourceURL,
  ]);

  useEffect(() => {
    if (highlightedMessageID === null) {
      return;
    }
    const node = messageBodyRefs.current[highlightedMessageID];
    if (node === null || node === undefined) {
      return;
    }
    node.scrollIntoView({
      block: "center",
      behavior: "smooth",
    });
  }, [highlightedMessageID, messages]);

  useEffect(() => {
    if (!followThreadBottomRef.current) {
      return;
    }
    followThreadBottomRef.current = false;
    const threadPane = threadPaneRef.current;
    if (threadPane === null) {
      return;
    }
    const frameID = window.requestAnimationFrame(() => {
      threadPane.scrollTop = threadPane.scrollHeight;
    });
    return () => {
      window.cancelAnimationFrame(frameID);
    };
  }, [messages, pendingPhotos, pendingVoices]);

  useEffect(() => {
    const accessToken = authStore.getAccessToken();
    if (chatID === "" || accessToken === null || realtimeReadyChatID !== chatID) {
      return;
    }

    let cancelled = false;
    setRealtimeState("connecting");
    const subscription = connectRealtime({
      accessToken,
      onOpen: () => {
        if (cancelled) {
          return;
        }
        setRealtimeState("connected");
      },
      onClose: () => {
        if (cancelled) {
          return;
        }
        setRealtimeState("closed");
      },
      onError: () => {
        if (cancelled) {
          return;
        }
        setRealtimeState("error");
      },
      onEvent: (event) => {
        if (cancelled) {
          return;
        }
        if (event.chat_id !== chatID) {
          return;
        }
        const lifecycleRealtimeAction = resolveThreadLifecycleRealtimeAction(event, chatID);
        if (lifecycleRealtimeAction === "navigate-away") {
          navigateToSafeChatShellAfterRemove();
          return;
        }
        if (lifecycleRealtimeAction === "refresh") {
          void refreshThreadAfterConversationMutation().catch((error) => {
            handleUnauthorized(error);
          });
          return;
        }
        if (event.type === "receipt.updated") {
          setReceiptParticipantsByUserID((current) =>
            applyReceiptUpdatedEventToParticipantMap(current, event, actorUserID),
          );
          return;
        }
        const shouldFollowRealtime = captureThreadBottomFollowIntent();
        setTimelineMessages((current) => applyRealtimeEvent(current, event));
        if (event.type === "message.created") {
          markConversationReadBestEffort(typeof event.sequence === "number" ? event.sequence : null);
        }
        if (!shouldHydrateSecretRealtimeEvent(event, activeConversation)) {
          return;
        }

        if (typeof event.sequence !== "number") {
          return;
        }
        const aroundSequence = Math.trunc(event.sequence);
        if (!Number.isFinite(aroundSequence) || aroundSequence <= 0) {
          return;
        }
        if (secretRealtimeHydrationRef.current.has(aroundSequence)) {
          return;
        }

        secretRealtimeHydrationRef.current.add(aroundSequence);
        void (async () => {
          try {
            const hydrated = await listMessageContext(chatID, {
              aroundSequence,
              before: SECRET_REALTIME_CONTEXT_BEFORE,
              after: SECRET_REALTIME_CONTEXT_AFTER,
            });
            if (cancelled) {
              return;
            }
            if (shouldFollowRealtime) {
              requestThreadBottomFollow();
            }
            setTimelineMessages((current) =>
              mergeTimelineMessages(current, sortMessagesAscending(hydrated.messages)),
            );
          } catch {
            // Best-effort hydration for secret realtime events.
          } finally {
            secretRealtimeHydrationRef.current.delete(aroundSequence);
          }
        })();
      },
    });

    return () => {
      cancelled = true;
      subscription.close();
    };
  }, [activeConversation, chatID, realtimeReadyChatID]);

  useEffect(() => {
    if (!hasChatAccess && searchOpen) {
      setSearchOpen(false);
    }
  }, [hasChatAccess, searchOpen]);

  useEffect(() => {
    if (!threadWriteEnabled && mediaMenuOpen) {
      setMediaMenuOpen(false);
    }
  }, [mediaMenuOpen, threadWriteEnabled]);

  useEffect(() => {
    if (!isSecretConversation) {
      return;
    }

    if (mediaMenuOpen) {
      setMediaMenuOpen(false);
    }
    if (voiceComposerOpen) {
      voiceHoldActiveRef.current = false;
      voiceQueuedStopRef.current = "none";
      clearVoiceTimers();
      releaseVoiceRecordingSession();
      setVoiceRecordingStartedAt(null);
      releaseVoicePreview();
      applyVoiceUIEvent({ type: "cancelled" });
      setVoiceComposerOpen(false);
    }
  }, [isSecretConversation, mediaMenuOpen, voiceComposerOpen]);

  useEffect(() => {
    if ((manageMembersConversationType === null || !threadWriteEnabled) && manageMembersOpen) {
      setManageMembersOpen(false);
    }
  }, [manageMembersConversationType, manageMembersOpen, threadWriteEnabled]);

  useEffect(() => {
    if (manageMembersConversationType === null) {
      applyManageMembersUIEvent({ type: "reset" });
    }
  }, [manageMembersConversationType]);

  useEffect(() => {
    if ((metadataEditorConversationType === null || !threadWriteEnabled) && metadataEditorOpen) {
      setMetadataEditorOpen(false);
    }
  }, [metadataEditorConversationType, metadataEditorOpen, threadWriteEnabled]);

  useEffect(() => {
    if (metadataEditorConversationType === null) {
      applyMetadataEditorUIEvent({ type: "reset" });
      setMetadataEditorAvatarPatchMode("keep");
      setMetadataEditorAvatarFile(null);
    }
  }, [metadataEditorConversationType]);

  useEffect(() => {
    if (threadWriteEnabled || !voiceComposerOpen) {
      return;
    }
    voiceHoldActiveRef.current = false;
    voiceQueuedStopRef.current = "none";
    clearVoiceTimers();
    releaseVoiceRecordingSession();
    setVoiceRecordingStartedAt(null);
    releaseVoicePreview();
    applyVoiceUIEvent({ type: "cancelled" });
    setVoiceComposerOpen(false);
  }, [threadWriteEnabled, voiceComposerOpen]);

  useEffect(() => {
    if (!showLifecycleAction && lifecycleActionUI.pendingAction !== null) {
      applyLifecycleActionUIEvent({ type: "cancel" });
    }
  }, [lifecycleActionUI.pendingAction, showLifecycleAction]);

  useEffect(() => {
    if (!showDirectDeleteAction && directDeleteUI.panelOpen) {
      applyDirectDeleteUIEvent({ type: "cancel" });
    }
  }, [directDeleteUI.panelOpen, showDirectDeleteAction]);

  useEffect(() => {
    voiceHoldActiveRef.current = false;
    voiceQueuedStopRef.current = "none";
    secretRealtimeHydrationRef.current.clear();
    clearVoiceTimers();
    releaseVoiceRecordingSession();
    setVoiceRecordingStartedAt(null);
    releaseVoicePreview();
    applyVoiceUIEvent({ type: "cancelled" });
    setVoiceComposerOpen(false);
    setSecretComposerError(null);
    setSecretRatchetSessionID(chatID === "" ? "" : `ratchet-${chatID}`);
    setSecretRatchetCounter("1");
    setSecretPreviousChainLength("0");
    setManageMembersOpen(false);
    setMetadataEditorOpen(false);
    setNotificationTargetModalVisible(false);
    applyDirectDeleteUIEvent({ type: "reset" });
    applyLifecycleActionUIEvent({ type: "reset" });
    applyManageMembersUIEvent({ type: "reset" });
    applyMetadataEditorUIEvent({ type: "reset" });
    setMetadataEditorAvatarPatchMode("keep");
    setMetadataEditorAvatarFile(null);
  }, [chatID]);

  const onBack = () => {
    navigate("/app");
  };

  const onToggleManageMembers = () => {
    if (!threadWriteEnabled || manageMembersConversationType === null) {
      return;
    }
    applyManageMembersUIEvent({ type: "clear-feedback" });
    setManageMembersOpen((current) => !current);
  };

  const onToggleMetadataEditor = () => {
    if (!threadWriteEnabled || metadataEditorConversationType === null || activeConversation === null) {
      return;
    }
    if (!metadataEditorOpen) {
      applyMetadataEditorUIEvent({
        type: "seed",
        name: activeConversation.name ?? "",
        description: activeConversation.description ?? "",
      });
      setMetadataEditorAvatarPatchMode("keep");
      setMetadataEditorAvatarFile(null);
    } else {
      applyMetadataEditorUIEvent({ type: "clear-feedback" });
    }
    setMetadataEditorOpen((current) => !current);
  };

  const onOpenDirectDelete = () => {
    if (!showDirectDeleteAction) {
      return;
    }
    setManageMembersOpen(false);
    setMetadataEditorOpen(false);
    applyLifecycleActionUIEvent({ type: "cancel" });
    applyDirectDeleteUIEvent({ type: "open" });
  };

  const onOpenLifecycleAction = (action: LifecycleAction) => {
    if (!showLifecycleAction) {
      return;
    }
    setManageMembersOpen(false);
    setMetadataEditorOpen(false);
    applyLifecycleActionUIEvent({ type: "open", action });
  };

  const refreshThreadAfterConversationMutation = async (): Promise<ConversationSummary | null> => {
    const [conversationResponse, messagesResponse] = await Promise.all([
      listConversations({ limit: 200 }),
      listMessages(chatID, { limit: 80 }),
    ]);
    const refreshedConversation =
      conversationResponse.conversations.find((conversation) => conversation.chat_id === chatID) ?? null;

    setActiveConversation(refreshedConversation);
    const sortedMessages = sortMessagesAscending(messagesResponse.messages);
    setTimelineMessages(sortedMessages);
    markConversationReadBestEffort(resolveLatestMessageSequence(sortedMessages));
    setTimelineError(null);
    setHasChatAccess(true);
    setRealtimeReadyChatID(chatID);
    return refreshedConversation;
  };

  const navigateToSafeChatShellAfterRemove = () => {
    setPendingActionError(null);
    setTimelineError(null);
    setHasChatAccess(false);
    setRealtimeReadyChatID(null);
    setActiveConversation(null);
    setReceiptParticipantsByUserID({});
    setTimelineMessages([]);
    navigate("/app", { replace: true });
  };

  const onSubmitDirectDelete = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!showDirectDeleteAction || !directDeleteSubmitEnabled) {
      return;
    }
    if (chatID === "") {
      setPendingActionError("Missing chat identifier.");
      return;
    }

    applyDirectDeleteUIEvent({ type: "submit-requested" });
    setPendingActionError(null);

    try {
      await executeDirectDeleteAction({
        chatID,
        deleteDirectConversation,
        navigateToSafeChatShell: navigateToSafeChatShellAfterRemove,
      });
      applyDirectDeleteUIEvent({ type: "submit-succeeded" });
    } catch (error) {
      if (handleUnauthorized(error)) {
        return;
      }

      if (error instanceof GatewayAPIError) {
        applyDirectDeleteUIEvent({
          type: "submit-failed",
          errorCode: error.code,
        });
        return;
      }

      applyDirectDeleteUIEvent({
        type: "submit-failed",
        fallbackMessage: error instanceof Error ? error.message : undefined,
      });
    }
  };

  const onSubmitLifecycleAction = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const action = lifecycleActionUI.pendingAction;
    if (
      action === null ||
      lifecycleConversationType === null ||
      !showLifecycleAction ||
      !lifecycleSubmitEnabled
    ) {
      return;
    }
    if (chatID === "") {
      setPendingActionError("Missing chat identifier.");
      return;
    }

    applyLifecycleActionUIEvent({ type: "submit-requested" });
    setPendingActionError(null);

    try {
      await executeConversationLifecycleAction({
        chatID,
        action,
        conversationType: lifecycleConversationType,
        closeGroupConversation,
        closeChannelConversation,
        removeGroupConversation,
        removeChannelConversation,
        refreshThreadAfterClose: refreshThreadAfterConversationMutation,
        navigateToSafeChatShell: navigateToSafeChatShellAfterRemove,
      });
      applyLifecycleActionUIEvent({
        type: "submit-succeeded",
        action,
        conversationType: lifecycleConversationType,
      });
    } catch (error) {
      if (handleUnauthorized(error)) {
        return;
      }

      if (error instanceof GatewayAPIError) {
        applyLifecycleActionUIEvent({
          type: "submit-failed",
          action,
          conversationType: lifecycleConversationType,
          errorCode: error.code,
        });
        return;
      }

      applyLifecycleActionUIEvent({
        type: "submit-failed",
        action,
        conversationType: lifecycleConversationType,
        fallbackMessage: error instanceof Error ? error.message : undefined,
      });
    }
  };

  const onSubmitManageMembers = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!threadWriteEnabled || manageMembersConversationType === null || manageMembersBusy) {
      return;
    }
    if (!manageMembersCanAdd) {
      applyManageMembersUIEvent({
        type: "submit-failed",
        conversationType: manageMembersConversationType,
        errorCode: "forbidden",
      });
      return;
    }
    if (chatID === "") {
      setPendingActionError("Missing chat identifier.");
      return;
    }

    const identifier = manageMembersUI.identifier.trim();
    if (identifier === "") {
      applyManageMembersUIEvent({
        type: "submit-failed",
        conversationType: manageMembersConversationType,
        fallbackMessage: "chatThread.manageMembers.errors.identifierRequired",
      });
      return;
    }

    applyManageMembersUIEvent({ type: "submit-requested" });
    setPendingActionError(null);

    try {
      const resolvedUserID = identifier.startsWith("usr_")
        ? identifier
        : await resolveUserIDByIdentifier(identifier);
      if (manageMembersConversationType === "group") {
        await addGroupMember(chatID, resolvedUserID);
      } else {
        await addChannelSubscriber(chatID, resolvedUserID);
      }

      await refreshThreadAfterConversationMutation();
      applyManageMembersUIEvent({
        type: "submit-succeeded",
        conversationType: manageMembersConversationType,
      });
    } catch (error) {
      if (handleUnauthorized(error)) {
        return;
      }

      if (error instanceof GatewayAPIError) {
        applyManageMembersUIEvent({
          type: "submit-failed",
          conversationType: manageMembersConversationType,
          errorCode: error.code,
        });
        return;
      }

      applyManageMembersUIEvent({
        type: "submit-failed",
        conversationType: manageMembersConversationType,
        fallbackMessage: error instanceof Error ? error.message : undefined,
      });
    }
  };

  const onRemoveManageMember = async (userID: string) => {
    if (!threadWriteEnabled || manageMembersConversationType === null || manageMembersBusy) {
      return;
    }
    if (chatID === "") {
      setPendingActionError("Missing chat identifier.");
      return;
    }

    const normalizedUserID = userID.trim();
    const actor = actorUserID?.trim() ?? "";
    const selfRemoval = actor !== "" && normalizedUserID === actor;
    if (normalizedUserID === "") {
      applyManageMembersUIEvent({
        type: "remove-failed",
        conversationType: manageMembersConversationType,
        userID: "",
        errorCode: "invalid_request",
      });
      return;
    }

    applyManageMembersUIEvent({
      type: "remove-requested",
      userID: normalizedUserID,
    });
    setPendingActionError(null);

    try {
      if (manageMembersConversationType === "group") {
        await removeGroupMember(chatID, normalizedUserID);
      } else {
        await removeChannelSubscriber(chatID, normalizedUserID);
      }

      if (selfRemoval) {
        applyManageMembersUIEvent({
          type: "remove-succeeded",
          conversationType: manageMembersConversationType,
          userID: normalizedUserID,
        });
        setManageMembersOpen(false);
        navigate("/app");
        return;
      }

      await refreshThreadAfterConversationMutation();
      applyManageMembersUIEvent({
        type: "remove-succeeded",
        conversationType: manageMembersConversationType,
        userID: normalizedUserID,
      });
    } catch (error) {
      if (handleUnauthorized(error)) {
        return;
      }

      if (error instanceof GatewayAPIError) {
        applyManageMembersUIEvent({
          type: "remove-failed",
          conversationType: manageMembersConversationType,
          userID: normalizedUserID,
          errorCode: error.code,
        });
        return;
      }

      applyManageMembersUIEvent({
        type: "remove-failed",
        conversationType: manageMembersConversationType,
        userID: normalizedUserID,
        fallbackMessage: error instanceof Error ? error.message : undefined,
      });
    }
  };

  const onSubmitMetadataEditor = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!threadWriteEnabled || metadataEditorConversationType === null || metadataEditorBusy) {
      return;
    }
    if (chatID === "") {
      setPendingActionError("Missing chat identifier.");
      return;
    }

    const nameValidationError = validateMetadataEditorName(metadataEditorUI.name);
    if (nameValidationError !== null) {
      applyMetadataEditorUIEvent({
        type: "submit-failed",
        conversationType: metadataEditorConversationType,
        fallbackMessage: nameValidationError,
      });
      return;
    }
    const descriptionValidationError = validateMetadataEditorDescription(metadataEditorUI.description);
    if (descriptionValidationError !== null) {
      applyMetadataEditorUIEvent({
        type: "submit-failed",
        conversationType: metadataEditorConversationType,
        fallbackMessage: descriptionValidationError,
      });
      return;
    }

    applyMetadataEditorUIEvent({ type: "submit-requested" });
    setPendingActionError(null);

    let avatarAssetPatch = resolveMetadataEditorAvatarAssetPatch(metadataEditorAvatarPatchMode);
    if (metadataEditorAvatarPatchMode === "replace") {
      if (metadataEditorAvatarFile === null) {
        applyMetadataEditorUIEvent({
          type: "submit-failed",
          conversationType: metadataEditorConversationType,
          fallbackMessage: "chatThread.metadataEditor.avatarRequiredForReplace",
        });
        return;
      }

      try {
        const normalizedAvatarAssetID = await uploadAvatarMedia(metadataEditorAvatarFile);
        avatarAssetPatch = resolveMetadataEditorAvatarAssetPatch("replace", normalizedAvatarAssetID);
      } catch (error) {
        if (handleUnauthorized(error)) {
          return;
        }
        applyMetadataEditorUIEvent({
          type: "submit-failed",
          conversationType: metadataEditorConversationType,
          fallbackMessage: mapMetadataEditorAvatarUploadError(error),
        });
        return;
      }
    }

    try {
      if (metadataEditorConversationType === "group") {
        await updateGroupConversationMetadata(chatID, {
          name: metadataEditorUI.name,
          description: metadataEditorUI.description,
          avatarAssetID: avatarAssetPatch,
        });
      } else {
        await updateChannelConversationMetadata(chatID, {
          name: metadataEditorUI.name,
          description: metadataEditorUI.description,
          avatarAssetID: avatarAssetPatch,
        });
      }

      const refreshedConversation = await refreshThreadAfterConversationMutation();
      setMetadataEditorAvatarPatchMode("keep");
      setMetadataEditorAvatarFile(null);
      applyMetadataEditorUIEvent({
        type: "submit-succeeded",
        conversationType: metadataEditorConversationType,
        name: refreshedConversation?.name ?? metadataEditorUI.name,
        description: refreshedConversation?.description ?? metadataEditorUI.description,
      });
    } catch (error) {
      if (handleUnauthorized(error)) {
        return;
      }

      if (error instanceof GatewayAPIError) {
        applyMetadataEditorUIEvent({
          type: "submit-failed",
          conversationType: metadataEditorConversationType,
          errorCode: error.code,
        });
        return;
      }

      applyMetadataEditorUIEvent({
        type: "submit-failed",
        conversationType: metadataEditorConversationType,
        fallbackMessage: error instanceof Error ? error.message : undefined,
      });
    }
  };

  const onOpenSearchResult = async (hit: SearchMessageHit) => {
    setPendingActionError(null);

    if (hit.chat_id !== chatID) {
      navigate(buildContextNavigationPath(hit.chat_id, hit.sequence, hit.message_id));
      return;
    }

    setLoading(true);
    try {
      const response = await listMessageContext(chatID, {
        aroundSequence: hit.sequence,
        before: SEARCH_CONTEXT_BEFORE,
        after: SEARCH_CONTEXT_AFTER,
      });
      const sorted = sortMessagesAscending(response.messages);
      setTimelineMessages(sorted);
      markConversationReadBestEffort(resolveLatestMessageSequence(sorted));
      setHighlightedMessageID(resolveContextHighlightMessageID(sorted, hit.message_id, response.around_sequence));
      setTimelineError(null);
    } catch (error) {
      if (handleUnauthorized(error)) {
        return;
      }
      if (handleForbidden(error)) {
        return;
      }
      setPendingActionError(error instanceof Error ? error.message : "Failed to open search context.");
    } finally {
      setLoading(false);
    }
  };

  const onOpenReplyReference = async (reference: MessageReferencePreview) => {
    const target = resolveReplyReferenceContextTarget(reference, chatID, messages);
    if (target === null) {
      return;
    }

    setPendingActionError(null);
    if (target.kind === "loaded") {
      setHighlightedMessageID(target.messageID);
      return;
    }

    setLoading(true);
    try {
      const response = await listMessageContext(chatID, {
        aroundSequence: target.aroundSequence,
        before: SEARCH_CONTEXT_BEFORE,
        after: SEARCH_CONTEXT_AFTER,
      });
      const sorted = sortMessagesAscending(response.messages);
      setTimelineMessages(sorted);
      markConversationReadBestEffort(resolveLatestMessageSequence(sorted));
      setHighlightedMessageID(resolveContextHighlightMessageID(sorted, target.messageID, response.around_sequence));
      setTimelineError(null);
    } catch (error) {
      if (handleUnauthorized(error)) {
        return;
      }
      if (handleForbidden(error)) {
        return;
      }
      setPendingActionError(error instanceof Error ? error.message : "Failed to open reply context.");
    } finally {
      setLoading(false);
    }
  };

  const removePendingPhotoByID = (localID: string, clearFileRef: boolean) => {
    setPendingPhotos((current) => {
      const target = current.find((item) => item.local_id === localID);
      if (target !== undefined) {
        URL.revokeObjectURL(target.preview_url);
      }
      return removePendingPhoto(current, localID);
    });
    if (clearFileRef) {
      delete pendingPhotoFilesRef.current[localID];
    }
  };

  const setPendingPhotoPatch = (localID: string, patch: Partial<PendingPhotoMessage>) => {
    setPendingPhotos((current) => patchPendingPhoto(current, localID, patch));
  };

  const removePendingVoiceByID = (localID: string, clearUploadRef: boolean, revokePreview: boolean) => {
    setPendingVoices((current) => {
      const target = current.find((item) => item.local_id === localID);
      if (target !== undefined && revokePreview) {
        URL.revokeObjectURL(target.preview_url);
      }
      return removePendingVoice(current, localID);
    });
    if (clearUploadRef) {
      delete pendingVoiceUploadsRef.current[localID];
    }
  };

  const setPendingVoicePatch = (localID: string, patch: Partial<PendingVoiceMessage>) => {
    setPendingVoices((current) => patchPendingVoice(current, localID, patch));
  };

  const waitForReadyAsset = async (
    assetID: string,
    initialStatus: string,
    localID: string,
  ): Promise<string> => {
    const normalizedInitialStatus = initialStatus.trim().toLowerCase();
    if (normalizedInitialStatus === "ready") {
      return assetID;
    }
    if (normalizedInitialStatus === "failed") {
      throw new Error("Photo processing failed.");
    }

    let lastKnownStatus = normalizedInitialStatus === "" ? "processing" : normalizedInitialStatus;
    for (let attempt = 0; attempt < MEDIA_ASSET_POLL_ATTEMPTS; attempt += 1) {
      const asset = await getMediaAsset(assetID);
      const status = asset.status.trim().toLowerCase();
      if (status === "ready") {
        return asset.asset_id;
      }
      if (status === "failed") {
        const errorCode = asset.error_code?.trim() ?? "";
        if (errorCode !== "") {
          throw new Error(`Photo processing failed (${errorCode}).`);
        }
        throw new Error("Photo processing failed.");
      }

      lastKnownStatus = status === "" ? lastKnownStatus : status;
      const progress = 70 + Math.trunc(((attempt + 1) / MEDIA_ASSET_POLL_ATTEMPTS) * 24);
      setPendingPhotoPatch(localID, {
        status: "pending",
        progress_percent: progress,
        error_message: undefined,
      });
      await delay(MEDIA_ASSET_POLL_INTERVAL_MS);
    }

    throw new Error(`Photo processing timed out (${lastKnownStatus}).`);
  };

  const waitForReadyVoiceAsset = async (
    assetID: string,
    initialStatus: string,
    localID: string,
  ): Promise<string> => {
    const normalizedInitialStatus = initialStatus.trim().toLowerCase();
    if (normalizedInitialStatus === "ready") {
      return assetID;
    }
    if (normalizedInitialStatus === "failed") {
      throw new Error("Voice processing failed.");
    }

    let lastKnownStatus = normalizedInitialStatus === "" ? "processing" : normalizedInitialStatus;
    for (let attempt = 0; attempt < MEDIA_ASSET_POLL_ATTEMPTS; attempt += 1) {
      const asset = await getMediaAsset(assetID);
      const status = asset.status.trim().toLowerCase();
      if (status === "ready") {
        return asset.asset_id;
      }
      if (status === "failed") {
        const errorCode = asset.error_code?.trim() ?? "";
        if (errorCode !== "") {
          throw new Error(`Voice processing failed (${errorCode}).`);
        }
        throw new Error("Voice processing failed.");
      }

      lastKnownStatus = status === "" ? lastKnownStatus : status;
      const progress = 70 + Math.trunc(((attempt + 1) / MEDIA_ASSET_POLL_ATTEMPTS) * 24);
      setPendingVoicePatch(localID, {
        status: "pending",
        progress_percent: progress,
        error_message: undefined,
      });
      await delay(MEDIA_ASSET_POLL_INTERVAL_MS);
    }

    throw new Error(`Voice processing timed out (${lastKnownStatus}).`);
  };

  const uploadAndSendPendingPhoto = async (localID: string, chatIDForSend: string, file: File) => {
    if (!threadWriteEnabled) {
      setPendingPhotoPatch(localID, {
        status: "failed",
        progress_percent: null,
        error_message: threadWriteUnavailableMessage,
      });
      return;
    }

    setPendingPhotoPatch(localID, {
      status: "pending",
      progress_percent: 8,
      error_message: undefined,
    });

    try {
      const metadata = await readPhotoFinalizeMetadata(file);

      const initiated: InitiateMediaUploadResponse = await initiateMediaUpload({
        kind: "photo",
        declared_size_bytes: Math.max(1, Math.trunc(file.size)),
      });

      setPendingPhotoPatch(localID, {
        status: "pending",
        progress_percent: 28,
        error_message: undefined,
      });

      const uploaded = await uploadMediaBinary(
        initiated.binary_upload_url,
        file,
        initiated.binary_upload_method,
        file.type.trim() === "" ? "application/octet-stream" : file.type,
      );

      setPendingPhotoPatch(localID, {
        status: "pending",
        progress_percent: 54,
        error_message: undefined,
      });

      const finalized = await finalizeMediaUpload(initiated.upload_id, {
        size_bytes: uploaded.size_bytes,
        mime_type: uploaded.mime_type,
        metadata,
      });

      setPendingPhotoPatch(localID, {
        status: "pending",
        progress_percent: 70,
        error_message: undefined,
      });

      const readyAssetID = await waitForReadyAsset(finalized.asset_id, finalized.status, localID);

      setPendingPhotoPatch(localID, {
        status: "pending",
        progress_percent: 97,
        error_message: undefined,
      });

      const sent = await sendChatMessage(
        chatIDForSend,
        createChatMessageInput({
          body: "",
          attachments: [{ media_id: readyAssetID, kind: "photo" }],
        }),
      );
      const sentWithPhoto = ensureMessageAttachment(sent, {
        media_id: readyAssetID,
        kind: "photo",
      });

      const shouldFollowSentPhoto = captureThreadBottomFollowIntent();
      setTimelineMessages((current) => upsertMessage(current, sentWithPhoto));
      markConversationReadBestEffort(sentWithPhoto.sequence);
      setHighlightedMessageID(shouldFollowSentPhoto ? sentWithPhoto.message_id : null);
      setPendingActionError(null);
      removePendingPhotoByID(localID, true);
    } catch (error) {
      if (handleUnauthorized(error)) {
        removePendingPhotoByID(localID, true);
        return;
      }
      if (handleForbidden(error)) {
        setPendingPhotoPatch(localID, {
          status: "failed",
          progress_percent: null,
          error_message: forbiddenMessage,
        });
        return;
      }

      setPendingPhotoPatch(localID, {
        status: "failed",
        progress_percent: null,
        error_message: error instanceof Error ? error.message : "Failed to send photo.",
      });
    }
  };

  const onRetryPendingPhoto = (localID: string) => {
    const file = pendingPhotoFilesRef.current[localID];
    if (file === undefined) {
      setPendingPhotoPatch(localID, {
        status: "failed",
        progress_percent: null,
        error_message: "Photo source is unavailable. Pick the photo again.",
      });
      return;
    }

    void uploadAndSendPendingPhoto(localID, chatID, file);
  };

  const onDeletePendingPhoto = (localID: string) => {
    removePendingPhotoByID(localID, true);
  };

  const uploadAndSendPendingVoice = async (localID: string, uploadInput: PendingVoiceUploadInput) => {
    if (!threadWriteEnabled) {
      setPendingVoicePatch(localID, {
        status: "failed",
        progress_percent: null,
        error_message: threadWriteUnavailableMessage,
      });
      return;
    }
    if (shouldNormalizeRecordedVoiceToWav(uploadInput.mimeType)) {
      setPendingVoicePatch(localID, {
        status: "failed",
        progress_percent: null,
        error_message: "Voice format is not iPhone-compatible. Record again after reloading this page.",
      });
      return;
    }
    if (uploadInput.blob.size < VOICE_MIN_CAPTURE_BYTES) {
      setPendingVoicePatch(localID, {
        status: "failed",
        progress_percent: null,
        error_message: "Voice capture was too short. Hold the button longer and try again.",
      });
      return;
    }

    setPendingVoicePatch(localID, {
      status: "pending",
      progress_percent: 8,
      error_message: undefined,
    });

    try {
      const initiated = await initiateMediaUpload({
        kind: "voice",
        declared_size_bytes: Math.max(1, Math.trunc(uploadInput.blob.size)),
      });

      setPendingVoicePatch(localID, {
        status: "pending",
        progress_percent: 28,
        error_message: undefined,
      });

      const uploaded = await uploadMediaBinary(
        initiated.binary_upload_url,
        uploadInput.blob,
        initiated.binary_upload_method,
        uploadInput.mimeType.trim() === "" ? VOICE_MIME_TYPE_FALLBACK : uploadInput.mimeType,
      );

      setPendingVoicePatch(localID, {
        status: "pending",
        progress_percent: 54,
        error_message: undefined,
      });

      const finalized = await finalizeMediaUpload(initiated.upload_id, {
        size_bytes: uploaded.size_bytes,
        mime_type: uploaded.mime_type,
        metadata: createVoiceFinalizeMetadata(uploadInput.durationSeconds),
      });

      setPendingVoicePatch(localID, {
        status: "pending",
        progress_percent: 70,
        error_message: undefined,
      });

      const readyAssetID = await waitForReadyVoiceAsset(finalized.asset_id, finalized.status, localID);

      setPendingVoicePatch(localID, {
        status: "pending",
        progress_percent: 97,
        error_message: undefined,
      });

      const sent = await sendChatMessage(
        uploadInput.chatID,
        createChatMessageInput({
          body: "",
          attachments: [{ media_id: readyAssetID, kind: "voice" }],
        }),
      );
      const sentWithVoice = ensureMessageAttachment(sent, {
        media_id: readyAssetID,
        kind: "voice",
      });

      setVoicePlaybackSource(resolveConversationMediaStateKey(uploadInput.chatID, readyAssetID), uploadInput.previewURL);
      const shouldFollowSentVoice = captureThreadBottomFollowIntent();
      setTimelineMessages((current) => upsertMessage(current, sentWithVoice));
      markConversationReadBestEffort(sentWithVoice.sequence);
      setHighlightedMessageID(shouldFollowSentVoice ? sentWithVoice.message_id : null);
      setPendingActionError(null);
      removePendingVoiceByID(localID, true, false);
    } catch (error) {
      if (handleUnauthorized(error)) {
        removePendingVoiceByID(localID, true, true);
        return;
      }
      if (handleForbidden(error)) {
        setPendingVoicePatch(localID, {
          status: "failed",
          progress_percent: null,
          error_message: forbiddenMessage,
        });
        return;
      }

      setPendingVoicePatch(localID, {
        status: "failed",
        progress_percent: null,
        error_message: error instanceof Error ? error.message : "Failed to send voice.",
      });
    }
  };

  const onRetryPendingVoice = (localID: string) => {
    const uploadInput = pendingVoiceUploadsRef.current[localID];
    if (uploadInput === undefined) {
      setPendingVoicePatch(localID, {
        status: "failed",
        progress_percent: null,
        error_message: "Voice source is unavailable. Record voice again.",
      });
      return;
    }

    void uploadAndSendPendingVoice(localID, uploadInput);
  };

  const onDeletePendingVoice = (localID: string) => {
    removePendingVoiceByID(localID, true, true);
  };

  const resolvePreferredVoiceMimeType = (): string => {
    if (typeof window === "undefined" || typeof window.MediaRecorder === "undefined") {
      return "";
    }
    if (typeof window.MediaRecorder.isTypeSupported !== "function") {
      return "";
    }

    const candidates = [
      "audio/mp4;codecs=mp4a.40.2",
      "audio/mp4",
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/ogg;codecs=opus",
    ];
    for (const candidate of candidates) {
      if (window.MediaRecorder.isTypeSupported(candidate)) {
        return candidate;
      }
    }

    return "";
  };

  const stopVoiceRecording = async ({
    capped,
    discard,
  }: {
    capped: boolean;
    discard: boolean;
  }): Promise<void> => {
    const active = voiceRecordingSessionRef.current;
    clearVoiceTimers();
    setVoiceRecordingStartedAt(null);

    if (active === null) {
      if (discard) {
        releaseVoicePreview();
        applyVoiceUIEvent({ type: "cancelled" });
      }
      return;
    }

    const { mediaRecorder, stopPromise, startedAt, stream } = active;
    voiceRecordingSessionRef.current = null;
    if (mediaRecorder.state !== "inactive") {
      mediaRecorder.stop();
    }

    let captured: Blob;
    try {
      captured = await stopPromise;
    } catch (error) {
      stopMediaStreamTracks(stream);
      applyVoiceUIEvent({ type: "error", message: resolveVoiceRecorderErrorMessage(error) });
      return;
    }

    stopMediaStreamTracks(stream);

    if (discard) {
      releaseVoicePreview();
      applyVoiceUIEvent({ type: "cancelled" });
      return;
    }

    if (captured.size <= 0) {
      applyVoiceUIEvent({
        type: "error",
        message: "No audio was captured. Hold the button longer and try again.",
      });
      return;
    }

    const durationMillis = Math.min(VOICE_MAX_DURATION_MILLIS, Math.max(0, Date.now() - startedAt));
    const durationSeconds = Math.max(1, Math.ceil(durationMillis / 1000));
    let normalizedCapture: Blob;
    try {
      normalizedCapture = await normalizeRecordedVoiceBlobForUpload(captured);
    } catch (error) {
      applyVoiceUIEvent({
        type: "error",
        message: error instanceof Error ? error.message : "Voice format conversion failed. Record again.",
      });
      return;
    }
    if (normalizedCapture.size < VOICE_MIN_CAPTURE_BYTES) {
      applyVoiceUIEvent({
        type: "error",
        message: "Voice capture was too short. Hold the button longer and try again.",
      });
      return;
    }
    const previewURL = URL.createObjectURL(normalizedCapture);
    const mimeType = normalizedCapture.type.trim() === "" ? VOICE_MIME_TYPE_FALLBACK : normalizedCapture.type;

    releaseVoicePreview();
    setVoicePreview({
      blob: normalizedCapture,
      previewURL,
      mimeType,
      durationSeconds: normalizeVoiceDurationSeconds(durationSeconds),
      capped,
    });
    applyVoiceUIEvent({
      type: "stop-succeeded",
      durationSeconds,
      capped,
    });
  };

  const startVoiceRecording = async () => {
    if (!threadWriteEnabled) {
      setPendingActionError(threadWriteUnavailableMessage);
      return;
    }
    if (chatID === "") {
      setPendingActionError("Missing chat identifier.");
      return;
    }
    if (
      typeof window === "undefined" ||
      typeof window.MediaRecorder === "undefined" ||
      typeof navigator === "undefined" ||
      navigator.mediaDevices === undefined ||
      typeof navigator.mediaDevices.getUserMedia !== "function"
    ) {
      applyVoiceUIEvent({
        type: "error",
        message: "Voice recording is unavailable in this browser.",
      });
      return;
    }
    if (!window.isSecureContext) {
      applyVoiceUIEvent({
        type: "error",
        message: VOICE_SECURE_CONTEXT_REQUIRED_MESSAGE,
      });
      return;
    }
    if (voiceRecordingSessionRef.current !== null) {
      return;
    }

    setPendingActionError(null);
    releaseVoicePreview();
    setVoiceRecordingStartedAt(null);
    applyVoiceUIEvent({ type: "start-requested" });

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const chunks: BlobPart[] = [];
      const mimeType = resolvePreferredVoiceMimeType();

      const recorder =
        mimeType === ""
          ? new window.MediaRecorder(stream)
          : new window.MediaRecorder(stream, {
              mimeType,
            });
      const stopPromise = new Promise<Blob>((resolve, reject) => {
        recorder.addEventListener("dataavailable", (event) => {
          if (event.data.size > 0) {
            chunks.push(event.data);
          }
        });
        recorder.addEventListener("error", (event) => {
          const recorderError = event.error ?? new Error("Voice recording failed.");
          reject(recorderError);
        });
        recorder.addEventListener("stop", () => {
          const blobType = resolveRecordedVoiceMimeType({
            recorderMimeType: recorder.mimeType,
            chunkMimeType: readFirstBlobMimeType(chunks),
            requestedMimeType: mimeType,
          });
          resolve(new Blob(chunks, { type: blobType }));
        });
      });

      const startedAt = Date.now();
      voiceRecordingSessionRef.current = {
        mediaRecorder: recorder,
        stream,
        startedAt,
        stopPromise,
      };

      recorder.start(240);
      setVoiceRecordingStartedAt(startedAt);
      applyVoiceUIEvent({ type: "start-succeeded" });
      voiceDurationTimeoutRef.current = window.setTimeout(() => {
        void stopVoiceRecording({
          capped: true,
          discard: false,
        });
      }, VOICE_MAX_DURATION_MILLIS);

      const queuedStop = voiceQueuedStopRef.current;
      if (!voiceHoldActiveRef.current || queuedStop !== "none") {
        voiceQueuedStopRef.current = "none";
        void stopVoiceRecording({
          capped: false,
          discard: queuedStop === "cancel",
        });
      }
    } catch (error) {
      clearVoiceTimers();
      releaseVoiceRecordingSession();
      setVoiceRecordingStartedAt(null);
      applyVoiceUIEvent({
        type: "error",
        message: resolveVoiceRecorderErrorMessage(error),
      });
    }
  };

  const onOpenVoiceComposer = () => {
    if (!threadWriteEnabled) {
      setPendingActionError(threadWriteUnavailableMessage);
      setMediaMenuOpen(false);
      return;
    }
    setPendingActionError(null);
    setMediaMenuOpen(false);
    setVoiceComposerOpen(true);
  };

  const releasedOverVoiceCancel = (event: ReactPointerEvent<HTMLButtonElement>): boolean => {
    const target = voiceCancelButtonRef.current;
    if (target === null) {
      return false;
    }
    const bounds = target.getBoundingClientRect();
    return (
      event.clientX >= bounds.left &&
      event.clientX <= bounds.right &&
      event.clientY >= bounds.top &&
      event.clientY <= bounds.bottom
    );
  };

  const onVoiceHoldStart = (event: ReactPointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (voiceRecordingUI.mode === "recording" || voiceRecordingUI.mode === "arming") {
      return;
    }
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    event.currentTarget.setPointerCapture(event.pointerId);

    voiceHoldActiveRef.current = true;
    voiceQueuedStopRef.current = "none";
    void startVoiceRecording();
  };

  const onVoiceHoldEnd = (event: ReactPointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    voiceHoldActiveRef.current = false;
    const cancelledPointer = event.type === "pointercancel";
    const releasedOverCancel = !cancelledPointer && releasedOverVoiceCancel(event);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    if (voiceRecordingUI.mode === "arming") {
      voiceQueuedStopRef.current = cancelledPointer || releasedOverCancel ? "cancel" : "release";
      return;
    }
    if (voiceRecordingUI.mode !== "recording") {
      return;
    }

    void stopVoiceRecording({
      capped: false,
      discard: cancelledPointer || releasedOverCancel,
    });
  };

  const onVoiceCancel = () => {
    voiceHoldActiveRef.current = false;

    if (voiceRecordingUI.mode === "arming") {
      voiceQueuedStopRef.current = "cancel";
      setVoiceRecordingStartedAt(null);
      applyVoiceUIEvent({ type: "cancelled" });
      return;
    }

    void stopVoiceRecording({
      capped: false,
      discard: true,
    });
  };

  const onDeleteVoicePreview = () => {
    releaseVoicePreview();
    applyVoiceUIEvent({ type: "preview-discarded" });
  };

  const onSendVoicePreview = () => {
    if (voicePreview === null) {
      return;
    }
    if (chatID === "") {
      setPendingActionError("Missing chat identifier.");
      return;
    }
    if (!threadWriteEnabled) {
      setPendingActionError(threadWriteUnavailableMessage);
      return;
    }

    const localID = randomPendingVoiceID();
    const pendingPreviewURL = URL.createObjectURL(voicePreview.blob);
    const uploadInput: PendingVoiceUploadInput = {
      blob: voicePreview.blob,
      mimeType: voicePreview.mimeType,
      durationSeconds: voicePreview.durationSeconds,
      capped: voicePreview.capped,
      chatID,
      previewURL: pendingPreviewURL,
    };
    pendingVoiceUploadsRef.current[localID] = uploadInput;

    const pendingVoice: PendingVoiceMessage = {
      local_id: localID,
      chat_id: chatID,
      created_at: new Date().toISOString(),
      preview_url: uploadInput.previewURL,
      mime_type: uploadInput.mimeType,
      duration_seconds: uploadInput.durationSeconds,
      capped: uploadInput.capped,
      progress_percent: 4,
      status: "pending",
    };

    setPendingVoices((current) => upsertPendingVoice(current, pendingVoice));
    setPendingActionError(null);
    releaseVoicePreview();
    applyVoiceUIEvent({ type: "preview-discarded" });
    void uploadAndSendPendingVoice(localID, uploadInput);
  };

  const onOpenPhotoPicker = () => {
    if (!threadWriteEnabled) {
      setPendingActionError(threadWriteUnavailableMessage);
      setMediaMenuOpen(false);
      return;
    }

    voiceHoldActiveRef.current = false;
    voiceQueuedStopRef.current = "none";
    clearVoiceTimers();
    releaseVoiceRecordingSession();
    setVoiceRecordingStartedAt(null);
    releaseVoicePreview();
    applyVoiceUIEvent({ type: "cancelled" });
    setVoiceComposerOpen(false);
    setPendingActionError(null);
    setMediaMenuOpen(false);
    filePickerRef.current?.click();
  };

  const onPhotoSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = "";
    if (file === null) {
      return;
    }
    if (chatID === "") {
      setPendingActionError("Missing chat identifier.");
      return;
    }
    if (!threadWriteEnabled) {
      setPendingActionError(threadWriteUnavailableMessage);
      return;
    }

    const localID = randomPendingPhotoID();
    const previewURL = URL.createObjectURL(file);
    pendingPhotoFilesRef.current[localID] = file;

    const pendingMessage: PendingPhotoMessage = {
      local_id: localID,
      chat_id: chatID,
      file_name: file.name.trim() === "" ? "photo" : file.name,
      preview_url: previewURL,
      created_at: new Date().toISOString(),
      progress_percent: 4,
      status: "pending",
    };

    setPendingPhotos((current) => upsertPendingPhoto(current, pendingMessage));
    setPendingActionError(null);
    void uploadAndSendPendingPhoto(localID, chatID, file);
  };

  const patchComposerFileAttachment = (
    localID: string,
    patch: Partial<ComposerFileAttachmentState>,
  ) => {
    setComposerFileAttachment((current) => {
      if (current === null || current.local_id !== localID) {
        return current;
      }
      return {
        ...current,
        ...patch,
      };
    });
  };

  const onOpenFilePicker = () => {
    if (!threadWriteEnabled) {
      setPendingActionError(threadWriteUnavailableMessage);
      setMediaMenuOpen(false);
      return;
    }
    setPendingActionError(null);
    setMediaMenuOpen(false);
    setVoiceComposerOpen(false);
    fileAttachmentPickerRef.current?.click();
  };

  const onFileSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = "";
    if (file === null) {
      return;
    }
    if (chatID === "") {
      setPendingActionError("Missing chat identifier.");
      return;
    }
    if (!threadWriteEnabled) {
      setPendingActionError(threadWriteUnavailableMessage);
      return;
    }

    let draft: ComposerFileAttachmentState;
    try {
      draft = createComposerFileAttachmentDraft(file);
    } catch (error) {
      setPendingActionError(error instanceof Error ? error.message : t("chatThread.files.errors.invalid"));
      return;
    }
    if (!isSafeFileUploadExtension(draft.filename)) {
      setComposerFileAttachment({
        ...draft,
        status: "failed",
        progress_percent: null,
        error_message: t("chatThread.files.errors.invalid"),
      });
      setPendingActionError(null);
      return;
    }

    setComposerFileAttachment(draft);
    setPendingActionError(null);
  };

  const onRemoveComposerFileAttachment = () => {
    setComposerFileAttachment(null);
  };

  const uploadComposerFileAttachment = async (
    attachment: ComposerFileAttachmentState,
  ): Promise<MessageAttachmentMetadata> => {
    patchComposerFileAttachment(attachment.local_id, {
      status: "uploading",
      progress_percent: 8,
      error_message: undefined,
    });

    const initiated = await initiateMediaUpload({
      kind: "file",
      filename: attachment.filename,
      declared_size_bytes: Math.max(1, Math.trunc(attachment.file.size)),
    });
    const uploadFilename = initiated.filename?.trim() || attachment.filename;

    patchComposerFileAttachment(attachment.local_id, {
      status: "uploading",
      progress_percent: 32,
      filename: uploadFilename,
      error_message: undefined,
    });

    const uploaded = await uploadMediaBinary(
      initiated.binary_upload_url,
      attachment.file,
      initiated.binary_upload_method,
      attachment.mime_type,
    );

    patchComposerFileAttachment(attachment.local_id, {
      status: "uploading",
      progress_percent: 62,
      error_message: undefined,
    });

    const finalized = await finalizeMediaUpload(initiated.upload_id, {
      size_bytes: uploaded.size_bytes,
      mime_type: uploaded.mime_type,
      filename: uploadFilename,
    });

    patchComposerFileAttachment(attachment.local_id, {
      status: "uploading",
      progress_percent: 90,
      error_message: undefined,
    });

    return {
      media_id: finalized.asset_id,
      filename: finalized.filename?.trim() || uploadFilename,
      kind: "file",
      status: finalized.status,
      size_bytes: finalized.size_bytes,
      mime_type: finalized.mime_type,
    };
  };

  const onSend = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (chatID === "") {
      return;
    }
    if (!threadWriteEnabled) {
      setPendingActionError(threadWriteUnavailableMessage);
      return;
    }

    setMediaMenuOpen(false);
    setPendingActionError(null);
    if (isSecretConversation) {
      setSecretComposerError(null);
    }
    setSending(true);

    try {
      let sent: ChatMessage;
      if (isSecretConversation) {
        const envelope = buildSecretMessageEnvelope({
          ciphertext: composerBody,
          ratchetSessionID: secretRatchetSessionID,
          ratchetCounter: secretRatchetCounter,
          previousChainLength: secretPreviousChainLength,
        });
        sent = await sendSecretMessage(chatID, envelope);
        setSecretRatchetCounter(String(envelope.ratchet_counter + 1));
        setSecretPreviousChainLength(String(Math.max(envelope.previous_chain_length, envelope.ratchet_counter)));
      } else {
        let uploadedFileMetadata: MessageAttachmentMetadata | null = null;
        if (composerFileAttachment !== null) {
          if (composerFileAttachment.status === "uploading") {
            return;
          }
          if (composerFileAttachment.status === "failed") {
            setPendingActionError(composerFileAttachment.error_message ?? t("chatThread.files.errors.invalid"));
            return;
          }
          uploadedFileMetadata = await uploadComposerFileAttachment(composerFileAttachment);
        }
        const basePayload = applyComposerReferenceToSendPayload(
          {
            body: composerBody,
          },
          composerReference,
        );
        const attachments =
          uploadedFileMetadata === null
            ? basePayload.attachments
            : [...(basePayload.attachments ?? []), { media_id: uploadedFileMetadata.media_id, kind: "file" }];
        sent = await sendChatMessage(
          chatID,
          createChatMessageInput(
            attachments === undefined
              ? basePayload
              : {
                  ...basePayload,
                  attachments,
                },
          ),
        );
        if (uploadedFileMetadata !== null) {
          sent = ensureMessageAttachment(sent, {
            media_id: uploadedFileMetadata.media_id,
            kind: "file",
          });
          setFileMetadataForConversation(chatID, uploadedFileMetadata);
        }
      }

      const shouldFollowSentMessage = captureThreadBottomFollowIntent();
      setTimelineMessages((current) => upsertMessage(current, sent));
      markConversationReadBestEffort(sent.sequence);
      setComposerBody("");
      setComposerFileAttachment(null);
      setComposerReference((current) => reduceComposerReferenceAfterSend(current, "success"));
      setHighlightedMessageID(shouldFollowSentMessage ? sent.message_id : null);
    } catch (error) {
      if (handleUnauthorized(error)) {
        return;
      }
      if (handleForbidden(error)) {
        return;
      }
      if (isSecretConversation) {
        if (error instanceof GatewayAPIError) {
          setSecretComposerError(mapSecretSendGatewayErrorCode(error.code));
        } else if (error instanceof Error) {
          setSecretComposerError(error.message);
        } else {
          setSecretComposerError("Failed to send secret message.");
        }
        return;
      }
      if (composerReference?.kind === "forward") {
        setPendingActionError(
          error instanceof GatewayAPIError
            ? t(mapForwardSendGatewayErrorCode(error.code))
            : t("chatThread.forward.errors.failed"),
        );
        return;
      }
      if (composerFileAttachment !== null) {
        const fileError = error instanceof GatewayAPIError
          ? t(mapFileAttachmentGatewayErrorCode(error.code))
          : error instanceof Error
            ? error.message
            : t("chatThread.files.errors.failed");
        patchComposerFileAttachment(composerFileAttachment.local_id, {
          status: "failed",
          progress_percent: null,
          error_message: fileError,
        });
        return;
      }
      setPendingActionError(error instanceof Error ? error.message : "Failed to send message.");
    } finally {
      setSending(false);
    }
  };

  const onReplyToMessage = (message: ChatMessage) => {
    const nextReference = buildReplyComposerReference(message, isSecretConversation);
    if (nextReference === null) {
      return;
    }
    setComposerReference(nextReference);
    setMediaMenuOpen(false);
    setVoiceComposerOpen(false);
    setPendingActionError(null);
    setSecretComposerError(null);
    window.requestAnimationFrame(() => {
      composerInputRef.current?.focus();
    });
  };

  const onForwardMessage = async (message: ChatMessage) => {
    const nextReference = buildForwardComposerReference(message, isSecretConversation);
    if (nextReference === null || nextReference.kind !== "forward") {
      return;
    }
    setPendingActionError(null);
    setForwardTargetPicker({
      source: nextReference,
      targets: [],
      loading: true,
      errorMessage: null,
    });

    try {
      const response = await listConversations({ limit: 100 });
      const targets = filterForwardTargetConversations(response.conversations);
      setForwardTargetPicker((current) =>
        current?.source === nextReference
          ? {
              ...current,
              targets,
              loading: false,
            }
          : current,
      );
    } catch (error) {
      if (handleUnauthorized(error)) {
        return;
      }
      setForwardTargetPicker((current) =>
        current?.source === nextReference
          ? {
              ...current,
              targets: [],
              loading: false,
              errorMessage: t("chatThread.forward.errors.targets"),
            }
          : current,
      );
    }
  };

  const onSelectForwardTarget = (conversation: ConversationSummary) => {
    const source = forwardTargetPicker?.source ?? null;
    if (source === null) {
      return;
    }
    const targetChatID = conversation.chat_id.trim();
    if (targetChatID === "") {
      return;
    }
    setForwardTargetPicker(null);
    setPendingActionError(null);
    if (targetChatID === chatID) {
      setComposerReference(source);
      window.requestAnimationFrame(() => {
        composerInputRef.current?.focus();
      });
      return;
    }
    navigate(`/app/chats/${encodeURIComponent(targetChatID)}`, {
      state: {
        forwardComposerReference: source,
      },
    });
  };

  const onCancelForwardTargetPicker = () => {
    setForwardTargetPicker(null);
  };

  const onCancelComposerReference = () => {
    setComposerReference((current) => reduceComposerReferenceAfterSend(current, "cancel"));
  };

  const onStartEdit = (message: ChatMessage) => {
    setEditingMessageID(message.message_id);
    setEditingBody(message.body);
    setPendingActionError(null);
  };

  const onCancelEdit = () => {
    setEditingMessageID(null);
    setEditingBody("");
  };

  const onSaveEdit = async (messageID: string) => {
    if (chatID === "") {
      return;
    }
    try {
      const edited = await editChatMessage(chatID, messageID, editingBody);
      setTimelineMessages((current) => upsertMessage(current, edited));
      setEditingMessageID(null);
      setEditingBody("");
      setPendingActionError(null);
    } catch (error) {
      if (handleUnauthorized(error)) {
        return;
      }
      if (handleForbidden(error)) {
        return;
      }
      setPendingActionError(error instanceof Error ? error.message : "Failed to edit message.");
    }
  };

  const onDeleteMessage = async (messageID: string) => {
    if (chatID === "") {
      return;
    }
    try {
      const deleted = await deleteChatMessage(chatID, messageID);
      setTimelineMessages((current) => upsertMessage(current, deleted));
      setPendingActionError(null);
    } catch (error) {
      if (handleUnauthorized(error)) {
        return;
      }
      if (handleForbidden(error)) {
        return;
      }
      setPendingActionError(error instanceof Error ? error.message : "Failed to delete message.");
    }
  };

  const pendingTimelinePhotos = useMemo(
    () => pendingPhotos.filter((item) => item.chat_id === chatID),
    [pendingPhotos, chatID],
  );
  const pendingTimelineVoices = useMemo(
    () => pendingVoices.filter((item) => item.chat_id === chatID),
    [pendingVoices, chatID],
  );
  const voicePreviewVisible = voiceComposerOpen && voiceRecordingUI.mode === "preview" && voicePreview !== null;
  const composerFileBlocksSend = composerFileAttachment !== null && composerFileAttachment.status === "failed";
  const composerFileSendReady = composerFileAttachment !== null && composerFileAttachment.status === "selected";
  const regularSendReady =
    !composerFileBlocksSend && (composerBody.trim() !== "" || composerReference !== null || composerFileSendReady);
  const secretSendReady =
    !isSecretConversation ||
    (composerBody.trim() !== "" &&
      secretRatchetSessionID.trim() !== "" &&
      secretRatchetCounter.trim() !== "" &&
      secretPreviousChainLength.trim() !== "");
  const composerSendReady = isSecretConversation ? secretSendReady : regularSendReady;

  return (
    <main className={CHAT_THREAD_SHELL_CLASSES.root}>
      {forwardTargetPicker !== null ? (
        <ForwardTargetPicker
          state={forwardTargetPicker}
          actorUserID={actorUserID}
          onSelectTarget={onSelectForwardTarget}
          onCancel={onCancelForwardTargetPicker}
        />
      ) : null}
      {notificationTargetModalVisible ? (
        <div className="notification-target-modal-scrim" role="presentation">
          <section
            className="notification-target-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="notification-target-modal-title"
            data-testid="notification-target-modal"
          >
            <h2 className="notification-target-modal-title" id="notification-target-modal-title">
              {notificationTargetModalTitle}
            </h2>
            <p className="notification-target-modal-body">{notificationTargetModalBody}</p>
            <div className="notification-target-modal-actions">
              <button className="secondary-button" type="button" onClick={onDismissNotificationTargetModal}>
                {t("chatThread.notifications.dismiss")}
              </button>
              <button
                className="primary-button"
                type="button"
                data-testid="notification-target-modal-return"
                onClick={onReturnToChatList}
              >
                {t("chatThread.notifications.backToChats")}
              </button>
            </div>
          </section>
        </div>
      ) : null}
      <header className="chat-shell-head">
        <div className="chat-shell-heading chat-shell-heading-with-avatar">
          <span className="conversation-avatar conversation-avatar-thread" aria-hidden="true">
            {threadHeaderAvatarSource !== "" ? (
              <img
                className="conversation-avatar-image"
                src={threadHeaderAvatarSource}
                alt=""
                onError={() => setThreadHeaderAvatarSource("")}
              />
            ) : (
              threadHeaderAvatarDescriptor.initial
            )}
          </span>
          <div className="chat-shell-heading-copy">
            <p className="chat-shell-eyebrow">{t("chatThread.shell.eyebrow")}</p>
            <h1 className="chat-shell-title" data-testid="chat-thread-title">
              {threadTitle}
            </h1>
            {isSecretConversation ? (
              <p className="message-state" data-testid="thread-secret-marker">
                <IconLock /> {t("chatThread.shell.secretConversation")}
              </p>
            ) : null}
          </div>
        </div>
        <div className="chat-head-actions">
          <span className={`realtime-dot realtime-dot-${getRealtimeTone(realtimeState)}`} data-testid="realtime-status" role="status">
            <span className="sr-only">realtime: {realtimeState}</span>
          </span>
          {showDirectDeleteAction ? (
            <button
              className={`icon-button icon-button-ghost icon-button-danger${directDeleteUI.panelOpen ? " icon-button-active" : ""}`}
              type="button"
              aria-label={t("chatThread.directDelete.actions.delete")}
              title={t("chatThread.directDelete.actions.delete")}
              data-testid="direct-delete-toggle"
              onClick={onOpenDirectDelete}
            >
              <IconTrash />
            </button>
          ) : null}
          {showLifecycleAction && lifecycleConversationType !== null ? (
            <>
              <button
                className={`icon-button icon-button-ghost${lifecycleActionUI.pendingAction === "close" ? " icon-button-active" : ""}`}
                type="button"
                aria-label={
                  lifecycleConversationType === "group"
                    ? t("chatThread.lifecycle.actions.closeGroup")
                    : t("chatThread.lifecycle.actions.closeChannel")
                }
                title={
                  lifecycleConversationType === "group"
                    ? t("chatThread.lifecycle.actions.closeGroup")
                    : t("chatThread.lifecycle.actions.closeChannel")
                }
                data-testid="lifecycle-close-toggle"
                onClick={() => onOpenLifecycleAction("close")}
              >
                <IconClose />
              </button>
              <button
                className={`icon-button icon-button-ghost${lifecycleActionUI.pendingAction === "remove" ? " icon-button-active" : ""}`}
                type="button"
                aria-label={
                  lifecycleConversationType === "group"
                    ? t("chatThread.lifecycle.actions.removeGroup")
                    : t("chatThread.lifecycle.actions.removeChannel")
                }
                title={
                  lifecycleConversationType === "group"
                    ? t("chatThread.lifecycle.actions.removeGroup")
                    : t("chatThread.lifecycle.actions.removeChannel")
                }
                data-testid="lifecycle-remove-toggle"
                onClick={() => onOpenLifecycleAction("remove")}
              >
                <IconTrash />
              </button>
            </>
          ) : null}
          {showMetadataEditorAction ? (
            <button
              className={`icon-button icon-button-ghost${metadataEditorOpen ? " icon-button-active" : ""}`}
              type="button"
              aria-label={metadataEditorOpen ? t("chatThread.actions.hideMetadataEditor") : t("chatThread.actions.editMetadata")}
              title={metadataEditorOpen ? t("chatThread.actions.hideMetadataEditor") : t("chatThread.actions.editMetadata")}
              data-testid="metadata-editor-toggle"
              onClick={onToggleMetadataEditor}
            >
              <IconEdit />
            </button>
          ) : null}
          {showManageMembersAction ? (
            <button
              className={`icon-button icon-button-ghost${manageMembersOpen ? " icon-button-active" : ""}`}
              type="button"
              aria-label={manageMembersOpen ? t("chatThread.actions.hideManageMembers") : t("chatThread.actions.manageMembers")}
              title={manageMembersOpen ? t("chatThread.actions.hideManageMembers") : t("chatThread.actions.manageMembers")}
              data-testid="manage-members-toggle"
              onClick={onToggleManageMembers}
            >
              <IconUserPlus />
            </button>
          ) : null}
          {hasChatAccess ? (
            <button
              className={`icon-button icon-button-ghost${searchOpen ? " icon-button-active" : ""}`}
              type="button"
              aria-label={searchOpen ? t("chatThread.actions.hideSearch") : t("chatThread.actions.showSearch")}
              title={searchOpen ? t("chatThread.actions.hideSearch") : t("chatThread.actions.showSearch")}
              onClick={() => setSearchOpen((current) => !current)}
            >
              <IconSearch />
            </button>
          ) : null}
          <button className="icon-button icon-button-ghost" type="button" onClick={onBack} aria-label={t("chatThread.actions.back")} title={t("chatThread.actions.back")}>
            <IconBack />
          </button>
        </div>
      </header>

      <section className={CHAT_THREAD_SHELL_CLASSES.window}>
        {timelineError !== null ? (
          <p className="inline-error" data-testid="chat-thread-error">
            {timelineError}
          </p>
        ) : null}
        {pendingActionError !== null ? <p className="inline-error">{pendingActionError}</p> : null}
        {threadLifecycleReadOnly ? (
          <p className="lifecycle-closed-state" data-testid="thread-lifecycle-closed">
            {t("chatThread.lifecycle.closedState")}
          </p>
        ) : null}
        {showDirectDeleteAction && directDeleteUI.panelOpen ? (
          <DirectDeleteConfirmationPanel
            state={directDeleteUI}
            confirmationPhrase={directDeleteConfirmationPhrase}
            submitEnabled={directDeleteSubmitEnabled}
            errorMessage={directDeleteErrorCopy}
            translate={t}
            onCancel={() => applyDirectDeleteUIEvent({ type: "cancel" })}
            onConfirmationChange={(value) =>
              applyDirectDeleteUIEvent({
                type: "confirmation-changed",
                value,
              })
            }
            onSubmit={onSubmitDirectDelete}
          />
        ) : null}
        {showLifecycleAction &&
        lifecycleConversationType !== null &&
        lifecycleActionUI.pendingAction !== null ? (
          <section className="lifecycle-panel" data-testid="lifecycle-confirmation-panel">
            <div className="lifecycle-panel-head">
              <p className="message-state">{t("chatThread.lifecycle.title")}</p>
              <p className="muted-line">{t("chatThread.lifecycle.helper")}</p>
            </div>
            <form className="lifecycle-form" onSubmit={onSubmitLifecycleAction}>
              <label className="chat-field">
                <span>{t("chatThread.lifecycle.confirmationLabel", { phrase: lifecycleConfirmationPhrase })}</span>
                <input
                  data-testid="lifecycle-confirmation-input"
                  value={lifecycleActionUI.confirmationValue}
                  onChange={(changeEvent) =>
                    applyLifecycleActionUIEvent({
                      type: "confirmation-changed",
                      value: changeEvent.target.value,
                    })
                  }
                  placeholder={t("chatThread.lifecycle.confirmationPlaceholder")}
                  disabled={lifecycleActionUI.loading}
                />
              </label>
              <p className="lifecycle-confirmation-hint">
                {t("chatThread.lifecycle.confirmationRequirement")}
              </p>
              <div className="lifecycle-actions">
                <button
                  className="secondary-button"
                  type="button"
                  onClick={() => applyLifecycleActionUIEvent({ type: "cancel" })}
                  disabled={lifecycleActionUI.loading}
                >
                  {t("chatThread.lifecycle.actions.cancel")}
                </button>
                <button
                  className="danger-button"
                  type="submit"
                  data-testid="lifecycle-submit"
                  disabled={!lifecycleSubmitEnabled}
                >
                  {lifecycleActionUI.loading
                    ? lifecycleActionUI.pendingAction === "close"
                      ? t("chatThread.lifecycle.actions.closing")
                      : t("chatThread.lifecycle.actions.removing")
                    : lifecycleActionUI.pendingAction === "close"
                      ? t("chatThread.lifecycle.actions.confirmClose")
                      : t("chatThread.lifecycle.actions.confirmRemove")}
                </button>
              </div>
            </form>
          </section>
        ) : null}
        {lifecycleErrorCopy !== null ? (
          <p className="inline-error" data-testid="lifecycle-error">
            {lifecycleErrorCopy}
          </p>
        ) : null}
        {lifecycleStatusCopy !== null ? (
          <p className="lifecycle-success" data-testid="lifecycle-status">
            {lifecycleStatusCopy}
          </p>
        ) : null}
        {showMetadataEditorAction && metadataEditorConversationType !== null && metadataEditorOpen ? (
          <section className="metadata-editor-panel" data-testid="metadata-editor-panel">
            <div className="metadata-editor-head">
              <p className="message-state">{t("chatThread.metadataEditor.title")}</p>
              <p className="muted-line">{t("chatThread.metadataEditor.helper")}</p>
            </div>
            <form className="metadata-editor-form" onSubmit={onSubmitMetadataEditor}>
              <label className="chat-field">
                <span>
                  {metadataEditorConversationType === "group"
                    ? t("chatThread.metadataEditor.nameLabelGroup")
                    : t("chatThread.metadataEditor.nameLabelChannel")}
                </span>
                <input
                  data-testid="metadata-editor-name"
                  value={metadataEditorUI.name}
                  onChange={(changeEvent) =>
                    applyMetadataEditorUIEvent({
                      type: "name-changed",
                      value: changeEvent.target.value,
                    })
                  }
                  placeholder={
                    metadataEditorConversationType === "group"
                      ? t("chatThread.metadataEditor.namePlaceholderGroup")
                      : t("chatThread.metadataEditor.namePlaceholderChannel")
                  }
                  disabled={metadataEditorBusy}
                />
              </label>
              <label className="chat-field">
                <span>{t("chatThread.metadataEditor.descriptionLabel")}</span>
                <textarea
                  data-testid="metadata-editor-description"
                  value={metadataEditorUI.description}
                  onChange={(changeEvent) =>
                    applyMetadataEditorUIEvent({
                      type: "description-changed",
                      value: changeEvent.target.value,
                    })
                  }
                  placeholder={t("chatThread.metadataEditor.descriptionPlaceholder")}
                  maxLength={METADATA_EDITOR_DESCRIPTION_MAX_LENGTH}
                  disabled={metadataEditorBusy}
                />
              </label>
              <label className="chat-field">
                <span>{t("chatThread.metadataEditor.avatarLabel")}</span>
                <input
                  type="file"
                  accept="image/*"
                  data-testid="metadata-editor-avatar-input"
                  onChange={(changeEvent) => {
                    const nextFile = changeEvent.target.files?.[0] ?? null;
                    setMetadataEditorAvatarFile(nextFile);
                    setMetadataEditorAvatarPatchMode(nextFile === null ? "keep" : "replace");
                  }}
                  disabled={metadataEditorBusy}
                />
              </label>
              {metadataEditorAvatarFile !== null ? (
                <p className="muted-line">{t("chatThread.metadataEditor.avatarQueued", { fileName: metadataEditorAvatarFile.name })}</p>
              ) : metadataEditorAvatarPatchMode === "remove" ? (
                <p className="muted-line">{t("chatThread.metadataEditor.avatarWillBeRemoved")}</p>
              ) : null}
              <div className="metadata-editor-avatar-actions">
                <button
                  className="secondary-button"
                  type="button"
                  data-testid="metadata-editor-avatar-remove"
                  onClick={() => {
                    setMetadataEditorAvatarFile(null);
                    setMetadataEditorAvatarPatchMode("remove");
                  }}
                  disabled={metadataEditorBusy}
                >
                  {t("chatThread.metadataEditor.avatarActions.remove")}
                </button>
                <button
                  className="secondary-button"
                  type="button"
                  onClick={() => {
                    setMetadataEditorAvatarFile(null);
                    setMetadataEditorAvatarPatchMode("keep");
                  }}
                  disabled={metadataEditorBusy}
                >
                  {t("chatThread.metadataEditor.avatarActions.keep")}
                </button>
              </div>
              <p
                className={`metadata-editor-validation${
                  metadataValidationMessage === null ? " metadata-editor-validation-ready" : " metadata-editor-validation-error"
                }`}
                data-testid="metadata-editor-validation"
              >
                {metadataValidationCopy}
              </p>
              <div className="metadata-editor-actions">
                <button
                  className="secondary-button"
                  type="button"
                  onClick={() => setMetadataEditorOpen(false)}
                  disabled={metadataEditorBusy}
                >
                  {t("chatThread.metadataEditor.actions.close")}
                </button>
                <button className="primary-button" type="submit" data-testid="metadata-editor-submit" disabled={metadataEditorBusy}>
                  {metadataEditorBusy ? t("chatThread.metadataEditor.actions.saving") : t("chatThread.metadataEditor.actions.save")}
                </button>
              </div>
            </form>
            {metadataEditorErrorCopy !== null ? (
              <p className="inline-error" data-testid="metadata-editor-error">
                {metadataEditorErrorCopy}
              </p>
            ) : null}
            {metadataEditorSuccessCopy !== null ? (
              <p className="metadata-editor-success" data-testid="metadata-editor-success">
                {metadataEditorSuccessCopy}
              </p>
            ) : null}
          </section>
        ) : null}
        {showManageMembersAction && manageMembersConversationType !== null && manageMembersOpen ? (
          <section className="manage-members-panel" data-testid="manage-members-panel">
            <div className="manage-members-head">
              <p className="message-state">{t("chatThread.manageMembers.title")}</p>
              <p className="muted-line">
                {manageMembersCanAdd
                  ? t("chatThread.manageMembers.helper.canAdd")
                  : manageMembersConversationType === "group"
                    ? t("chatThread.manageMembers.helper.groupLeaveOnly")
                    : t("chatThread.manageMembers.helper.channelLeaveOnly")}
              </p>
            </div>
            {manageMembersEntries.length > 0 ? (
              <ul className="manage-members-list" data-testid="manage-members-list">
                {manageMembersEntries.map((entry) => {
                  const removeBusy = manageMembersUI.removingUserID === entry.userID;
                  const selfEntry = actorUserID?.trim() === entry.userID;
                  return (
                    <li key={entry.userID} className="manage-members-list-item">
                      <div className="manage-members-entry-meta">
                        <span className="manage-members-entry-id">{resolveMessageSenderLabel(entry.userID)}</span>
                        <span className="manage-members-entry-role">{entry.role}</span>
                      </div>
                      {entry.removable ? (
                        <button
                          className="secondary-button"
                          type="button"
                          data-testid={`manage-members-remove-${entry.userID}`}
                          onClick={() => void onRemoveManageMember(entry.userID)}
                          disabled={manageMembersBusy}
                        >
                          {removeBusy
                            ? selfEntry
                              ? t("chatThread.manageMembers.actions.leaving")
                              : t("chatThread.manageMembers.actions.removing")
                            : selfEntry
                              ? t("chatThread.manageMembers.actions.leave")
                              : t("chatThread.manageMembers.actions.remove")}
                        </button>
                      ) : (
                        <span className="manage-members-entry-protected">{t("chatThread.manageMembers.state.protected")}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="muted-line" data-testid="manage-members-empty">
                {t("chatThread.manageMembers.empty")}
              </p>
            )}
            <form className="manage-members-form" onSubmit={onSubmitManageMembers}>
              <label className="chat-field">
                <span>{t("chatThread.manageMembers.identifierLabel")}</span>
                <input
                  data-testid="manage-members-identifier"
                  value={manageMembersUI.identifier}
                  onChange={(changeEvent) =>
                    applyManageMembersUIEvent({
                      type: "identifier-changed",
                      value: changeEvent.target.value,
                    })
                  }
                  placeholder={t("chatThread.manageMembers.identifierPlaceholder")}
                  disabled={manageMembersBusy}
                />
              </label>
              <div className="manage-members-actions">
                <button
                  className="secondary-button"
                  type="button"
                  onClick={() => setManageMembersOpen(false)}
                  disabled={manageMembersBusy}
                >
                  {t("chatThread.manageMembers.actions.close")}
                </button>
                <button
                  className="primary-button"
                  type="submit"
                  data-testid="manage-members-submit"
                  disabled={manageMembersBusy || !manageMembersCanAdd}
                >
                  {manageMembersUI.submitting
                    ? t("chatThread.manageMembers.actions.adding")
                    : t(resolveManageMembersSubmitLabel(manageMembersConversationType))}
                </button>
              </div>
            </form>
            {manageMembersErrorCopy !== null ? (
              <p className="inline-error" data-testid="manage-members-error">
                {manageMembersErrorCopy}
              </p>
            ) : null}
            {manageMembersSuccessCopy !== null ? (
              <p className="manage-members-success" data-testid="manage-members-success">
                {manageMembersSuccessCopy}
              </p>
            ) : null}
          </section>
        ) : null}

        {hasChatAccess && searchOpen ? (
          <div className={CHAT_THREAD_SHELL_CLASSES.searchPanel}>
            <SearchPanel activeChatID={chatID} onOpenResult={onOpenSearchResult} onClose={() => setSearchOpen(false)} />
          </div>
        ) : null}

        <ThreadTimeline
          loading={loading}
          chatID={chatID}
          messages={messages}
          actorUserID={actorUserID}
          sourceSecretChat={isSecretConversation}
          participantReceipts={participantReceipts}
          editingMessageID={editingMessageID}
          editingBody={editingBody}
          highlightedMessageID={highlightedMessageID}
          defaultLatestScrollRevision={defaultLatestScrollRevision}
          defaultLatestMessageID={defaultLatestMessageID}
          voicePlaybackSources={voicePlaybackSources}
          photoAttachmentSources={photoAttachmentSources}
          fileAttachmentMetadata={fileAttachmentMetadata}
          voicePlayerNativeFallback={voicePlayerNativeFallback}
          pendingTimelinePhotos={pendingTimelinePhotos}
          pendingTimelineVoices={pendingTimelineVoices}
          threadPaneRef={threadPaneRef}
          messageBodyRefs={messageBodyRefs}
          onSetEditingBody={setEditingBody}
          onSaveEdit={onSaveEdit}
          onCancelEdit={onCancelEdit}
          onStartEdit={onStartEdit}
          onReplyToMessage={onReplyToMessage}
          onForwardMessage={onForwardMessage}
          onDeleteMessage={onDeleteMessage}
          onOpenReplyReference={(reference) => {
            void onOpenReplyReference(reference);
          }}
          onHydrateVoicePlaybackSource={hydrateVoicePlaybackSource}
          onHydratePhotoAttachmentSource={hydratePhotoAttachmentSource}
          onHydrateFileAttachmentMetadata={hydrateFileAttachmentMetadata}
          onDownloadFileAttachment={downloadFileAttachment}
          onSetExpandedPhotoSource={openFullSizePhotoAttachment}
          onMarkVoicePlayerNativeFallback={markVoicePlayerNativeFallback}
          onRetryPendingPhoto={onRetryPendingPhoto}
          onDeletePendingPhoto={onDeletePendingPhoto}
          onRetryPendingVoice={onRetryPendingVoice}
          onDeletePendingVoice={onDeletePendingVoice}
        />

        <section className={CHAT_THREAD_SHELL_CLASSES.composerLane} data-testid="composer-lane">
          <form className="stack-form" onSubmit={onSend}>
            <ComposerReferencePreview reference={composerReference} onCancel={onCancelComposerReference} />
            {composerFileAttachment !== null ? (
              <ComposerFileAttachmentPreview
                attachment={composerFileAttachment}
                onRemove={onRemoveComposerFileAttachment}
              />
            ) : null}
            <textarea
              ref={composerInputRef}
              className="message-composer-input"
              data-testid="message-compose-input"
              rows={2}
              value={composerBody}
              onChange={(event) => {
                const nextBody = event.target.value;
                composerBodyRef.current = nextBody;
                setComposerBody(nextBody);
                if (secretComposerError !== null) {
                  setSecretComposerError(null);
                }
              }}
              placeholder={
                threadWriteEnabled
                  ? isSecretConversation
                    ? t("chatThread.composer.secretPlaceholder")
                    : t("chatThread.composer.messagePlaceholder")
                  : threadLifecycleReadOnly
                    ? t("chatThread.lifecycle.closedState")
                  : t("chatThread.composer.noAccessPlaceholder")
              }
              disabled={!threadWriteEnabled}
            />
            {isSecretConversation ? (
              <section className="stack-form" data-testid="secret-envelope-panel">
                <label className="chat-field">
                  <span>{t("chatThread.secretEnvelope.labels.ratchetSessionId")}</span>
                  <input
                    data-testid="secret-ratchet-session-id"
                    value={secretRatchetSessionID}
                    onChange={(event) => {
                      setSecretRatchetSessionID(event.target.value);
                      if (secretComposerError !== null) {
                        setSecretComposerError(null);
                      }
                    }}
                    placeholder={t("chatThread.secretEnvelope.placeholders.ratchetSessionId")}
                    disabled={!threadWriteEnabled || sending}
                  />
                </label>
                <div className="manage-members-actions">
                  <label className="chat-field">
                    <span>{t("chatThread.secretEnvelope.labels.ratchetCounter")}</span>
                    <input
                      data-testid="secret-ratchet-counter"
                      value={secretRatchetCounter}
                      onChange={(event) => {
                        setSecretRatchetCounter(event.target.value);
                        if (secretComposerError !== null) {
                          setSecretComposerError(null);
                        }
                      }}
                      inputMode="numeric"
                      disabled={!threadWriteEnabled || sending}
                    />
                  </label>
                  <label className="chat-field">
                    <span>{t("chatThread.secretEnvelope.labels.previousChainLength")}</span>
                    <input
                      data-testid="secret-previous-chain-length"
                      value={secretPreviousChainLength}
                      onChange={(event) => {
                        setSecretPreviousChainLength(event.target.value);
                        if (secretComposerError !== null) {
                          setSecretComposerError(null);
                        }
                      }}
                      inputMode="numeric"
                      disabled={!threadWriteEnabled || sending}
                    />
                  </label>
                </div>
                {secretComposerError !== null ? (
                  <p className="inline-error" data-testid="secret-send-error">
                    {secretComposerErrorCopy}
                  </p>
                ) : null}
              </section>
            ) : null}
            {voiceComposerOpen ? (
              <section className="voice-recorder-panel" data-testid="voice-recorder-panel">
                <header className="voice-recorder-head">
                  <p className="message-state">{t("chatThread.voice.title")}</p>
                  {voiceRecordingUI.mode === "recording" || voiceRecordingUI.mode === "arming" ? (
                    <button
                      className="secondary-button voice-recorder-cancel"
                      type="button"
                      data-testid="voice-record-cancel"
                      ref={voiceCancelButtonRef}
                      onClick={onVoiceCancel}
                    >
                      {t("chatThread.voice.cancel")}
                    </button>
                  ) : null}
                </header>

                {voicePreviewVisible ? (
                  <div className="voice-preview-shell" data-testid="voice-preview">
                    <audio src={voicePreview.previewURL} controls preload="metadata" />
                    <p className="message-state">
                      {voicePreview.capped
                        ? t("chatThread.voice.reachedMax", { duration: formatVoiceDuration(voicePreview.durationSeconds) })
                        : t("chatThread.voice.recorded", { duration: formatVoiceDuration(voicePreview.durationSeconds) })}
                    </p>
                    <div className="message-actions">
                      <button
                        className="secondary-button"
                        type="button"
                        data-testid="voice-preview-send"
                        onClick={onSendVoicePreview}
                      >
                        {t("chatThread.actions.send")}
                      </button>
                      <button
                        className="danger-button"
                        type="button"
                        data-testid="voice-preview-delete"
                        onClick={onDeleteVoicePreview}
                      >
                        {t("chatThread.actions.delete")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="voice-record-live">
                    <button
                      className={`voice-record-hold${voiceRecordingUI.mode === "recording" ? " voice-record-hold-active" : ""}`}
                      type="button"
                      data-testid="voice-record-hold"
                      onPointerDown={onVoiceHoldStart}
                      onPointerUp={onVoiceHoldEnd}
                      onPointerCancel={onVoiceHoldEnd}
                    >
                      <IconVoiceWave />
                      <span>
                        {voiceRecordingUI.mode === "arming"
                          ? t("chatThread.voice.starting")
                          : voiceRecordingUI.mode === "recording"
                            ? t("chatThread.voice.recordingHoldHint")
                            : t("chatThread.voice.holdToRecord")}
                      </span>
                    </button>
                    <p className="voice-record-timer" data-testid="voice-record-timer">
                      <VoiceRecordingElapsedTimer mode={voiceRecordingUI.mode} startedAtMillis={voiceRecordingStartedAt} />
                    </p>
                    <p className="message-state">{t("chatThread.voice.max", { duration: formatVoiceDuration(VOICE_MAX_DURATION_SECONDS) })}</p>
                  </div>
                )}

                {voiceRecordingUI.errorMessage !== null ? (
                  <p className="pending-photo-error" data-testid="voice-record-error">
                    {voiceRecordingUI.errorMessage}
                  </p>
                ) : null}
              </section>
            ) : null}
            <div className="composer-actions">
              <div className="composer-media-menu-shell">
                <button
                  className={`icon-button icon-button-ghost${mediaMenuOpen ? " icon-button-active" : ""}`}
                  data-testid="composer-media-menu-toggle"
                  type="button"
                  disabled={!threadWriteEnabled || isSecretConversation}
                  aria-label={t("chatThread.actions.mediaActions")}
                  aria-haspopup="menu"
                  aria-expanded={mediaMenuOpen}
                  onClick={() => setMediaMenuOpen((open) => !open)}
                  title={t("chatThread.actions.mediaActions")}
                >
                  <IconPlus />
                </button>
                {mediaMenuOpen ? (
                  <div className="composer-media-menu" role="menu" data-testid="composer-media-menu">
                    <button
                      type="button"
                      role="menuitem"
                      className="composer-media-menu-item"
                      onClick={onOpenPhotoPicker}
                    >
                      <IconPhoto />
                      <span>{t("chatThread.actions.photo")}</span>
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="composer-media-menu-item"
                      onClick={onOpenVoiceComposer}
                    >
                      <IconMic />
                      <span>{t("chatThread.actions.voice")}</span>
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="composer-media-menu-item"
                      onClick={onOpenFilePicker}
                    >
                      <IconFile />
                      <span>{t("chatThread.actions.file")}</span>
                    </button>
                  </div>
                ) : null}
              </div>
              <button
                className="icon-button icon-button-primary"
                data-testid="message-send"
                type="submit"
                disabled={sending || !threadWriteEnabled || !composerSendReady}
                aria-label={t("chatThread.actions.sendMessage")}
                title={t("chatThread.actions.sendMessage")}
              >
                <IconSend />
              </button>
            </div>
          </form>
          <input
            ref={filePickerRef}
            type="file"
            accept="image/*"
            className="composer-photo-input"
            onChange={onPhotoSelected}
          />
          <input
            ref={fileAttachmentPickerRef}
            type="file"
            accept={SAFE_FILE_UPLOAD_ACCEPT}
            className="composer-photo-input"
            data-testid="composer-file-input"
            onChange={onFileSelected}
          />
        </section>
      </section>
      {expandedPhoto !== null ? (
        <PhotoAttachmentLightbox
          expandedPhoto={expandedPhoto}
          dialogLabel={t("chatThread.timeline.photoPreview")}
          closeLabel={t("chatThread.timeline.closePhotoPreview")}
          fullAlt={t("chatThread.timeline.photoAttachmentFullAlt")}
          loadingLabel={t("chatThread.timeline.photoLoading")}
          failedLabel={t("chatThread.timeline.photoLoadFailed")}
          onClose={closeExpandedPhoto}
        />
      ) : null}
    </main>
  );
}
